// The hero kit (docs/129): the item art that replaces Oryx, a piece at a time, on the modular hero. A piece
// listed here draws its own 24 px icon in place of its Oryx frame; one that shows on the body also has
// layers on the 32 px hero canvas (pivot (16, 28)), drawn once against the human body and moved onto
// the short and tall bodies by that height's anchor for the layer. Each species is one of the three heights
// with its own skin and hair, and its own feature layers (a beard, horns, wings) stacked by z among the gear.
// A piece not listed falls back to its Oryx frame. Art: assets/source/commissioned/hero-kit/; checks and
// masks: tools/item-art.py.

import { artDefOf } from './baseItemArt';
import { HELD_POSES } from './heldPoses.gen';

/** Where a worn layer sits on the body. Each moves by its own anchor, so a piece that spans two parts
 *  that move apart (gauntlets: one per hand) is drawn as one layer per part. */
export type WornLayer = 'cape' | 'legs' | 'feet' | 'torso' | 'waist' | 'hand_back' | 'hand_front' | 'helmet' | 'main_hand' | 'off_hand';

/** What a hero wears in, one piece each: the dresser's slots. */
export type Slot = 'main_hand' | 'off_hand' | 'helmet' | 'torso' | 'cape' | 'hands' | 'waist' | 'legs' | 'feet';

export interface WornPart {
  layer: WornLayer;
  z: number;
  url: string;
  /** A held piece's grip: the art pixel the fist closes on. It is drawn with the grip on the body's hand, and the
   *  body's fist (with whatever glove is on it) is drawn again over it, so the hand wraps the grip. */
  grip?: readonly [number, number];
  /** A helmet's hold on the head under it: most hide whatever pokes out above their lower edge (in their columns);
   *  a closed helm hides the whole head above it; a band (a circlet) hides nothing. */
  cover?: 'crown' | 'closed' | 'band';
  /** A helmet's file key, for its measured shape (itemArtShapes HAT_BOTTOM). */
  file?: string;
  /** A held piece's pose, about its grip: how the art is turned to be held the way a hero holds it (docs/151, 154). */
  pose?: Pose;
  /** A species' own pose (by `BodyArt.key`) in place of `pose`: a race that holds the piece another way. */
  bySpecies?: Partial<Record<string, Pose>>;
  /** Held in the front hand while the off hand holds nothing drawn (docs/157 HP7): a short head-forward piece that no
   *  lift in the back hand keeps off the face. */
  frontWhenFree?: boolean;
  /** Its pose in the front hand, in place of `pose` and `bySpecies` (docs/157 HP9: a dagger held point-down). */
  frontPose?: Pose;
  /** Drawn behind the head (docs/158 HB1): every head layer down to the chin is drawn again over it. A piece in the
   *  front hand always is. */
  behindHead?: boolean;
  /** Held across the body (docs/158 HB3, a bow): the whole back arm, not only the fist, is drawn again over it. */
  arm?: boolean;
}

/** How a held piece is turned about its grip: mirrored across the grip's column (`flip`), then turned `turn`
 *  quarter turns clockwise. Pixel-exact (`posed` in heroKit.ts). In the back hand it is then lifted `lift` rows: the
 *  hand holds it that much further down the haft (docs/157 HP6). */
export interface Pose { flip?: boolean; turn?: 0 | 1 | 2 | 3; lift?: number }
/** A held piece's pose and its species' own (heldPoses.gen.ts, written by `tools/item-art.py poses`). */
export interface HeldPose { pose?: Pose; bySpecies?: Partial<Record<string, Pose>>; frontWhenFree?: boolean; frontPose?: Pose; behindHead?: boolean; arm?: boolean }

/** Layers that hug the body: drawn only inside the wearer's outline (its outer outline kept on top), so a piece drawn
 *  on the human fits a slimmer or shorter body; what overhung the human too (pauldrons, puffs) is kept. */
export const CONFORM: ReadonlySet<WornLayer> = new Set(['torso', 'legs', 'waist', 'hand_back', 'hand_front', 'feet']);

const CLOSED = new Set([
  '1947-doctor-ravels-beak-mask',
  '1957-faceless-hood-of-night-market',
  '1967-pearl-divers-bell-helm',
  '1974-dread-sallet-of-the-gibbet-road',
  '1979-wolf-lords-sallet',
  '1983-tourney-helm-of-sir-dunstan',
  '1984-barrow-kings-rusted-helm',
  '1987-zealot-helm-of-the-pyre',
  '1988-oni-faced-helm-of-the-red-pass',
  '1989-justicars-visored-helm',
  '1990-lord-ashbys-plumed-helm',
  '1991-great-helm-of-the-gatehouse',
  '1992-wolfjaw-great-helm',
  '1995-hrolfs-iron-fury-mask',
  '1996-war-chiefs-plumed-greathelm',
  '1997-mayhem-helm-of-the-black-rook',
  '1998-dragon-crested-helm-of-vhastrax',
  '3440-horned-helm-of-the-burning-host',
  '4001-obsidian-greathelm',
  '4106-frostbound-greathelm',
  '4115-iron-tortoise-helm',
  '4121-hollow-kings-crowned-helm',
  '4252-siegebreaker-great-helm',
  '4310-ashlands-great-helm',
  '4314-plaguedoctors-beaked-mask',
  '4407-leviathan-deep-helm',
  '4414-runeguard-helm',
  '4423-goblin-kings-stewpot-helm',
]);
const BANDS = new Set([
  '1937-holly-circlet-of-midwinter',
  '1942-circlet-of-scorching',
  '1943-scholars-tongue-circlet',
  '1945-crown-of-antlered-oak',
  '1950-silver-tongue-circlet',
  '1952-circlet-of-the-quiet-voice',
  '1955-ivy-crown-of-the-hollow-seer',
  '1956-lamplit-circlet-of-saint-quill',
  '1958-lupine-crown-of-the-greyhowl',
  '1959-shark-tooth-crown-of-saltmarsh',
  '1960-diadem-of-the-glass-queen',
  '1962-sand-king-crown-of-ashur',
  '1963-greenwood-crown-of-the-fey-road',
  '3439-drowned-kings-circlet',
  '4261-starweaver-circlet',
  '4418-celestial-choir-halo',
  '903-shieldhands-red-headband',
  '917-cutpurses-knotted-bandana',
  '937-acolytes-sun-circlet',
  '953-oathsworn-silver-circlet',
  '963-grovekeepers-leaf-wreath',
  '967-grovekeepers-antler-circlet',
]);

export interface ItemArt {
  key: string;
  /** The 24 px icon. */
  icon: string;
  /** For a piece that shows on the body: its slot and its 32 px layers. */
  worn?: { slot: Slot; parts: WornPart[] };
}

/** One of the three heights every species is built on. Pieces are drawn on the medium (human) body; a
 *  height moves each layer by its anchor, in art pixels; absent is (0, 0). A held piece goes by its grip instead. */
export interface Height {
  key: 'short' | 'medium' | 'tall';
  anchors: Partial<Record<WornLayer, readonly [number, number]>>;
  /** The back hand's centre, where a held piece's grip goes. */
  hand: readonly [number, number];
  /** The back fist's pixels (skin and outline) as row runs [y, x0, x1]; drawn again over a held piece. */
  fist: ReadonlyArray<readonly [number, number, number]>;
  /** The front hand's centre, where a piece held in the off hand goes: the back hand mirrored across the body. */
  offHand: readonly [number, number];
  /** The front fist's pixels: the back fist mirrored across the body. */
  offFist: ReadonlyArray<readonly [number, number, number]>;
  /** The back arm, shoulder to fist (docs/158 HB3): drawn again over a piece held across the body. */
  arm: ReadonlyArray<readonly [number, number, number]>;
  /** The head's lowest row (docs/158 HB1): a piece behind the head is covered by every head layer down to it. */
  chin: number;
}

export type Sex = 'male' | 'female';

/** A species' own layer, drawn on its body (no anchor): a beard, horns, a tail, wings. */
export interface Feature { key: string; z: number; url: string }

export interface BodyArt {
  key: string;
  name: string;
  sex: Sex;
  /** Its height's body with the species' skin and hair. A female body keeps the male outline below the neck, so
   *  every worn layer fits both; only the head differs, and hair falling past the neck is a feature over armour. */
  url: string;
  height: Height['key'];
  anchors: Height['anchors'];
  hand: Height['hand'];
  fist: Height['fist'];
  offHand: Height['offHand'];
  offFist: Height['offFist'];
  arm: Height['arm'];
  chin: Height['chin'];
  features: Feature[];
}

/** docs/12 §6.2 z numbers, lowest first; the body itself sits at 20. Legs (25) go under boots so a tall boot
 *  covers the trouser hem, and the waist (45) over armour so a belt cinches it. */
export const BODY_Z = 20;
/** The fist drawn again over a held piece: above the main hand (80), under the off hand's shield (90). */
export const FIST_Z = 85;
const Z: Record<WornLayer, number> = { cape: 10, legs: 25, feet: 30, torso: 40, waist: 45, hand_back: 50, hand_front: 55, helmet: 70, main_hand: 80, off_hand: 90 };

/** A held piece passes behind the body (`z` under BODY_Z) when the body's own fist should wrap its grip. */
const part = (file: string, layer: WornLayer, z = Z[layer]): WornPart => ({
  layer, z, url: `/assets/hero-kit/worn/${file}.png`,
  ...(layer === 'helmet' ? { cover: CLOSED.has(file) ? 'closed' as const : BANDS.has(file) ? 'band' as const : 'crown' as const, file } : {}),
});

/** A piece held in the back hand (or, in the off hand, by the front one), by its grip; its pose is its catalogue id's. */
const held = (file: string, grip: readonly [number, number], z = Z.main_hand): WornPart[] => [{ ...part(file, 'main_hand', z), grip }];

const art = (key: string, slot?: Slot, parts?: WornPart[]): ItemArt => {
  // docs/157: a held part takes its piece's measured pose (by the catalogue id its file starts with)
  const pose = HELD_POSES[Number(key.split('-')[0])];
  const posed = pose ? parts?.map((p) => (p.grip ? { ...p, ...pose } : p)) : parts;
  return {
    key,
    icon: `/assets/hero-kit/icons/${key}.png`,
    ...(slot ? { worn: { slot, parts: posed ?? [part(key, slot as WornLayer)] } } : {}),
  };
};

/** Catalogue id -> its QA art. */
export const ITEM_ART: Readonly<Record<number, ItemArt>> = {
  1030: art('1030-wrens-snowfield-sabre', 'main_hand', held('1030-wrens-snowfield-sabre', [9, 17])),
  1206: art('1206-frostbite-hatchet', 'main_hand', held('1206-frostbite-hatchet', [9, 17], Z.main_hand)),
  1255: art('1255-pilgrims-flanged-mace', 'main_hand', held('1255-pilgrims-flanged-mace', [8, 16])),
  1400: art('1400-heron-neck-glaive', 'main_hand', held('1400-heron-neck-glaive', [9, 20])),
  1515: art('1515-ironbark-longbow', 'main_hand', held('1515-ironbark-longbow', [11, 20])),
  1475: art('1475-lantern-bearers-pole', 'main_hand', held('1475-lantern-bearers-pole', [10, 20])),
  1895: art('1895-kite-of-the-fordwater-levy', 'off_hand'),
  1965: art('1965-watchmans-kettle-hat', 'helmet'),
  1785: art('1785-wolfpelt-brigandine', 'torso'),
  2035: art('2035-belfry-bat-silk-cloak', 'cape'),
  2120: art('2120-thistledown-elf-boots', 'feet'),
  2065: art('2065-tanners-oxblood-gauntlets', 'hands', [part('2065-tanners-oxblood-gauntlets-back', 'hand_back'), part('2065-tanners-oxblood-gauntlets-front', 'hand_front')]),
  2500: art('2500-hedge-witchs-red-draught'),
  2255: art('2255-lantern-lighters-ring'),
  2420: art('2420-grandmothers-heart-locket'),
  2785: art('2785-stormcallers-writ'),
  2862: art('2862-tinkers-alchemists-fire'),
  3115: art('3115-grimsby-wharf-fish-pie'),
  3205: art('3205-liars-lexicon'),
  3265: art('3265-bluefire-diamond-of-the-earls'),
  // the second batch: wearables, one or more for every body slot
  1838: art('1838-smiths-practice-plate', 'torso'),
  1829: art('1829-kingsroad-toll-mail', 'torso'),
  1782: art('1782-badger-hide-coat', 'torso'),
  1753: art('1753-bog-waders-oilcloth', 'torso'),
  1851: art('1851-bastion-of-the-inheritor', 'torso'),
  1936: art('1936-witch-grizzels-old-hat', 'helmet'),
  1990: art('1990-lord-ashbys-plumed-helm', 'helmet'),
  1985: art('1985-hob-horned-mummer-helm', 'helmet'),
  1950: art('1950-silver-tongue-circlet', 'helmet'),
  2105: art('2105-cobblers-honest-brogues', 'feet'),
  2122: art('2122-fell-walker-winterboots', 'feet'),
  2139: art('2139-brimstone-hellwalkers', 'feet'),
  2155: art('2155-ploughboys-patched-breeches', 'legs'),
  2191: art('2191-duelists-silken-pantaloons', 'legs'),
  2194: art('2194-greaves-of-the-mountain-king', 'legs'),
  2165: art('2165-novices-study-skirt', 'legs'),
  2073: art('2073-bearclaw-bracers', 'hands', [part('2073-bearclaw-bracers-back', 'hand_back'), part('2073-bearclaw-bracers-front', 'hand_front')]),
  2012: art('2012-lambswool-shepherds-cloak', 'cape'),
  2019: art('2019-blue-willow-raincloak', 'cape'),
  2200: art('2200-drovers-rope-girdle', 'waist'),
  // the third batch: 500 pieces across every family (docs/129 §6), held weapons drawn upright and placed by their grip
  1002: art('1002-tollkeepers-sabre', 'main_hand', held('1002-tollkeepers-sabre', [11, 20])),
  1007: art('1007-bandit-takers-blade', 'main_hand', held('1007-bandit-takers-blade', [11, 20])),
  1012: art('1012-saltmarsh-cutlass', 'main_hand', held('1012-saltmarsh-cutlass', [11, 20])),
  1017: art('1017-thornwick-rapier', 'main_hand', held('1017-thornwick-rapier', [11, 20])),
  1022: art('1022-dwarrowdeep-short-blade', 'main_hand', held('1022-dwarrowdeep-short-blade', [11, 20])),
  1027: art('1027-mudlarks-shortsword', 'main_hand', held('1027-mudlarks-shortsword', [11, 20])),
  1034: art('1034-rimebiter-sabre', 'main_hand', held('1034-rimebiter-sabre', [11, 20])),
  1039: art('1039-tamsin-hales-bulwark-blade', 'main_hand', held('1039-tamsin-hales-bulwark-blade', [11, 20])),
  1044: art('1044-chainbreaker-of-low-gaol', 'main_hand', held('1044-chainbreaker-of-low-gaol', [11, 20])),
  1049: art('1049-tinderbox-sparkblade', 'main_hand', held('1049-tinderbox-sparkblade', [11, 20])),
  1054: art('1054-wolfsbane-hanger', 'main_hand', held('1054-wolfsbane-hanger', [11, 20])),
  1059: art('1059-bookbinders-blade', 'main_hand', held('1059-bookbinders-blade', [11, 20])),
  1064: art('1064-bridgekeepers-zweihander', 'main_hand', held('1064-bridgekeepers-zweihander', [11, 20])),
  1069: art('1069-lantern-knights-longsword', 'main_hand', held('1069-lantern-knights-longsword', [11, 20])),
  1074: art('1074-glassmere-edge', 'main_hand', held('1074-glassmere-edge', [11, 20])),
  1079: art('1079-hexbreaker-of-grey-abbey', 'main_hand', held('1079-hexbreaker-of-grey-abbey', [11, 20])),
  1084: art('1084-bogslime-edge', 'main_hand', held('1084-bogslime-edge', [11, 20])),
  1089: art('1089-revenant-blade-of-coldharbour', 'main_hand', held('1089-revenant-blade-of-coldharbour', [11, 20])),
  1095: art('1095-headsman-of-the-quiet-court', 'main_hand', held('1095-headsman-of-the-quiet-court', [11, 20])),
  1100: art('1100-ninefold-reaper-of-gallowmoor', 'main_hand', held('1100-ninefold-reaper-of-gallowmoor', [11, 20])),
  1105: art('1105-hermit-saints-peachwood-sword', 'main_hand', held('1105-hermit-saints-peachwood-sword', [11, 20])),
  1110: art('1110-forgeheart-temperbrand', 'main_hand', held('1110-forgeheart-temperbrand', [11, 20])),
  1113: art('1113-hedge-witchs-bodkin', 'main_hand', held('1113-hedge-witchs-bodkin', [11, 20])),
  1118: art('1118-shepherd-boys-whittling-knife', 'main_hand', held('1118-shepherd-boys-whittling-knife', [11, 20])),
  1123: art('1123-wolf-tooth-dirk', 'main_hand', held('1123-wolf-tooth-dirk', [11, 20])),
  1128: art('1128-mummers-throwing-knife', 'main_hand', held('1128-mummers-throwing-knife', [11, 20])),
  1134: art('1134-twinned-black-and-white-dirks', 'main_hand', held('1134-twinned-black-and-white-dirks', [11, 20])),
  1139: art('1139-survivalists-saw-back-knife', 'main_hand', held('1139-survivalists-saw-back-knife', [11, 20])),
  1144: art('1144-ember-tinder-knife', 'main_hand', held('1144-ember-tinder-knife', [11, 20])),
  1149: art('1149-hollowbone-fetish-knife', 'main_hand', held('1149-hollowbone-fetish-knife', [11, 20])),
  1154: art('1154-unquiet-dagger', 'main_hand', held('1154-unquiet-dagger', [11, 20])),
  1159: art('1159-barbed-devils-tooth', 'main_hand', held('1159-barbed-devils-tooth', [11, 20])),
  1164: art('1164-sacrificial-knife-of-cold-altar', 'main_hand', held('1164-sacrificial-knife-of-cold-altar', [11, 20])),
  1170: art('1170-mordant-spike', 'main_hand', held('1170-mordant-spike', [11, 20])),
  1175: art('1175-fiends-hunger', 'main_hand', held('1175-fiends-hunger', [11, 20])),
  1180: art('1180-saint-corras-thorn', 'main_hand', held('1180-saint-corras-thorn', [11, 20])),
  1183: art('1183-butchers-market-cleaver', 'main_hand', held('1183-butchers-market-cleaver', [11, 20], Z.main_hand)),
  1188: art('1188-ice-chopper-of-wenmere', 'main_hand', held('1188-ice-chopper-of-wenmere', [11, 20])),
  1193: art('1193-charcoal-burners-axe', 'main_hand', held('1193-charcoal-burners-axe', [11, 20], Z.main_hand)),
  1198: art('1198-hogshead-coopers-adze', 'main_hand', held('1198-hogshead-coopers-adze', [11, 20], Z.main_hand)),
  1203: art('1203-berserkers-thirsty-axe', 'main_hand', held('1203-berserkers-thirsty-axe', [11, 20], Z.main_hand)),
  1209: art('1209-axe-of-chilling-fear', 'main_hand', held('1209-axe-of-chilling-fear', [11, 20], Z.main_hand)),
  1214: art('1214-ulfen-shieldbreaker', 'main_hand', held('1214-ulfen-shieldbreaker', [11, 20])),
  1219: art('1219-glaive-of-the-moorwatch', 'main_hand', held('1219-glaive-of-the-moorwatch', [11, 20])),
  1224: art('1224-sickle-axe-of-the-harvest-queen', 'main_hand', held('1224-sickle-axe-of-the-harvest-queen', [11, 20], Z.main_hand)),
  1229: art('1229-chillfear-axe-of-rimehollow', 'main_hand', held('1229-chillfear-axe-of-rimehollow', [11, 20], Z.main_hand)),
  1234: art('1234-lumber-lords-felling-axe', 'main_hand', held('1234-lumber-lords-felling-axe', [11, 20], Z.main_hand)),
  1239: art('1239-stormcleaver-of-highcairn', 'main_hand', held('1239-stormcleaver-of-highcairn', [11, 20], Z.main_hand)),
  1244: art('1244-blackaxe-of-the-thornwold', 'main_hand', held('1244-blackaxe-of-the-thornwold', [11, 20], Z.main_hand)),
  1249: art('1249-skyrend-the-riven-crystal', 'main_hand', held('1249-skyrend-the-riven-crystal', [11, 20])),
  1252: art('1252-shepherds-knobkerrie', 'main_hand', held('1252-shepherds-knobkerrie', [11, 20])),
  1258: art('1258-gravediggers-tamping-maul', 'main_hand', held('1258-gravediggers-tamping-maul', [11, 20])),
  1263: art('1263-millstone-hammer', 'main_hand', held('1263-millstone-hammer', [11, 20])),
  1268: art('1268-mace-of-gentle-disruption', 'main_hand', held('1268-mace-of-gentle-disruption', [11, 20])),
  1273: art('1273-chaplains-cudgel', 'main_hand', held('1273-chaplains-cudgel', [11, 20])),
  1278: art('1278-wandmenders-mallet', 'main_hand', held('1278-wandmenders-mallet', [11, 20])),
  1283: art('1283-goblin-knocker', 'main_hand', held('1283-goblin-knocker', [11, 20])),
  1287: art('1287-hedge-witchs-knotted-club', 'main_hand', held('1287-hedge-witchs-knotted-club', [11, 20])),
  1292: art('1292-mace-of-terror', 'main_hand', held('1292-mace-of-terror', [11, 20])),
  1297: art('1297-faultline-hammer', 'main_hand', held('1297-faultline-hammer', [11, 20])),
  1302: art('1302-hammer-of-thunderbolts', 'main_hand', held('1302-hammer-of-thunderbolts', [11, 20])),
  1307: art('1307-mace-of-disruption', 'main_hand', held('1307-mace-of-disruption', [11, 20])),
  1312: art('1312-woodcutters-splitting-maul', 'main_hand', held('1312-woodcutters-splitting-maul', [11, 20])),
  1317: art('1317-tavern-leg', 'main_hand', held('1317-tavern-leg', [11, 20])),
  1322: art('1322-iron-shod-tent-club', 'main_hand', held('1322-iron-shod-tent-club', [11, 20])),
  1327: art('1327-weighted-bludgeon', 'main_hand', held('1327-weighted-bludgeon', [11, 20])),
  1332: art('1332-rustbringer', 'main_hand', held('1332-rustbringer', [11, 20])),
  1337: art('1337-abbots-penitent-flail', 'main_hand', held('1337-abbots-penitent-flail', [11, 20])),
  1342: art('1342-dancing-bear-cudgel', 'main_hand', held('1342-dancing-bear-cudgel', [11, 20])),
  1347: art('1347-generals-word', 'main_hand', held('1347-generals-word', [11, 20])),
  1351: art('1351-wardstone-maul', 'main_hand', held('1351-wardstone-maul', [11, 20])),
  1356: art('1356-deflecting-branch', 'main_hand', held('1356-deflecting-branch', [11, 20])),
  1361: art('1361-mother-oaks-knotted-fist', 'main_hand', held('1361-mother-oaks-knotted-fist', [11, 20])),
  1366: art('1366-rowan-bound-mace', 'main_hand', held('1366-rowan-bound-mace', [11, 20])),
  1372: art('1372-militia-pike', 'main_hand', held('1372-militia-pike', [11, 20])),
  1377: art('1377-picket-line-spear', 'main_hand', held('1377-picket-line-spear', [11, 20])),
  1382: art('1382-sentrys-halberd', 'main_hand', held('1382-sentrys-halberd', [11, 20])),
  1387: art('1387-gale-javelin', 'main_hand', held('1387-gale-javelin', [11, 20])),
  1392: art('1392-glorys-glaive', 'main_hand', held('1392-glorys-glaive', [11, 20])),
  1397: art('1397-gluttonous-spear', 'main_hand', held('1397-gluttonous-spear', [11, 20])),
  1403: art('1403-spear-of-the-north', 'main_hand', held('1403-spear-of-the-north', [11, 20])),
  1408: art('1408-preordained-spear', 'main_hand', held('1408-preordained-spear', [11, 20])),
  1413: art('1413-fenwardens-lance', 'main_hand', held('1413-fenwardens-lance', [11, 20])),
  1418: art('1418-icicle-longspear', 'main_hand', held('1418-icicle-longspear', [11, 20])),
  1423: art('1423-thicket-beaters-spontoon', 'main_hand', held('1423-thicket-beaters-spontoon', [11, 20])),
  1427: art('1427-rope-bound-javelin', 'main_hand', held('1427-rope-bound-javelin', [11, 20])),
  1432: art('1432-mudflat-trident', 'main_hand', held('1432-mudflat-trident', [11, 20])),
  1437: art('1437-splinter-spear', 'main_hand', held('1437-splinter-spear', [11, 20])),
  1442: art('1442-marsh-lantern-lance', 'main_hand', held('1442-marsh-lantern-lance', [11, 20])),
  1447: art('1447-witch-prickers-spike', 'main_hand', held('1447-witch-prickers-spike', [11, 20])),
  1452: art('1452-midirs-trident', 'main_hand', held('1452-midirs-trident', [11, 20])),
  1457: art('1457-glaive-of-the-artist', 'main_hand', held('1457-glaive-of-the-artist', [11, 20])),
  1462: art('1462-mother-herons-gig', 'main_hand', held('1462-mother-herons-gig', [11, 20])),
  1467: art('1467-stormwatch-pike-of-the-coast', 'main_hand', held('1467-stormwatch-pike-of-the-coast', [11, 20])),
  1472: art('1472-forked-dowsing-staff', 'main_hand', held('1472-forked-dowsing-staff', [11, 20])),
  1478: art('1478-soldras-last-defence-staff', 'main_hand', held('1478-soldras-last-defence-staff', [11, 20])),
  1483: art('1483-staff-of-withering', 'main_hand', held('1483-staff-of-withering', [11, 20])),
  1488: art('1488-staff-of-master-lu-po', 'main_hand', held('1488-staff-of-master-lu-po', [11, 20])),
  1493: art('1493-bloodgorger-scythe', 'main_hand', held('1493-bloodgorger-scythe', [11, 20])),
  1498: art('1498-goldenwheat-voulge', 'main_hand', held('1498-goldenwheat-voulge', [11, 20])),
  1502: art('1502-shepherd-tamsins-wolfbow', 'main_hand', held('1502-shepherd-tamsins-wolfbow', [11, 20])),
  1507: art('1507-briarbarb-shortbow', 'main_hand', held('1507-briarbarb-shortbow', [11, 20])),
  1512: art('1512-charcoal-burners-bow', 'main_hand', held('1512-charcoal-burners-bow', [11, 20])),
  1518: art('1518-mirrorbright-shortbow', 'main_hand', held('1518-mirrorbright-shortbow', [11, 20])),
  1522: art('1522-gutstring-of-the-hollow-fen', 'main_hand', held('1522-gutstring-of-the-hollow-fen', [11, 20])),
  1527: art('1527-hedge-witchs-hazel-bow', 'main_hand', held('1527-hedge-witchs-hazel-bow', [11, 20])),
  1532: art('1532-emberhalf-bow', 'main_hand', held('1532-emberhalf-bow', [11, 20])),
  1537: art('1537-saint-eddas-corpse-light-bow', 'main_hand', held('1537-saint-eddas-corpse-light-bow', [11, 20])),
  1542: art('1542-starfall-the-comet-yew', 'main_hand', held('1542-starfall-the-comet-yew', [11, 20])),
  1547: art('1547-screeching-slingstone-pouch', 'main_hand', held('1547-screeching-slingstone-pouch', [11, 20])),
  1552: art('1552-old-queen-mabs-glass-shot-sling', 'main_hand', held('1552-old-queen-mabs-glass-shot-sling', [11, 20])),
  1556: art('1556-gale-crested-javelin', 'main_hand', held('1556-gale-crested-javelin', [11, 20])),
  1561: art('1561-tracking-dart-of-the-fox-wives', 'main_hand', held('1561-tracking-dart-of-the-fox-wives', [11, 20])),
  1566: art('1566-bloodfern-javelin', 'main_hand', held('1566-bloodfern-javelin', [11, 20])),
  1572: art('1572-watch-sergeants-crossbow', 'main_hand', held('1572-watch-sergeants-crossbow', [11, 20])),
  1577: art('1577-pikemans-hip-crossbow', 'main_hand', held('1577-pikemans-hip-crossbow', [11, 20])),
  1581: art('1581-marshlight-repeater', 'main_hand', held('1581-marshlight-repeater', [11, 20])),
  1586: art('1586-arcing-hand-crossbow', 'main_hand', held('1586-arcing-hand-crossbow', [11, 20])),
  1591: art('1591-goblin-gate-repeater', 'main_hand', held('1591-goblin-gate-repeater', [11, 20])),
  1596: art('1596-saltpan-smugglers-crossbow', 'main_hand', held('1596-saltpan-smugglers-crossbow', [11, 20])),
  1601: art('1601-shattershot-crossbow', 'main_hand', held('1601-shattershot-crossbow', [11, 20])),
  1605: art('1605-wyvern-sting-crossbow', 'main_hand', held('1605-wyvern-sting-crossbow', [11, 20])),
  1612: art('1612-ashwood-ember-wand', 'main_hand', held('1612-ashwood-ember-wand', [11, 20])),
  1617: art('1617-sentrys-rowan-wand', 'main_hand', held('1617-sentrys-rowan-wand', [11, 20])),
  1622: art('1622-bogeymans-switch', 'main_hand', held('1622-bogeymans-switch', [11, 20])),
  1627: art('1627-hawthorn-hedge-wand', 'main_hand', held('1627-hawthorn-hedge-wand', [11, 20])),
  1632: art('1632-long-arm-wand-of-the-reeve', 'main_hand', held('1632-long-arm-wand-of-the-reeve', [11, 20])),
  1637: art('1637-snowfield-wand', 'main_hand', held('1637-snowfield-wand', [11, 20])),
  1642: art('1642-tinker-pims-wonder-wand', 'main_hand', held('1642-tinker-pims-wonder-wand', [11, 20])),
  1647: art('1647-wand-of-smouldering-peat', 'main_hand', held('1647-wand-of-smouldering-peat', [11, 20])),
  1652: art('1652-howling-rod-of-the-moor', 'main_hand', held('1652-howling-rod-of-the-moor', [11, 20])),
  1657: art('1657-rod-of-deflected-barbs', 'main_hand', held('1657-rod-of-deflected-barbs', [11, 20])),
  1662: art('1662-the-immovable-pilgrims-rod', 'main_hand', held('1662-the-immovable-pilgrims-rod', [11, 20])),
  1667: art('1667-desiccating-sceptre-of-red-waste', 'main_hand', held('1667-desiccating-sceptre-of-red-waste', [11, 20])),
  1674: art('1674-birch-hedge-staff', 'main_hand', held('1674-birch-hedge-staff', [11, 20])),
  1679: art('1679-gamblers-dice-staff', 'main_hand', held('1679-gamblers-dice-staff', [11, 20])),
  1684: art('1684-hospice-staff-of-brenmoor', 'main_hand', held('1684-hospice-staff-of-brenmoor', [11, 20])),
  1688: art('1688-staff-of-the-web-tender', 'main_hand', held('1688-staff-of-the-web-tender', [11, 20])),
  1693: art('1693-pilgrims-celestial-staff', 'main_hand', held('1693-pilgrims-celestial-staff', [11, 20])),
  1698: art('1698-greenmantle-glade-staff', 'main_hand', held('1698-greenmantle-glade-staff', [11, 20])),
  1703: art('1703-staff-of-the-tempest-reach', 'main_hand', held('1703-staff-of-the-tempest-reach', [11, 20])),
  1708: art('1708-staff-of-unmeasured-power', 'main_hand', held('1708-staff-of-unmeasured-power', [11, 20])),
  1713: art('1713-tidewardens-trident-staff', 'main_hand', held('1713-tidewardens-trident-staff', [11, 20])),
  1717: art('1717-thrushbeck-primer', 'off_hand'),
  1722: art('1722-ammonite-of-the-drowned-choir', 'off_hand'),
  1728: art('1728-fen-light-in-a-bottle', 'off_hand'),
  1733: art('1733-thurible-of-unveiling', 'off_hand'),
  1738: art('1738-wayfinder-of-the-old-roads', 'off_hand'),
  1743: art('1743-rift-orb-of-hollow-tor', 'off_hand'),
  1749: art('1749-last-candle-of-the-nine-saints', 'off_hand'),
  1752: art('1752-lamplighters-coat', 'torso'),
  1758: art('1758-starstitched-robe', 'torso'),
  1763: art('1763-gravediggers-long-coat', 'torso'),
  1768: art('1768-library-robes-of-saint-quill', 'torso'),
  1773: art('1773-robe-of-the-unseen-hour', 'torso'),
  1778: art('1778-poachers-doeskin-jerkin', 'torso'),
  1784: art('1784-fenrunners-eelskin', 'torso'),
  1790: art('1790-hide-of-the-white-ape', 'torso'),
  1795: art('1795-deep-pocket-jerkin', 'torso'),
  1800: art('1800-fox-fur-trappers-coat', 'torso'),
  1805: art('1805-muffled-riding-leathers', 'torso'),
  1810: art('1810-pilgrims-mail-undershirt', 'torso'),
  1815: art('1815-swordmaster-aldrics-mail', 'torso'),
  1820: art('1820-wave-chain-of-the-saltmarsh', 'torso'),
  1824: art('1824-bellringers-harmonic-hauberk', 'torso'),
  1830: art('1830-serpents-scale-hauberk', 'torso'),
  1835: art('1835-half-plate-of-the-bridge-toll', 'torso'),
  1841: art('1841-captain-hawleys-plate', 'torso'),
  1846: art('1846-demon-forged-plate-of-vhar', 'torso'),
  1852: art('1852-armour-of-the-unbroken-oath', 'torso'),
  1857: art('1857-juggernaut-plate-of-old-gorm', 'torso'),
  1862: art('1862-sextons-silvered-buckler', 'off_hand'),
  1867: art('1867-battle-hedge-buckler', 'off_hand'),
  1872: art('1872-swordbreaker-targe-of-ostrava', 'off_hand'),
  1877: art('1877-fallen-bridge-guards-shield', 'off_hand'),
  1883: art('1883-hedgehog-shield-of-bramblewick', 'off_hand'),
  1888: art('1888-wollops-reversing-shield', 'off_hand'),
  1893: art('1893-heraldic-kite-of-house-varrow', 'off_hand'),
  1899: art('1899-wyrmward-kite-of-saint-gell', 'off_hand'),
  1904: art('1904-turncoats-kite', 'off_hand'),
  1909: art('1909-bearward-kite-of-greyhowl', 'off_hand'),
  1914: art('1914-unbreakable-kite-of-thane-orsk', 'off_hand'),
  1919: art('1919-skull-painted-tower-of-grunda', 'off_hand'),
  1925: art('1925-resolute-wall-of-ostrava', 'off_hand'),
  1932: art('1932-candlecap-of-the-night-verger', 'helmet'),
  1938: art('1938-market-day-mask', 'helmet'),
  1943: art('1943-scholars-tongue-circlet', 'helmet'),
  1948: art('1948-conjurors-bottomless-top-hat', 'helmet'),
  1954: art('1954-crown-of-the-bog-witches', 'helmet'),
  1959: art('1959-shark-tooth-crown-of-saltmarsh', 'helmet'),
  1964: art('1964-troubadour-cap-of-merriwick', 'helmet'),
  1970: art('1970-ditch-diggers-sallet', 'helmet'),
  1975: art('1975-hedge-warden-kettle', 'helmet'),
  1980: art('1980-sappers-kettle-of-kharn-dabur', 'helmet'),
  1986: art('1986-old-ironpot-of-the-levy', 'helmet'),
  1992: art('1992-wolfjaw-great-helm', 'helmet'),
  1997: art('1997-mayhem-helm-of-the-black-rook', 'helmet'),
  2002: art('2002-pilgrims-dust-mantle', 'cape'),
  2007: art('2007-beekeepers-veil-cape', 'cape'),
  2013: art('2013-long-road-homesick-mantle', 'cape'),
  2018: art('2018-clotted-rust-cloak', 'cape'),
  2023: art('2023-bearfolk-shoulder-pelt', 'cape'),
  2028: art('2028-tinkers-unremarked-mantle', 'cape'),
  2033: art('2033-carrion-crow-tatters', 'cape'),
  2039: art('2039-greenwardens-leaf-mantle', 'cape'),
  2044: art('2044-last-light-survivors-cloak', 'cape'),
  2049: art('2049-lion-pelt-of-king-ourric', 'cape'),
  2054: art('2054-ragged-mantle-of-saint-orrin', 'cape'),
  2057: art('2057-drill-sergeants-grippy-gloves', 'hands', [part('2057-drill-sergeants-grippy-gloves-back', 'hand_back'), part('2057-drill-sergeants-grippy-gloves-front', 'hand_front')]),
  2062: art('2062-assayers-emerald-gloves', 'hands', [part('2062-assayers-emerald-gloves-back', 'hand_back'), part('2062-assayers-emerald-gloves-front', 'hand_front')]),
  2068: art('2068-ratcatchers-biting-gloves', 'hands', [part('2068-ratcatchers-biting-gloves-back', 'hand_back'), part('2068-ratcatchers-biting-gloves-front', 'hand_front')]),
  2074: art('2074-saltmarsh-crab-gloves', 'hands', [part('2074-saltmarsh-crab-gloves-back', 'hand_back'), part('2074-saltmarsh-crab-gloves-front', 'hand_front')]),
  2079: art('2079-pearl-divers-webbed-gloves', 'hands', [part('2079-pearl-divers-webbed-gloves-back', 'hand_back'), part('2079-pearl-divers-webbed-gloves-front', 'hand_front')]),
  2084: art('2084-hangmans-knot-gauntlets', 'hands', [part('2084-hangmans-knot-gauntlets-back', 'hand_back'), part('2084-hangmans-knot-gauntlets-front', 'hand_front')]),
  2089: art('2089-warden-monks-bracers', 'hands', [part('2089-warden-monks-bracers-back', 'hand_back'), part('2089-warden-monks-bracers-front', 'hand_front')]),
  2094: art('2094-ankh-wing-bronze-bracers', 'hands', [part('2094-ankh-wing-bronze-bracers-back', 'hand_back'), part('2094-ankh-wing-bronze-bracers-front', 'hand_front')]),
  2099: art('2099-summer-kings-hoof-gauntlets', 'hands', [part('2099-summer-kings-hoof-gauntlets-back', 'hand_back'), part('2099-summer-kings-hoof-gauntlets-front', 'hand_front')]),
  2104: art('2104-vambrace-of-the-iron-god', 'hands', [part('2104-vambrace-of-the-iron-god-back', 'hand_back'), part('2104-vambrace-of-the-iron-god-front', 'hand_front')]),
  2108: art('2108-skaters-bone-runner-boots', 'feet'),
  2113: art('2113-rubber-soled-stompers', 'feet'),
  2118: art('2118-peat-cutters-waders', 'feet'),
  2125: art('2125-springheel-bounders', 'feet'),
  2129: art('2129-deserters-turned-boots', 'feet'),
  2134: art('2134-lizard-tamers-scale-boots', 'feet'),
  2140: art('2140-weasel-fur-pounce-boots', 'feet'),
  2145: art('2145-cobweb-spider-slippers', 'feet'),
  2150: art('2150-thunderstep-war-boots', 'feet'),
  2158: art('2158-sergeants-mail-chausses', 'legs'),
  2163: art('2163-snowfield-felt-leggings', 'legs'),
  2169: art('2169-stout-heart-golden-greaves', 'legs'),
  2174: art('2174-frostmere-ice-silk-leggings', 'legs'),
  2179: art('2179-herbwifes-pocketed-skirt', 'legs'),
  2185: art('2185-knight-errants-gilded-greaves', 'legs'),
  2190: art('2190-witchweave-midnight-skirt', 'legs'),
  2197: art('2197-shadow-queens-night-hose', 'legs'),
  2203: art('2203-hedge-scholars-scroll-belt', 'waist'),
  2208: art('2208-lantern-hook-belt', 'waist'),
  2213: art('2213-cutlers-knife-bandolier', 'waist'),
  2218: art('2218-lieutenants-scarlet-sash', 'waist'),
  2223: art('2223-pedlars-hundred-pouch-belt', 'waist'),
  2228: art('2228-apothecarys-vial-belt', 'waist'),
  2233: art('2233-stonebeards-hold-belt', 'waist'),
  2237: art('2237-thunderhead-hip-quiver', 'waist'),
  2242: art('2242-blackbriar-thornbelt', 'waist'),
  2247: art('2247-baldric-of-the-last-banner', 'waist'),
  2252: art('2252-ring-of-small-mercies'),
  2259: art('2259-apprentices-tallow-ring'),
  2265: art('2265-ring-of-the-ninth-bell'),
  2271: art('2271-wolfbite-band'),
  2277: art('2277-ratcatchers-signet'),
  2282: art('2282-ring-of-the-owl-keeper'),
  2288: art('2288-bearwards-iron-ring'),
  2294: art('2294-ring-of-the-unseen-hours'),
  2300: art('2300-widow-hasks-mourning-ring'),
  2306: art('2306-ring-of-the-patient-novice'),
  2312: art('2312-bandits-dice-ring'),
  2317: art('2317-ring-of-the-deceivers-warning'),
  2323: art('2323-grim-ring-of-the-ossuary'),
  2329: art('2329-ring-of-the-oathbound-squire'),
  2335: art('2335-telekinetic-coil-of-lord-pell'),
  2341: art('2341-ring-of-counter-whispers'),
  2346: art('2346-ring-of-the-ram-unbroken'),
  2352: art('2352-dull-grey-ioun-pebble'),
  2358: art('2358-shrine-maidens-bead'),
  2364: art('2364-charm-of-restoration'),
  2370: art('2370-mudlarks-lucky-shard'),
  2376: art('2376-essence-forged-charm'),
  2382: art('2382-badge-of-seasons'),
  2388: art('2388-griffon-heart-medal'),
  2394: art('2394-saint-brigids-cross-knot'),
  2400: art('2400-scarab-of-the-sandwalker'),
  2406: art('2406-skull-of-old-mother-grudge'),
  2412: art('2412-ioun-stone-of-greater-absorption'),
  2418: art('2418-king-ardans-ransom-ingot'),
  2423: art('2423-sextons-iron-locket'),
  2429: art('2429-charcoal-burners-kerchief'),
  2435: art('2435-swamp-reed-necklace'),
  2441: art('2441-thieves-kerchief'),
  2447: art('2447-necklace-of-small-fireballs'),
  2453: art('2453-dancing-scarf-of-velis'),
  2458: art('2458-uzunjati-story-amulet'),
  2464: art('2464-masquerade-scarf'),
  2470: art('2470-ouroboros-amulet'),
  2476: art('2476-sea-witchs-amulet'),
  2482: art('2482-keep-stone-amulet'),
  2487: art('2487-bloodbinders-ruby-drop'),
  2493: art('2493-necklace-of-the-ninety-fires'),
  2499: art('2499-locket-of-sealed-nightmares'),
  2503: art('2503-brother-alders-field-dram'),
  2509: art('2509-blood-oath-philter'),
  2515: art('2515-abbess-mildreths-vintage'),
  2521: art('2521-bitterroot-rousing-draught'),
  2527: art('2527-scholars-bitter-mutagen'),
  2534: art('2534-vigil-water-of-the-grey-chapel'),
  2540: art('2540-stonewifes-statue-draught'),
  2546: art('2546-draught-of-the-last-rampart'),
  2551: art('2551-hill-giants-ale'),
  2557: art('2557-bravos-foaming-stoup'),
  2563: art('2563-vengeful-demons-tears'),
  2569: art('2569-undetectable-ink-draught'),
  2575: art('2575-sinew-shock-serum'),
  2581: art('2581-cindergrass-poultice'),
  2589: art('2589-silver-quickness-potion'),
  2595: art('2595-fey-road-cordial'),
  2600: art('2600-hovering-froth'),
  2606: art('2606-red-wyrm-breath-potion'),
  2612: art('2612-swampfire-belch'),
  2618: art('2618-freezing-fog-phial'),
  2624: art('2624-vampires-kiss-philter'),
  2630: art('2630-whetwater-edge-oil'),
  2636: art('2636-concussion-oil'),
  2642: art('2642-swiftness-oil'),
  2648: art('2648-tricky-liniment'),
  2654: art('2654-life-boosting-oil'),
  2660: art('2660-woodsmans-axe-grease'),
  2666: art('2666-cobblers-ironhide-wax'),
  2672: art('2672-cinderward-balm'),
  2678: art('2678-mountain-goat-hoof-balm'),
  2684: art('2684-oil-of-weightlessness'),
  2688: art('2688-ration-tonic'),
  2694: art('2694-cordial-of-understanding'),
  2700: art('2700-hermits-well-water'),
  2705: art('2705-sea-touch-elixir'),
  2711: art('2711-philter-of-morning-bells'),
  2717: art('2717-chameleon-disguise-draught'),
  2723: art('2723-four-leaf-tisane'),
  2729: art('2729-memory-philter'),
  2735: art('2735-drakeheart-mutagen'),
  2741: art('2741-spiced-demonade'),
  2747: art('2747-supreme-troll-blood-draught'),
  2752: art('2752-aunt-hespers-lesson-letter'),
  2758: art('2758-thrice-folded-dart-scroll'),
  2764: art('2764-candlemas-flame-scroll'),
  2770: art('2770-witchlight-wisp-scroll'),
  2776: art('2776-scroll-of-scorching-ribbon'),
  2782: art('2782-moonmoth-silence-strip'),
  2788: art('2788-abbess-merrows-mass-blessing'),
  2794: art('2794-scroll-of-clarity'),
  2800: art('2800-scroll-of-the-bramble-wall'),
  2806: art('2806-mudlarks-slip-strip'),
  2812: art('2812-lucky-hares-foot-talisman'),
  2818: art('2818-bitterwind-scroll'),
  2824: art('2824-moneylenders-sealed-note'),
  2829: art('2829-marsh-gas-psalter'),
  2835: art('2835-owlbear-claw-talisman'),
  2841: art('2841-quickfoot-charter'),
  2847: art('2847-writ-of-unfettered-roads'),
  2853: art('2853-the-falling-star-indenture'),
  2859: art('2859-faerie-road-charter'),
  2865: art('2865-dragons-breath-jug'),
  2871: art('2871-glassblowers-acid-vial'),
  2877: art('2877-glacier-melt-frost-vial'),
  2883: art('2883-thunderhead-in-a-flask'),
  2889: art('2889-chapel-drawn-holy-water'),
  2894: art('2894-tanglefoot-bag'),
  2900: art('2900-spinner-queens-cocoon-cask'),
  2906: art('2906-blindpepper-bomb'),
  2912: art('2912-brimstone-hornet-nest'),
  2917: art('2917-trollbane-firejar'),
  2923: art('2923-ooze-in-a-jam-jar'),
  2929: art('2929-frozen-lake-shard'),
  2935: art('2935-bead-of-force'),
  2941: art('2941-bottled-dawn'),
  2946: art('2946-bramble-bolas'),
  2952: art('2952-honey-trap-crock'),
  2958: art('2958-mimic-spit-jar'),
  2964: art('2964-witchsmoke-phial'),
  2970: art('2970-smugglers-flashbang'),
  2975: art('2975-shieldmoss-bomb'),
  2981: art('2981-nettle-seed-pod'),
  2987: art('2987-sunrise-petard'),
  2993: art('2993-topaz-thunder-nugget'),
  2998: art('2998-ogre-belch-bottle'),
  3003: art('3003-coopers-hempen-coil'),
  3009: art('3009-tallow-candle-stub'),
  3015: art('3015-smugglers-dark-lantern'),
  3021: art('3021-brindle-pond-tackle-box'),
  3027: art('3027-iron-stake-and-mallet'),
  3033: art('3033-prospectors-lodestone-compass'),
  3039: art('3039-greyfang-pass-climbing-kit'),
  3045: art('3045-wayfarers-candle'),
  3051: art('3051-tanglefoot-snare-kit'),
  3057: art('3057-hourglass-of-borrowed-minutes'),
  3063: art('3063-rope-of-entanglement'),
  3069: art('3069-abbess-morrows-surgery-chest'),
  3075: art('3075-thornish-nocturnal'),
  3081: art('3081-the-hag-wifes-deck-of-plenty'),
  3087: art('3087-bloodwhisper-cauldron'),
  3092: art('3092-gloam-bread'),
  3098: art('3098-gammer-huldes-cinnamon-knot'),
  3104: art('3104-missive-mint'),
  3109: art('3109-ploughmans-wedge-of-cheese'),
  3116: art('3116-smoked-eel-in-paper'),
  3122: art('3122-fizzwhistle-gumdrop'),
  3127: art('3127-galvanic-chew'),
  3133: art('3133-hagspit-toffee'),
  3138: art('3138-candied-witchs-finger'),
  3144: art('3144-brannock-hall-mead'),
  3150: art('3150-energizing-tea'),
  3155: art('3155-auric-noodles'),
  3161: art('3161-silver-crescent-cake'),
  3167: art('3167-ambrosia-of-the-fey-road'),
  3172: art('3172-borrowers-bookmark'),
  3178: art('3178-primer-for-farm-children'),
  3184: art('3184-lamplighters-rota'),
  3189: art('3189-monster-hunters-chapbook'),
  3195: art('3195-confidantes-journal'),
  3201: art('3201-codex-of-unimpeded-sight'),
  3207: art('3207-psalter-of-the-blessed'),
  3213: art('3213-sappers-treatise-on-fire'),
  3218: art('3218-bookkeepers-lucky-ledger'),
  3224: art('3224-manual-of-brisk-exercise'),
  3230: art('3230-bloodstained-treatise'),
  3235: art('3235-bestiary-of-the-fey-roads'),
  3241: art('3241-tome-of-clear-thought'),
  3247: art('3247-book-of-ebon-tides'),
  3252: art('3252-tallow-lamp-topaz'),
  3258: art('3258-pinewood-amberheart'),
  3264: art('3264-star-ruby-of-vasht'),
  3271: art('3271-violet-spinel-of-queen-isk'),
  3277: art('3277-crown-of-the-mud-king'),
  3283: art('3283-ashkar-pyramid-pendant'),
  3289: art('3289-fen-emerald-dowry-ring'),
  3295: art('3295-sunken-shrine-coral-branch'),
  3301: art('3301-sun-priests-gold-medallion'),
  3307: art('3307-saffron-pouch-of-qaresh'),
  3313: art('3313-gran-pells-knitting-bag'),
  3319: art('3319-devilish-doubloon'),
  3325: art('3325-obsidian-steed-figurine'),
  3331: art('3331-tin-soldier-of-the-old-guard'),
  3337: art('3337-gaolers-iron-ring-of-keys'),
  3343: art('3343-phoenix-egg'),
  3349: art('3349-old-storytellers-bones'),
  3355: art('3355-clockwork-jester-doll'),
  3361: art('3361-grifters-deck'),
  3367: art('3367-ember-orb-of-the-burnt-tower'),
  3373: art('3373-candle-of-the-vigil-saint'),
  3379: art('3379-tick-stop-watch'),
  3385: art('3385-hearthstone-of-home'),
  3391: art('3391-widow-motts-pig-jar'),
  3397: art('3397-dwarf-thanes-whetting-stone'),
  3404: art('3404-frostbitten-blade-of-the-north', 'main_hand', held('3404-frostbitten-blade-of-the-north', [11, 20])),
  3408: art('3408-sun-crowned-morning-star', 'main_hand', held('3408-sun-crowned-morning-star', [11, 20])),
  3415: art('3415-oath-sworn-elven-longbow', 'main_hand', held('3415-oath-sworn-elven-longbow', [11, 20])),
  3416: art('3416-clockwork-siege-crossbow', 'main_hand', held('3416-clockwork-siege-crossbow', [11, 20])),
  3423: art('3423-shadow-stalkers-jerkin', 'torso'),
  3428: art('3428-black-knights-cursed-harness', 'torso'),
  3429: art('3429-saints-mended-shield', 'off_hand'),
  3434: art('3434-skull-ward-of-the-barrow-kings', 'off_hand'),
  3441: art('3441-witchs-crooked-hat', 'helmet'),
  3450: art('3450-manta-ray-cloak-of-the-deep', 'cape'),
  3457: art('3457-abbey-healers-linen-wraps', 'hands', [part('3457-abbey-healers-linen-wraps-back', 'hand_back'), part('3457-abbey-healers-linen-wraps-front', 'hand_front')]),
  3460: art('3460-seven-league-boots-of-old-roads', 'feet'),
  3465: art('3465-bog-striders-waders', 'feet'),
  3469: art('3469-thorn-court-briar-leggings', 'legs'),
  3475: art('3475-alchemists-bandolier-of-vials', 'waist'),
  3481: art('3481-ring-of-the-regenerating-troll'),
  3490: art('3490-martyrs-heart-reliquary'),
  3498: art('3498-scarf-of-the-winter-shepherd'),
  4001: art('4001-obsidian-greathelm', 'helmet'),
  4032: art('4032-stormcallers-mantle', 'cape'),
  4043: art('4043-huntmasters-bracers', 'hands', [part('4043-huntmasters-bracers-back', 'hand_back'), part('4043-huntmasters-bracers-front', 'hand_front')]),
  4061: art('4061-velvet-slippers', 'feet'),
  4071: art('4071-younger-fang-of-kesh', 'main_hand', held('4071-younger-fang-of-kesh', [11, 20])),
  4080: art('4080-lantern-saints-lamp', 'off_hand'),
  4084: art('4084-lantern-saints-ember-ring'),
  4090: art('4090-tidecallers-wand', 'main_hand', held('4090-tidecallers-wand', [11, 20])),
  4103: art('4103-emberheart-apron-belt', 'waist'),
  4105: art('4105-frostbound-jarls-bearded-axe', 'main_hand', held('4105-frostbound-jarls-bearded-axe', [11, 20], Z.main_hand)),
  4109: art('4109-whispering-grove-staff', 'main_hand', held('4109-whispering-grove-staff', [11, 20])),
  4110: art('4110-whispering-grove-jerkin', 'torso'),
  4115: art('4115-iron-tortoise-helm', 'helmet'),
  4122: art('4122-hollow-kings-funeral-cloak', 'cape'),
  4123: art('4123-hollow-kings-grave-torc'),
  4137: art('4137-wyrmscale-greaves', 'legs'),
  4201: art('4201-shadowmantle-jerkin', 'torso'),
  4222: art('4222-bonecallers-grave-ring'),
  4240: art('4240-thornwarden-briarspear', 'main_hand', held('4240-thornwarden-briarspear', [11, 20])),
  4251: art('4251-siegebreaker-gauntlets', 'hands', [part('4251-siegebreaker-gauntlets-back', 'hand_back'), part('4251-siegebreaker-gauntlets-front', 'hand_front')]),
  4252: art('4252-siegebreaker-great-helm', 'helmet'),
  4271: art('4271-saltcrown-greatcoat', 'cape'),
  4272: art('4272-saltcrown-sea-boots', 'feet'),
  4307: art('4307-glasswing-charm'),
  4309: art('4309-ashlands-warplate', 'torso'),
  4323: art('4323-clockwork-goggles', 'helmet'),
  4324: art('4324-clockwork-bandolier', 'waist'),
  4328: art('4328-crimson-court-rapier', 'main_hand', held('4328-crimson-court-rapier', [11, 20])),
  4329: art('4329-crimson-court-mantle', 'cape'),
  4333: art('4333-oathkeepers-shield', 'off_hand'),
  4334: art('4334-thunderclap-knuckles', 'hands', [part('4334-thunderclap-knuckles-back', 'hand_back'), part('4334-thunderclap-knuckles-front', 'hand_front')]),
  4335: art('4335-thunderclap-boots', 'feet'),
  4403: art('4403-phoenix-ember-torc'),
  4412: art('4412-runeguard-warhammer', 'main_hand', held('4412-runeguard-warhammer', [11, 20])),
  4417: art('4417-celestial-cantors-robe', 'torso'),
  4418: art('4418-celestial-choir-halo', 'helmet'),
  4425: art('4425-goblin-kings-bottlecap-ring'),
  4445: art('4445-frost-vial'),
  4451: art('4451-pot-of-grease'),
  4457: art('4457-witchs-bane-flask'),
  4463: art('4463-quake-charge'),
  // the fourth batch: 400 more pieces (docs/129 §6.2), held weapons mirrored when that clears the face
  1003: art('1003-fenreed-scimitar', 'main_hand', held('1003-fenreed-scimitar', [11, 20])),
  1010: art('1010-drovers-cutlass', 'main_hand', held('1010-drovers-cutlass', [11, 20])),
  1016: art('1016-prentice-smiths-first-blade', 'main_hand', held('1016-prentice-smiths-first-blade', [11, 20])),
  1024: art('1024-hogsback-butcher-sword', 'main_hand', held('1024-hogsback-butcher-sword', [11, 20])),
  1031: art('1031-tinkers-tangle-sword', 'main_hand', held('1031-tinkers-tangle-sword', [11, 20])),
  1038: art('1038-hollowfen-keenblade', 'main_hand', held('1038-hollowfen-keenblade', [11, 20])),
  1045: art('1045-wardwrights-spellguard', 'main_hand', held('1045-wardwrights-spellguard', [11, 20])),
  1051: art('1051-saint-agnets-merciful-edge', 'main_hand', held('1051-saint-agnets-merciful-edge', [11, 20])),
  1058: art('1058-captain-orrels-pursesword', 'main_hand', held('1058-captain-orrels-pursesword', [11, 20])),
  1065: art('1065-fenwatch-greatsword', 'main_hand', held('1065-fenwatch-greatsword', [11, 20])),
  1072: art('1072-winterbourne-brand', 'main_hand', held('1072-winterbourne-brand', [11, 20])),
  1078: art('1078-blade-of-the-fallen-saints', 'main_hand', held('1078-blade-of-the-fallen-saints', [11, 20])),
  1086: art('1086-four-wolves-sabre', 'main_hand', held('1086-four-wolves-sabre', [11, 20])),
  1092: art('1092-hollowcrown-usurpers-sword', 'main_hand', held('1092-hollowcrown-usurpers-sword', [11, 20])),
  1099: art('1099-bastion-of-the-ninth-wall', 'main_hand', held('1099-bastion-of-the-ninth-wall', [11, 20])),
  1106: art('1106-starlings-brilliant-rapier', 'main_hand', held('1106-starlings-brilliant-rapier', [11, 20])),
  1112: art('1112-cutpurses-thumb-knife', 'main_hand', held('1112-cutpurses-thumb-knife', [11, 20])),
  1120: art('1120-fen-eel-gutter', 'main_hand', held('1120-fen-eel-gutter', [11, 20])),
  1126: art('1126-sextons-silver-paring-knife', 'main_hand', held('1126-sextons-silver-paring-knife', [11, 20])),
  1133: art('1133-barber-surgeons-kukri', 'main_hand', held('1133-barber-surgeons-kukri', [11, 20])),
  1141: art('1141-undercroft-gutter-knife', 'main_hand', held('1141-undercroft-gutter-knife', [11, 20])),
  1147: art('1147-shadefield-knife', 'main_hand', held('1147-shadefield-knife', [11, 20])),
  1155: art('1155-trappers-gutting-knife', 'main_hand', held('1155-trappers-gutting-knife', [11, 20])),
  1161: art('1161-heartripper-of-ravens-hollow', 'main_hand', held('1161-heartripper-of-ravens-hollow', [11, 20])),
  1168: art('1168-gloaming-shard', 'main_hand', held('1168-gloaming-shard', [11, 20])),
  1176: art('1176-spiral-athame-of-the-last-coven', 'main_hand', held('1176-spiral-athame-of-the-last-coven', [11, 20])),
  1186: art('1186-ferrystead-boat-axe', 'main_hand', held('1186-ferrystead-boat-axe', [11, 20], Z.main_hand)),
  1192: art('1192-pikemans-glaive', 'main_hand', held('1192-pikemans-glaive', [11, 20])),
  1200: art('1200-executioners-apprentice-axe', 'main_hand', held('1200-executioners-apprentice-axe', [11, 20], Z.main_hand)),
  1208: art('1208-thunder-struck-maul-axe', 'main_hand', held('1208-thunder-struck-maul-axe', [11, 20], Z.main_hand)),
  1215: art('1215-trollhunters-hewing-axe', 'main_hand', held('1215-trollhunters-hewing-axe', [11, 20], Z.main_hand)),
  1222: art('1222-shieldwall-hewing-axe', 'main_hand', held('1222-shieldwall-hewing-axe', [11, 20], Z.main_hand)),
  1230: art('1230-gravewardens-spirit-axe', 'main_hand', held('1230-gravewardens-spirit-axe', [11, 20], Z.main_hand)),
  1236: art('1236-gearhall-buzzsaw-axe', 'main_hand', held('1236-gearhall-buzzsaw-axe', [11, 20], Z.main_hand)),
  1243: art('1243-axe-of-the-undermount-kings', 'main_hand', held('1243-axe-of-the-undermount-kings', [11, 20], Z.main_hand)),
  1256: art('1256-threshers-flail', 'main_hand', held('1256-threshers-flail', [11, 20])),
  1262: art('1262-fishwifes-bat', 'main_hand', held('1262-fishwifes-bat', [11, 20])),
  1270: art('1270-impact-club', 'main_hand', held('1270-impact-club', [11, 20])),
  1276: art('1276-polarizing-mace', 'main_hand', held('1276-polarizing-mace', [11, 20])),
  1282: art('1282-ratcatchers-bludgeon', 'main_hand', held('1282-ratcatchers-bludgeon', [11, 20])),
  1290: art('1290-saint-wends-lantern-hammer', 'main_hand', held('1290-saint-wends-lantern-hammer', [11, 20])),
  1296: art('1296-chimera-flail', 'main_hand', held('1296-chimera-flail', [11, 20])),
  1303: art('1303-sky-hammer', 'main_hand', held('1303-sky-hammer', [11, 20])),
  1310: art('1310-snowshoe-mallet', 'main_hand', held('1310-snowshoe-mallet', [11, 20])),
  1316: art('1316-blacksmiths-swage-hammer', 'main_hand', held('1316-blacksmiths-swage-hammer', [11, 20])),
  1323: art('1323-ditchwater-morningstar', 'main_hand', held('1323-ditchwater-morningstar', [11, 20])),
  1330: art('1330-nightmare-cudgel', 'main_hand', held('1330-nightmare-cudgel', [11, 20])),
  1336: art('1336-dungsweepers-club', 'main_hand', held('1336-dungsweepers-club', [11, 20])),
  1344: art('1344-grudge-keepers-hammer', 'main_hand', held('1344-grudge-keepers-hammer', [11, 20])),
  1350: art('1350-constricting-meteor', 'main_hand', held('1350-constricting-meteor', [11, 20])),
  1357: art('1357-rimeheart-morningstar', 'main_hand', held('1357-rimeheart-morningstar', [11, 20])),
  1364: art('1364-toadstool-mace', 'main_hand', held('1364-toadstool-mace', [11, 20])),
  1374: art('1374-ash-hafted-javelin', 'main_hand', held('1374-ash-hafted-javelin', [11, 20])),
  1381: art('1381-hedgerow-billhook', 'main_hand', held('1381-hedgerow-billhook', [11, 20])),
  1388: art('1388-pestilent-spear', 'main_hand', held('1388-pestilent-spear', [11, 20])),
  1395: art('1395-grounding-spike', 'main_hand', held('1395-grounding-spike', [11, 20])),
  1402: art('1402-mire-stalker-leister', 'main_hand', held('1402-mire-stalker-leister', [11, 20])),
  1410: art('1410-pike-of-the-monster-slayer', 'main_hand', held('1410-pike-of-the-monster-slayer', [11, 20])),
  1416: art('1416-lance-of-suns-radiance', 'main_hand', held('1416-lance-of-suns-radiance', [11, 20])),
  1424: art('1424-lamplighters-pole', 'main_hand', held('1424-lamplighters-pole', [11, 20])),
  1430: art('1430-heavy-boar-lance', 'main_hand', held('1430-heavy-boar-lance', [11, 20])),
  1438: art('1438-calloused-ploughshare-glaive', 'main_hand', held('1438-calloused-ploughshare-glaive', [11, 20])),
  1444: art('1444-steadfast-halberd', 'main_hand', held('1444-steadfast-halberd', [11, 20])),
  1451: art('1451-ferrymans-quant', 'main_hand', held('1451-ferrymans-quant', [11, 20])),
  1458: art('1458-radiant-lance', 'main_hand', held('1458-radiant-lance', [11, 20])),
  1465: art('1465-hells-judgement', 'main_hand', held('1465-hells-judgement', [11, 20])),
  1470: art('1470-drovers-quarterstaff', 'main_hand', held('1470-drovers-quarterstaff', [11, 20])),
  1477: art('1477-dragonscale-bo-staff', 'main_hand', held('1477-dragonscale-bo-staff', [11, 20])),
  1485: art('1485-staff-of-striking', 'main_hand', held('1485-staff-of-striking', [11, 20])),
  1490: art('1490-hobnail-lochaber-axe', 'main_hand', held('1490-hobnail-lochaber-axe', [11, 20], Z.main_hand)),
  1496: art('1496-sonic-polearm-of-the-deep-halls', 'main_hand', held('1496-sonic-polearm-of-the-deep-halls', [11, 20])),
  1501: art('1501-hearthhook-ashbow', 'main_hand', held('1501-hearthhook-ashbow', [11, 20])),
  1508: art('1508-owlwatch-bow', 'main_hand', held('1508-owlwatch-bow', [11, 20])),
  1514: art('1514-goosewing-flatbow', 'main_hand', held('1514-goosewing-flatbow', [11, 20])),
  1523: art('1523-silverbirch-moonbow', 'main_hand', held('1523-silverbirch-moonbow', [11, 20])),
  1529: art('1529-stormheart-bow', 'main_hand', held('1529-stormheart-bow', [11, 20])),
  1535: art('1535-eventide-bow', 'main_hand', held('1535-eventide-bow', [11, 20])),
  1541: art('1541-last-elm-kings-longbow', 'main_hand', held('1541-last-elm-kings-longbow', [11, 20])),
  1549: art('1549-whip-tongue-sling', 'main_hand', held('1549-whip-tongue-sling', [11, 20])),
  1555: art('1555-javelin-of-the-thunder-ford', 'main_hand', held('1555-javelin-of-the-thunder-ford', [11, 20])),
  1562: art('1562-reedcutters-harpoon', 'main_hand', held('1562-reedcutters-harpoon', [11, 20])),
  1568: art('1568-rime-spear-of-the-ice-hollow', 'main_hand', held('1568-rime-spear-of-the-ice-hollow', [11, 20])),
  1571: art('1571-poachers-latchbow', 'main_hand', held('1571-poachers-latchbow', [11, 20])),
  1579: art('1579-lambs-tail-crossbow', 'main_hand', held('1579-lambs-tail-crossbow', [11, 20])),
  1587: art('1587-spiritsight-crossbow', 'main_hand', held('1587-spiritsight-crossbow', [11, 20])),
  1593: art('1593-bogmoss-ambush-bow', 'main_hand', held('1593-bogmoss-ambush-bow', [11, 20])),
  1600: art('1600-the-golden-bolt', 'main_hand', held('1600-the-golden-bolt', [11, 20])),
  1608: art('1608-spider-queens-webcaster', 'main_hand', held('1608-spider-queens-webcaster', [11, 20])),
  1616: art('1616-pedlars-dowsing-wand', 'main_hand', held('1616-pedlars-dowsing-wand', [11, 20])),
  1623: art('1623-gaolers-iron-wand', 'main_hand', held('1623-gaolers-iron-wand', [11, 20])),
  1629: art('1629-brother-ivos-lustral-wand', 'main_hand', held('1629-brother-ivos-lustral-wand', [11, 20])),
  1635: art('1635-fogbank-wand', 'main_hand', held('1635-fogbank-wand', [11, 20])),
  1641: art('1641-wand-of-the-burning-mill', 'main_hand', held('1641-wand-of-the-burning-mill', [11, 20])),
  1649: art('1649-wand-of-overflowing-springs', 'main_hand', held('1649-wand-of-overflowing-springs', [11, 20])),
  1655: art('1655-rod-of-the-thorn-abbot', 'main_hand', held('1655-rod-of-the-thorn-abbot', [11, 20])),
  1661: art('1661-lord-ostrics-rod-of-might', 'main_hand', held('1661-lord-ostrics-rod-of-might', [11, 20])),
  1668: art('1668-rod-of-the-swarming-skulls', 'main_hand', held('1668-rod-of-the-swarming-skulls', [11, 20])),
  1672: art('1672-wanderers-ashstaff', 'main_hand', held('1672-wanderers-ashstaff', [11, 20])),
  1680: art('1680-mossbury-lantern-pole', 'main_hand', held('1680-mossbury-lantern-pole', [11, 20])),
  1686: art('1686-charmers-willow-staff', 'main_hand', held('1686-charmers-willow-staff', [11, 20])),
  1694: art('1694-gravediggers-rest-staff', 'main_hand', held('1694-gravediggers-rest-staff', [11, 20])),
  1700: art('1700-seelie-summer-staff', 'main_hand', held('1700-seelie-summer-staff', [11, 20])),
  1707: art('1707-staff-of-the-old-magi', 'main_hand', held('1707-staff-of-the-old-magi', [11, 20])),
  1714: art('1714-lantern-bearers-crookstaff', 'main_hand', held('1714-lantern-bearers-crookstaff', [11, 20])),
  1719: art('1719-mire-toad-totem', 'off_hand'),
  1726: art('1726-chattering-jaw-of-old-tom', 'off_hand'),
  1734: art('1734-weeping-willow-totem', 'off_hand'),
  1740: art('1740-moonmoth-grimoire', 'off_hand'),
  1747: art('1747-sage-and-soap-breviary', 'off_hand'),
  1754: art('1754-pilgrims-patched-sackcloth', 'torso'),
  1761: art('1761-coat-of-shifting-dyes', 'torso'),
  1767: art('1767-frostwick-robes', 'torso'),
  1774: art('1774-bonfire-dancers-silks', 'torso'),
  1781: art('1781-ferrywomans-oiled-leathers', 'torso'),
  1789: art('1789-shadowfen-assassins-skin', 'torso'),
  1796: art('1796-locust-leather-coat', 'torso'),
  1803: art('1803-jerkin-of-the-unchained-saint', 'torso'),
  1809: art('1809-hedge-knights-chain-shirt', 'torso'),
  1816: art('1816-messenger-mail-of-the-watch', 'torso'),
  1823: art('1823-moonlit-chain-of-the-greyhowl', 'torso'),
  1831: art('1831-chainmail-of-the-last-bridge', 'torso'),
  1839: art('1839-dwarven-plate-of-kharn-dabur', 'torso'),
  1845: art('1845-saint-cuthrics-warding-plate', 'torso'),
  1853: art('1853-smoking-plate-of-old-heithmir', 'torso'),
  1864: art('1864-shepherds-wicker-targe', 'off_hand'),
  1871: art('1871-duskwood-buckler-of-the-gloaming', 'off_hand'),
  1878: art('1878-tollkeepers-coin-studded-round', 'off_hand'),
  1885: art('1885-grasping-shield-of-the-drowned', 'off_hand'),
  1891: art('1891-brother-pells-dancing-shield', 'off_hand'),
  1900: art('1900-lodestone-kite-of-the-iron-hills', 'off_hand'),
  1906: art('1906-heralds-kite-of-the-fair-wind', 'off_hand'),
  1913: art('1913-vanguard-of-the-last-bridge', 'off_hand'),
  1920: art('1920-testudo-of-the-fordwater-pikes', 'off_hand'),
  1927: art('1927-smother-tower-of-the-fire-watch', 'off_hand'),
  1930: art('1930-mummers-grand-entrance-hat', 'helmet'),
  1939: art('1939-capn-bellows-lucky-tricorn', 'helmet'),
  1945: art('1945-crown-of-antlered-oak', 'helmet'),
  1952: art('1952-circlet-of-the-quiet-voice', 'helmet'),
  1960: art('1960-diadem-of-the-glass-queen', 'helmet'),
  1967: art('1967-pearl-divers-bell-helm', 'helmet'),
  1974: art('1974-dread-sallet-of-the-gibbet-road', 'helmet'),
  1981: art('1981-winged-helm-of-saint-hild', 'helmet'),
  1989: art('1989-justicars-visored-helm', 'helmet'),
  1996: art('1996-war-chiefs-plumed-greathelm', 'helmet'),
  2004: art('2004-ferrymans-tarp-cape', 'cape'),
  2011: art('2011-smokehouse-mantle', 'cape'),
  2021: art('2021-pyrefowl-feather-cloak', 'cape'),
  2027: art('2027-hedge-brides-petal-cloak', 'cape'),
  2036: art('2036-will-o-wisp-shimmer-cloak', 'cape'),
  2043: art('2043-squirrel-brocade-cloak', 'cape'),
  2050: art('2050-starfall-pilgrims-cloak', 'cape'),
  2059: art('2059-cutpurses-black-wrist-wraps', 'hands', [part('2059-cutpurses-black-wrist-wraps-back', 'hand_back'), part('2059-cutpurses-black-wrist-wraps-front', 'hand_front')]),
  2067: art('2067-black-band-of-the-night-watch', 'hands', [part('2067-black-band-of-the-night-watch-back', 'hand_back'), part('2067-black-band-of-the-night-watch-front', 'hand_front')]),
  2075: art('2075-strangler-fig-gauntlets', 'hands', [part('2075-strangler-fig-gauntlets-back', 'hand_back'), part('2075-strangler-fig-gauntlets-front', 'hand_front')]),
  2082: art('2082-mushroom-pickers-grub-gloves', 'hands', [part('2082-mushroom-pickers-grub-gloves-back', 'hand_back'), part('2082-mushroom-pickers-grub-gloves-front', 'hand_front')]),
  2090: art('2090-magisters-gilt-gloves', 'hands', [part('2090-magisters-gilt-gloves-back', 'hand_back'), part('2090-magisters-gilt-gloves-front', 'hand_front')]),
  2096: art('2096-vampire-lords-kid-gloves', 'hands', [part('2096-vampire-lords-kid-gloves-back', 'hand_back'), part('2096-vampire-lords-kid-gloves-front', 'hand_front')]),
  2103: art('2103-frostweavers-silver-gloves', 'hands', [part('2103-frostweavers-silver-gloves-back', 'hand_back'), part('2103-frostweavers-silver-gloves-front', 'hand_front')]),
  2107: art('2107-goat-path-hobnails', 'feet'),
  2114: art('2114-satyrs-dancing-shoes', 'feet'),
  2121: art('2121-hare-leap-striders', 'feet'),
  2130: art('2130-steeplejacks-daredevil-boots', 'feet'),
  2136: art('2136-hunt-masters-spurred-boots', 'feet'),
  2144: art('2144-wyvern-hide-stompers', 'feet'),
  2151: art('2151-sandals-of-the-white-stag', 'feet'),
  2157: art('2157-rivet-studded-drover-leggings', 'legs'),
  2164: art('2164-crimson-mummers-hose', 'legs'),
  2172: art('2172-warren-breaker-greaves', 'legs'),
  2178: art('2178-monks-wrapped-trousers', 'legs'),
  2184: art('2184-giantstep-iron-greaves', 'legs'),
  2192: art('2192-glacier-knight-leggings', 'legs'),
  2199: art('2199-elderbark-breeches-of-long-life', 'legs'),
  2204: art('2204-shepherds-lambing-sash', 'waist'),
  2211: art('2211-mere-skaters-belt', 'waist'),
  2217: art('2217-field-nurses-white-sash', 'waist'),
  2225: art('2225-spidersilk-gossamer-sash', 'waist'),
  2232: art('2232-wayfarers-moneybelt', 'waist'),
  2239: art('2239-barrow-wardens-bone-girdle', 'waist'),
  2246: art('2246-archivist-saints-book-sash', 'waist'),
  2257: art('2257-oxbow-ferrymans-ring'),
  2263: art('2263-bramblewick-briar-ring'),
  2269: art('2269-thornhollow-featherfall'),
  2276: art('2276-ring-of-hoarded-breath'),
  2283: art('2283-ring-of-evasions-grace'),
  2289: art('2289-the-jarls-hall-ring'),
  2295: art('2295-mirrorwise-spell-turning-ring'),
  2302: art('2302-fishwifes-gutting-ring'),
  2308: art('2308-sandstriders-brass-ring'),
  2314: art('2314-ring-of-owlish-wakefulness'),
  2321: art('2321-kraken-coil-ring'),
  2327: art('2327-mirewitchs-toad-ring'),
  2333: art('2333-ring-of-imperious-command'),
  2340: art('2340-ring-of-the-tigers-chase'),
  2347: art('2347-saint-orrins-keeping-ring'),
  2351: art('2351-ferrymans-spare-coin'),
  2359: art('2359-corn-dolly-pin'),
  2365: art('2365-flood-charm-of-marrowmere'),
  2371: art('2371-brooch-of-shielding'),
  2378: art('2378-earth-charm-of-hollowhold'),
  2384: art('2384-monkey-kings-lucky-charm'),
  2390: art('2390-thrice-polished-brooch-of-eld'),
  2397: art('2397-ioun-stone-of-regeneration'),
  2403: art('2403-hunters-brooch-of-saint-ervast'),
  2409: art('2409-jade-disc-of-the-lantern-court'),
  2416: art('2416-talisman-of-ultimate-ruin'),
  2421: art('2421-shepherds-wool-scarf'),
  2427: art('2427-midwifes-amber-drop'),
  2434: art('2434-choker-of-the-clear-voice'),
  2440: art('2440-ox-hide-neck-guard'),
  2448: art('2448-necklace-of-adaptation'),
  2454: art('2454-sanguine-pendant'),
  2460: art('2460-earrings-of-the-eclipse'),
  2467: art('2467-torc-of-the-fen-warden'),
  2473: art('2473-seven-bead-fireball-necklace'),
  2480: art('2480-medallion-of-thoughts'),
  2486: art('2486-corvane-court-locket'),
  2494: art('2494-dragonscale-torc-of-five-kings'),
  2506: art('2506-mirefolk-leech-tonic'),
  2512: art('2512-troll-blood-tincture'),
  2519: art('2519-ember-feather-draught'),
  2525: art('2525-owl-night-tisane'),
  2532: art('2532-kettle-witchs-blue-ruin'),
  2539: art('2539-oakheart-barkskin-draught'),
  2545: art('2545-grandmother-pikes-iron-gruel'),
  2553: art('2553-fire-giants-forge-mead'),
  2559: art('2559-bestial-mutagen'),
  2565: art('2565-fog-wardens-glass-draught'),
  2572: art('2572-fenwifes-antidote'),
  2578: art('2578-panacea-of-many-colours'),
  2586: art('2586-hares-foot-elixir'),
  2593: art('2593-gaseous-form-draught'),
  2599: art('2599-leapers-spring-water'),
  2607: art('2607-horned-drake-breath'),
  2613: art('2613-brimstone-gargle'),
  2619: art('2619-wrathful-vapours'),
  2626: art('2626-bottled-melody'),
  2632: art('2632-wounding-oil'),
  2639: art('2639-serpent-oil'),
  2646: art('2646-etherealness-oil'),
  2652: art('2652-saints-bone-balm'),
  2659: art('2659-hobbs-mill-grindstone-oil'),
  2667: art('2667-deepforge-iron-oil'),
  2673: art('2673-fire-eaters-lard'),
  2680: art('2680-owl-fat-eye-salve'),
  2687: art('2687-aligned-oil-of-the-bright-path'),
  2690: art('2690-troll-fat-posset'),
  2696: art('2696-elixir-of-wakefulness'),
  2703: art('2703-winterstep-elixir'),
  2709: art('2709-salamander-elixir'),
  2716: art('2716-infiltrators-elixir'),
  2722: art('2722-boulderhead-bock'),
  2728: art('2728-eidetic-potion'),
  2736: art('2736-serene-mutagen'),
  2742: art('2742-capsaicin-tonic'),
  2748: art('2748-draught-of-infinite-paths'),
  2751: art('2751-mirewatch-riddling-scroll'),
  2759: art('2759-hedgepriests-nudge'),
  2765: art('2765-scroll-of-the-long-reach'),
  2772: art('2772-scroll-of-brother-ashs-blessing'),
  2778: art('2778-scroll-of-shatterglass'),
  2784: art('2784-old-cinderhold-fireball-scroll'),
  2792: art('2792-fenwardens-draining-verse'),
  2798: art('2798-vellum-of-old-graves'),
  2805: art('2805-tinkers-alarm-talisman'),
  2811: art('2811-hound-track-scroll'),
  2817: art('2817-hobs-hearthfire-strip'),
  2825: art('2825-coffin-nail-exorcism-writ'),
  2831: art('2831-moonwell-waking-scroll'),
  2838: art('2838-scrying-vellum-of-the-watch'),
  2844: art('2844-banishing-writ-of-the-abbey'),
  2851: art('2851-sphinx-sealed-answer-scroll'),
  2857: art('2857-clockwrights-stalled-hour'),
  2867: art('2867-last-bead-of-the-necklace'),
  2874: art('2874-dust-of-desiccation'),
  2880: art('2880-hoarfrost-marble'),
  2886: art('2886-blasting-stone'),
  2893: art('2893-saint-wends-lamp-grenade'),
  2899: art('2899-sticky-algae-bomb'),
  2907: art('2907-dust-of-sneezing-and-choking'),
  2913: art('2913-phoenix-ash-grenade'),
  2919: art('2919-bottled-wildfire'),
  2926: art('2926-copper-eater-beetle-tin'),
  2932: art('2932-spark-beetle-jar'),
  2938: art('2938-shrine-font-holy-water'),
  2945: art('2945-witch-bottle-of-returned-ills'),
  2951: art('2951-fisher-kings-casting-net'),
  2957: art('2957-lodestone-shot'),
  2965: art('2965-dust-of-tracelessness'),
  2971: art('2971-cockatrice-feather-dust'),
  2978: art('2978-stinkpot-of-ruin-hollow'),
  2984: art('2984-dragonbone-dust'),
  2990: art('2990-owlbear-musk-flask'),
  2997: art('2997-saint-wends-last-candle'),
  3006: art('3006-pedlars-bundle-of-pitons'),
  3012: art('3012-roll-of-boiled-linen'),
  3018: art('3018-mud-caked-trenching-spade'),
  3025: art('3025-bundle-of-tarred-torches'),
  3031: art('3031-ironweed-climbing-rope'),
  3038: art('3038-concealable-picks-of-tam-weller'),
  3044: art('3044-hollin-watch-spyglass'),
  3050: art('3050-hearthside-communal-flute'),
  3058: art('3058-fen-lure-beast-call'),
  3064: art('3064-skalds-harp-of-kettle-hold'),
  3070: art('3070-ghost-lantern-of-the-barrow'),
  3077: art('3077-weatherwrights-map'),
  3083: art('3083-lantern-of-the-last-vigil'),
  3089: art('3089-teapot-of-mother-clemency'),
  3093: art('3093-trackers-bramble-stew'),
  3099: art('3099-drowned-duck-pickled-egg'),
  3105: art('3105-bramblefen-blackberries'),
  3112: art('3112-salt-pork-ration'),
  3119: art('3119-willowbark-tea'),
  3126: art('3126-companions-broth'),
  3132: art('3132-fury-cocktail'),
  3140: art('3140-saboteurs-liquorice-heart'),
  3146: art('3146-sour-plum-wine'),
  3152: art('3152-healthful-honeypot'),
  3159: art('3159-dragons-blood-pudding'),
  3165: art('3165-feast-box-of-the-nine-hearths'),
  3174: art('3174-field-guide-to-mire-beasts'),
  3181: art('3181-riddle-book-of-mother-gorse'),
  3187: art('3187-herbal-of-the-nine-hedgerows'),
  3193: art('3193-little-book-of-odds'),
  3200: art('3200-recording-book'),
  3208: art('3208-undertakers-manifest'),
  3215: art('3215-draxies-recipe-book'),
  3221: art('3221-shadow-tome'),
  3227: art('3227-book-of-lingering-blaze'),
  3234: art('3234-report-on-the-drowned-abbey'),
  3240: art('3240-manual-of-quickness-of-action'),
  3248: art('3248-codex-of-destruction-and-renewal'),
  3253: art('3253-coin-of-the-drowned-kingdom'),
  3260: art('3260-stonehollow-gold-ingots'),
  3267: art('3267-half-an-amethyst-geode'),
  3274: art('3274-unpolished-bog-emerald'),
  3280: art('3280-painted-portrait-locket'),
  3287: art('3287-marsh-brides-gold-band'),
  3293: art('3293-hill-chiefs-gold-torc'),
  3300: art('3300-chapel-thiefs-candlestick'),
  3306: art('3306-ruby-eyed-bog-idol'),
  3312: art('3312-sack-of-sacks'),
  3320: art('3320-fortunes-own-knucklebone'),
  3326: art('3326-serpentine-owl-of-the-rookery'),
  3333: art('3333-coral-kraken-figurine'),
  3339: art('3339-teal-key-of-the-fey-road'),
  3345: art('3345-addled-basilisk-egg'),
  3352: art('3352-corn-dolly-of-wet-hollow'),
  3358: art('3358-battle-hymn-box'),
  3364: art('3364-far-seers-globe'),
  3371: art('3371-witchs-black-heart'),
  3377: art('3377-conch-of-the-drowned-choir'),
  3383: art('3383-stone-of-the-earth-lords'),
  3390: art('3390-beanstalk-bean'),
  3396: art('3396-witch-bottle-of-iron-nails'),
  3401: art('3401-saints-oath-sword', 'main_hand', held('3401-saints-oath-sword', [11, 20])),
  3407: art('3407-hearth-wardens-hammer', 'main_hand', held('3407-hearth-wardens-hammer', [11, 20])),
  3411: art('3411-hag-queens-silver-pin', 'main_hand', held('3411-hag-queens-silver-pin', [11, 20])),
  3421: art('3421-mithral-mail-of-the-deep-hold', 'torso'),
  3427: art('3427-elven-leaf-mail-of-the-glade', 'torso'),
  3432: art('3432-duellists-mirror-buckler', 'off_hand'),
  3442: art('3442-crystal-helm-of-the-glacier-lord', 'helmet'),
  3448: art('3448-mantle-of-the-phoenix-pyre', 'cape'),
  3458: art('3458-bloodstained-bracers-of-the-pit', 'hands', [part('3458-bloodstained-bracers-of-the-pit-back', 'hand_back'), part('3458-bloodstained-bracers-of-the-pit-front', 'hand_front')]),
  3462: art('3462-frost-walkers-boots', 'feet'),
  3472: art('3472-gravewalkers-black-leggings', 'legs'),
  3477: art('3477-huntmasters-hip-quiver', 'waist'),
  3482: art('3482-fire-wardens-ruby-ring'),
  3488: art('3488-brooch-of-the-hidden-shield'),
  3493: art('3493-amulet-of-the-dragon-mother'),
  4012: art('4012-torc-of-the-red-fjord'),
  4041: art('4041-huntmasters-quiver-cloak', 'cape'),
  4082: art('4082-lantern-saints-hood', 'helmet'),
  4104: art('4104-emberheart-cinder-treads', 'feet'),
  4114: art('4114-iron-tortoise-carapace', 'torso'),
  4116: art('4116-gilded-princes-signet'),
  4132: art('4132-swamp-witchs-toad-locket'),
  4203: art('4203-shadowmantle-grips', 'hands', [part('4203-shadowmantle-grips-back', 'hand_back'), part('4203-shadowmantle-grips-front', 'hand_front')]),
  4211: art('4211-orrery-astrolabe-cap', 'helmet'),
  4214: art('4214-orrery-comet-charm'),
  4231: art('4231-windrunner-cloak', 'cape'),
  4260: art('4260-starweaver-wand', 'main_hand', held('4260-starweaver-wand', [11, 20])),
  4270: art('4270-saltcrown-cutlass', 'main_hand', held('4270-saltcrown-cutlass', [11, 20])),
  4280: art('4280-ashen-pilgrims-censer', 'off_hand'),
  4281: art('4281-ashen-pilgrims-sackcloth', 'torso'),
  4300: art('4300-deepdelvers-mattock', 'main_hand', held('4300-deepdelvers-mattock', [11, 20])),
  4303: art('4303-deepdelvers-hobnails', 'feet'),
  4306: art('4306-glasswing-band'),
  4313: art('4313-ashlands-war-girdle', 'waist'),
  4318: art('4318-wolfpelt-boar-spear', 'main_hand', held('4318-wolfpelt-boar-spear', [11, 20])),
  4402: art('4402-phoenix-ashmantle', 'cape'),
  4407: art('4407-leviathan-deep-helm', 'helmet'),
  4413: art('4413-runeguard-hauberk', 'torso'),
  4415: art('4415-runeguard-graven-gauntlets', 'hands', [part('4415-runeguard-graven-gauntlets-back', 'hand_back'), part('4415-runeguard-graven-gauntlets-front', 'hand_front')]),
  4421: art('4421-goblin-kings-bonking-club', 'main_hand', held('4421-goblin-kings-bonking-club', [11, 20])),
  4422: art('4422-goblin-kings-pot-lid', 'off_hand'),
  4440: art('4440-shadowmoon-pact-band'),
  4444: art('4444-flask-of-holy-water'),
  4450: art('4450-bag-of-caltrops'),
  4458: art('4458-sack-of-flour'),
  4464: art('4464-bottled-gale'),
  // the fifth batch: 150 more pieces (docs/129 §6.3)
  1009: art('1009-shepherds-wolf-sword', 'main_hand', held('1009-shepherds-wolf-sword', [11, 20])),
  1032: art('1032-burghers-smallsword', 'main_hand', held('1032-burghers-smallsword', [11, 20])),
  1052: art('1052-larkspur-vitalizing-sword', 'main_hand', held('1052-larkspur-vitalizing-sword', [11, 20])),
  1071: art('1071-chimneyfire-longsword', 'main_hand', held('1071-chimneyfire-longsword', [11, 20])),
  1093: art('1093-mother-ashgroves-falchion', 'main_hand', held('1093-mother-ashgroves-falchion', [11, 20])),
  1121: art('1121-smugglers-boot-knife', 'main_hand', held('1121-smugglers-boot-knife', [11, 20])),
  1140: art('1140-ninefold-swarmdagger', 'main_hand', held('1140-ninefold-swarmdagger', [11, 20])),
  1158: art('1158-viper-queens-kris', 'main_hand', held('1158-viper-queens-kris', [11, 20])),
  1178: art('1178-nightjar-thief-of-stars', 'main_hand', held('1178-nightjar-thief-of-stars', [11, 20])),
  1191: art('1191-turnip-patch-hoe-axe', 'main_hand', held('1191-turnip-patch-hoe-axe', [11, 20], Z.main_hand)),
  1213: art('1213-ravagers-axe', 'main_hand', held('1213-ravagers-axe', [11, 20], Z.main_hand)),
  1235: art('1235-clear-cutter-of-the-burnt-weald', 'main_hand', held('1235-clear-cutter-of-the-burnt-weald', [11, 20])),
  1260: art('1260-lanternwick-club', 'main_hand', held('1260-lanternwick-club', [11, 20])),
  1281: art('1281-belltower-flail', 'main_hand', held('1281-belltower-flail', [11, 20])),
  1304: art('1304-shepherds-flail', 'main_hand', held('1304-shepherds-flail', [11, 20])),
  1325: art('1325-mile-marker-maul', 'main_hand', held('1325-mile-marker-maul', [11, 20])),
  1346: art('1346-boughshatter', 'main_hand', held('1346-boughshatter', [11, 20])),
  1368: art('1368-grim-ladys-morningstar', 'main_hand', held('1368-grim-ladys-morningstar', [11, 20])),
  1379: art('1379-herders-goad', 'main_hand', held('1379-herders-goad', [11, 20])),
  1401: art('1401-waymarkers-spear', 'main_hand', held('1401-waymarkers-spear', [11, 20])),
  1422: art('1422-sandstrider-assegai', 'main_hand', held('1422-sandstrider-assegai', [11, 20])),
  1443: art('1443-squires-practice-lance', 'main_hand', held('1443-squires-practice-lance', [11, 20])),
  1464: art('1464-dragonslaying-lance', 'main_hand', held('1464-dragonslaying-lance', [11, 20])),
  1482: art('1482-staff-of-the-python', 'main_hand', held('1482-staff-of-the-python', [11, 20])),
  1495: art('1495-reapers-toll', 'main_hand', held('1495-reapers-toll', [11, 20])),
  1511: art('1511-saltwick-wherry-bow', 'main_hand', held('1511-saltwick-wherry-bow', [11, 20])),
  1538: art('1538-witchwood-recurve', 'main_hand', held('1538-witchwood-recurve', [11, 20])),
  1563: art('1563-jade-wheel-chakram', 'main_hand', held('1563-jade-wheel-chakram', [11, 20])),
  1582: art('1582-bridgegate-pavise-bow', 'main_hand', held('1582-bridgegate-pavise-bow', [11, 20])),
  1603: art('1603-trollgate-ballista-bow', 'main_hand', held('1603-trollgate-ballista-bow', [11, 20])),
  1620: art('1620-widow-pells-darting-wand', 'main_hand', held('1620-widow-pells-darting-wand', [11, 20])),
  1643: art('1643-gallows-oak-lightning-rod', 'main_hand', held('1643-gallows-oak-lightning-rod', [11, 20])),
  1664: art('1664-wand-of-the-sleeping-hill', 'main_hand', held('1664-wand-of-the-sleeping-hill', [11, 20])),
  1687: art('1687-spore-shepherds-staff', 'main_hand', held('1687-spore-shepherds-staff', [11, 20])),
  1710: art('1710-staff-of-the-white-necromancer', 'main_hand', held('1710-staff-of-the-white-necromancer', [11, 20])),
  1725: art('1725-scriveners-inkwell-orb', 'off_hand'),
  1746: art('1746-deepwater-anglers-lamp', 'off_hand'),
  1762: art('1762-millers-floury-apron', 'torso'),
  1786: art('1786-troll-skin-jerkin', 'torso'),
  1808: art('1808-scale-shirt-of-fordwater', 'torso'),
  1832: art('1832-sharkskin-lined-mail', 'torso'),
  1856: art('1856-witch-hunters-breastplate', 'torso'),
  1869: art('1869-bivouac-targe-of-the-long-march', 'off_hand'),
  1890: art('1890-ratkings-gnawing-shield', 'off_hand'),
  1911: art('1911-bonesetter-kite-of-saint-ama', 'off_hand'),
  1941: art('1941-mage-hat-of-the-blue-tower', 'helmet'),
  1961: art('1961-vellorans-second-best-hat', 'helmet'),
  1982: art('1982-hedge-knights-bascinet', 'helmet'),
  2009: art('2009-cat-nap-velvet-cloak', 'cape'),
  2031: art('2031-saltmarsh-manta-cloak', 'cape'),
  2051: art('2051-mantle-of-the-green-saint', 'cape'),
  2066: art('2066-ice-fishers-gauntlets', 'hands', [part('2066-ice-fishers-gauntlets-back', 'hand_back'), part('2066-ice-fishers-gauntlets-front', 'hand_front')]),
  2092: art('2092-seneschals-white-gloves', 'hands', [part('2092-seneschals-white-gloves-back', 'hand_back'), part('2092-seneschals-white-gloves-front', 'hand_front')]),
  2117: art('2117-millers-flour-dusted-boots', 'feet'),
  2143: art('2143-avalanche-rider-boots', 'feet'),
  2170: art('2170-berserkers-tartan-kilt', 'legs'),
  2196: art('2196-moonlit-dancers-leggings', 'legs'),
  2212: art('2212-mourners-black-band', 'waist'),
  2234: art('2234-belt-of-the-five-thrones', 'waist'),
  2253: art('2253-hearthkeepers-ember-ring'),
  2260: art('2260-shepherds-watch-ring'),
  2273: art('2273-dowagers-scold-ring'),
  2280: art('2280-brother-ansels-rosary-ring'),
  2301: art('2301-ring-of-the-lost-carter'),
  2320: art('2320-ring-of-dragons-discernment'),
  2342: art('2342-ring-of-wizardry-third-circle'),
  2354: art('2354-pilgrims-tin-badge'),
  2361: art('2361-fairground-glass-eye'),
  2373: art('2373-ioun-stone-of-fortitude'),
  2381: art('2381-oracle-charm-of-granny-pim'),
  2402: art('2402-phoenix-fire-medal'),
  2425: art('2425-pleasing-bouquet-amulet'),
  2431: art('2431-name-pendant-of-pikeman-hob'),
  2444: art('2444-periapt-of-wound-closure'),
  2451: art('2451-moonsilver-necklace'),
  2471: art('2471-choker-of-the-hounds-throat'),
  2490: art('2490-amulet-of-the-planes-crossing'),
  2504: art('2504-elsies-bottled-vim'),
  2511: art('2511-gravediggers-second-wind'),
  2523: art('2523-blackthorn-blood-wine'),
  2533: art('2533-crystal-court-sairazul-blue'),
  2554: art('2554-cloud-giants-thunder-cordial'),
  2574: art('2574-dire-cleansing-draught'),
  2583: art('2583-merciful-balm'),
  2597: art('2597-gecko-syrup'),
  2617: art('2617-tentacle-brew'),
  2637: art('2637-extreme-bludgeoning-oil'),
  2657: art('2657-antimagic-pitch'),
  2677: art('2677-bootmakers-mink-oil'),
  2697: art('2697-bitter-bean-draught'),
  2718: art('2718-bombers-eye-elixir'),
  2738: art('2738-draught-of-giant-growth'),
  2754: art('2754-mossback-mending-scroll'),
  2760: art('2760-scroll-of-the-startled-ox'),
  2773: art('2773-shieldwall-talisman'),
  2777: art('2777-thornwick-snare-scroll'),
  2797: art('2797-scroll-of-the-truthful-lamp'),
  2815: art('2815-goodwife-pells-supper-grace'),
  2834: art('2834-grudgestone-talisman'),
  2852: art('2852-last-rites-reversed'),
  2866: art('2866-bead-of-the-ember-necklace'),
  2872: art('2872-steelscour-flask'),
  2885: art('2885-quarrymans-thunderstone'),
  2892: art('2892-silversoul-bomb'),
  2914: art('2914-salamander-oil-jar'),
  2933: art('2933-thunderclap-gourd'),
  2954: art('2954-sundew-glue-gourd'),
  2973: art('2973-gravity-pearl'),
  2994: art('2994-lime-thistle-burr-ball'),
  3002: art('3002-hooded-bullseye-lantern'),
  3008: art('3008-gamekeepers-gin-trap'),
  3022: art('3022-toll-bridge-hourglass'),
  3026: art('3026-beekeepers-smoker'),
  3043: art('3043-blinding-lantern'),
  3061: art('3061-lantern-of-auspex'),
  3079: art('3079-lantern-of-judgment'),
  3094: art('3094-menders-leek-soup'),
  3100: art('3100-monastery-bitterbrew'),
  3120: art('3120-moon-radish-soup'),
  3141: art('3141-ember-inn-mulled-wine'),
  3160: art('3160-trollmothers-bone-broth'),
  3173: art('3173-bookmark-of-eldritch-insight'),
  3179: art('3179-hedge-witchs-recipe-scraps'),
  3198: art('3198-opus-maleficarum'),
  3220: art('3220-tome-of-knowledge'),
  3239: art('3239-the-ironmongers-drill'),
  3254: art('3254-fordwater-toll-tokens'),
  3259: art('3259-moonmilk-opal'),
  3281: art('3281-ferrymens-guild-pendant'),
  3299: art('3299-royal-scribes-griffon-quill'),
  3314: art('3314-bag-of-the-mountain-tinkers'),
  3318: art('3318-fen-witchs-luckstone'),
  3332: art('3332-moss-bear-figurine'),
  3336: art('3336-saint-pellers-gold-key'),
  3354: art('3354-foundling-home-rag-doll'),
  3372: art('3372-jilted-knights-cracked-heart'),
  3389: art('3389-ferrymans-obol'),
  3402: art('3402-drowned-captains-cutlass', 'main_hand', held('3402-drowned-captains-cutlass', [11, 20])),
  3431: art('3431-wyrm-scale-shield-of-cinders', 'off_hand'),
  3440: art('3440-horned-helm-of-the-burning-host', 'helmet'),
  3478: art('3478-desert-princes-silk-sash', 'waist'),
  3489: art('3489-wolf-tooth-fetish-of-the-clans'),
  4102: art('4102-emberheart-tongs-gauntlets', 'hands', [part('4102-emberheart-tongs-gauntlets-back', 'hand_back'), part('4102-emberheart-tongs-gauntlets-front', 'hand_front')]),
  4108: art('4108-frostbound-rime-boots', 'feet'),
  4117: art('4117-gilded-princes-chain-of-office'),
  4133: art('4133-swamp-witchs-bog-ring'),
  4202: art('4202-shadowmantle-cowl', 'cape'),
  4212: art('4212-orrery-starcloth-robe', 'torso'),
  4310: art('4310-ashlands-great-helm', 'helmet'),
  4455: art('4455-bottled-swarm'),
  // the sixth batch: 900 more pieces (docs/129 §6.4)
  1000: art('1000-hedgewardens-shortsword', 'main_hand', held('1000-hedgewardens-shortsword', [11, 20])),
  1004: art('1004-cold-iron-arming-sword', 'main_hand', held('1004-cold-iron-arming-sword', [11, 20])),
  1006: art('1006-gravediggers-spadesword', 'main_hand', held('1006-gravediggers-spadesword', [11, 20])),
  1011: art('1011-drowned-goose-bar-sword', 'main_hand', held('1011-drowned-goose-bar-sword', [11, 20])),
  1014: art('1014-lamplighters-blade', 'main_hand', held('1014-lamplighters-blade', [11, 20])),
  1015: art('1015-goblin-notched-broadsword', 'main_hand', held('1015-goblin-notched-broadsword', [11, 20])),
  1019: art('1019-fishwifes-filleting-sword', 'main_hand', held('1019-fishwifes-filleting-sword', [11, 20])),
  1021: art('1021-weathered-cavalry-sabre', 'main_hand', held('1021-weathered-cavalry-sabre', [11, 20])),
  1025: art('1025-slate-edge-practice-blade', 'main_hand', held('1025-slate-edge-practice-blade', [11, 20])),
  1028: art('1028-bellringers-blade', 'main_hand', held('1028-bellringers-blade', [11, 20])),
  1029: art('1029-hearthguard-arming-sword', 'main_hand', held('1029-hearthguard-arming-sword', [11, 20])),
  1035: art('1035-galvanic-sidesword', 'main_hand', held('1035-galvanic-sidesword', [11, 20])),
  1037: art('1037-saint-ysoldes-wounding-blade', 'main_hand', held('1037-saint-ysoldes-wounding-blade', [11, 20])),
  1041: art('1041-greenwood-leafblade', 'main_hand', held('1041-greenwood-leafblade', [11, 20])),
  1043: art('1043-hivekeepers-humming-sword', 'main_hand', held('1043-hivekeepers-humming-sword', [11, 20])),
  1046: art('1046-duskmere-gloom-blade', 'main_hand', held('1046-duskmere-gloom-blade', [11, 20])),
  1048: art('1048-charcoal-burners-smoking-sword', 'main_hand', held('1048-charcoal-burners-smoking-sword', [11, 20])),
  1053: art('1053-corporal-pikes-regimental-sabre', 'main_hand', held('1053-corporal-pikes-regimental-sabre', [11, 20])),
  1056: art('1056-sump-wardens-oozecutter', 'main_hand', held('1056-sump-wardens-oozecutter', [11, 20])),
  1060: art('1060-riddlemasters-rapier', 'main_hand', held('1060-riddlemasters-rapier', [11, 20])),
  1062: art('1062-spurned-knights-blade', 'main_hand', held('1062-spurned-knights-blade', [11, 20])),
  1063: art('1063-mercenarys-flamberge', 'main_hand', held('1063-mercenarys-flamberge', [11, 20])),
  1067: art('1067-highwaymans-sabre', 'main_hand', held('1067-highwaymans-sabre', [11, 20])),
  1070: art('1070-wellkeepers-shortsword', 'main_hand', held('1070-wellkeepers-shortsword', [11, 20])),
  1075: art('1075-lady-vells-ballroom-blade', 'main_hand', held('1075-lady-vells-ballroom-blade', [11, 20])),
  1077: art('1077-hillbreaker-greatsword', 'main_hand', held('1077-hillbreaker-greatsword', [11, 20])),
  1080: art('1080-ashvale-bonereaver', 'main_hand', held('1080-ashvale-bonereaver', [11, 20])),
  1082: art('1082-mourners-dirgeblade', 'main_hand', held('1082-mourners-dirgeblade', [11, 20])),
  1085: art('1085-sunforge-searing-blade', 'main_hand', held('1085-sunforge-searing-blade', [11, 20])),
  1088: art('1088-hawkmoor-talonstrike', 'main_hand', held('1088-hawkmoor-talonstrike', [11, 20])),
  1091: art('1091-grey-abbots-penitent-sword', 'main_hand', held('1091-grey-abbots-penitent-sword', [11, 20])),
  1094: art('1094-ridgeback-cavalry-sabre', 'main_hand', held('1094-ridgeback-cavalry-sabre', [11, 20])),
  1097: art('1097-saint-wends-avenger', 'main_hand', held('1097-saint-wends-avenger', [11, 20])),
  1101: art('1101-gram-the-grieving-sword', 'main_hand', held('1101-gram-the-grieving-sword', [11, 20])),
  1103: art('1103-emperor-aldrics-blade', 'main_hand', held('1103-emperor-aldrics-blade', [11, 20])),
  1107: art('1107-stormcrown-flash', 'main_hand', held('1107-stormcrown-flash', [11, 20])),
  1108: art('1108-black-sovereigns-obsidian', 'main_hand', held('1108-black-sovereigns-obsidian', [11, 20])),
  1111: art('1111-poachers-skinning-knife', 'main_hand', held('1111-poachers-skinning-knife', [11, 20])),
  1115: art('1115-ratcatchers-stiletto', 'main_hand', held('1115-ratcatchers-stiletto', [11, 20])),
  1117: art('1117-gilded-letter-opener', 'main_hand', held('1117-gilded-letter-opener', [11, 20])),
  1122: art('1122-miners-pick-knife', 'main_hand', held('1122-miners-pick-knife', [11, 20])),
  1125: art('1125-jack-o-lantern-carver', 'main_hand', held('1125-jack-o-lantern-carver', [11, 20])),
  1127: art('1127-snowshoe-skinner', 'main_hand', held('1127-snowshoe-skinner', [11, 20])),
  1130: art('1130-rope-cutters-hook-knife', 'main_hand', held('1130-rope-cutters-hook-knife', [11, 20])),
  1132: art('1132-adderfang-dagger', 'main_hand', held('1132-adderfang-dagger', [11, 20])),
  1136: art('1136-serpent-dagger-of-mirewood', 'main_hand', held('1136-serpent-dagger-of-mirewood', [11, 20])),
  1138: art('1138-pells-returning-knife', 'main_hand', held('1138-pells-returning-knife', [11, 20])),
  1142: art('1142-moth-wing-stiletto', 'main_hand', held('1142-moth-wing-stiletto', [11, 20])),
  1145: art('1145-spellthiefs-athame', 'main_hand', held('1145-spellthiefs-athame', [11, 20])),
  1148: art('1148-bramblewick-mercy-knife', 'main_hand', held('1148-bramblewick-mercy-knife', [11, 20])),
  1151: art('1151-lizardbane-parrying-dagger', 'main_hand', held('1151-lizardbane-parrying-dagger', [11, 20])),
  1153: art('1153-tallow-chandlers-knife', 'main_hand', held('1153-tallow-chandlers-knife', [11, 20])),
  1157: art('1157-exsanguinating-blade', 'main_hand', held('1157-exsanguinating-blade', [11, 20])),
  1160: art('1160-constant-dagger-of-the-lowroad', 'main_hand', held('1160-constant-dagger-of-the-lowroad', [11, 20])),
  1163: art('1163-flaying-knife-of-the-bone-priest', 'main_hand', held('1163-flaying-knife-of-the-bone-priest', [11, 20])),
  1166: art('1166-frostfang-of-hoarfell', 'main_hand', held('1166-frostfang-of-hoarfell', [11, 20])),
  1169: art('1169-hound-masters-bite', 'main_hand', held('1169-hound-masters-bite', [11, 20])),
  1172: art('1172-kingsbane-the-whisper-blade', 'main_hand', held('1172-kingsbane-the-whisper-blade', [11, 20])),
  1173: art('1173-ivy-moonshades-moment-blade', 'main_hand', held('1173-ivy-moonshades-moment-blade', [11, 20])),
  1177: art('1177-apotheosis-knife', 'main_hand', held('1177-apotheosis-knife', [11, 20])),
  1181: art('1181-woodcutters-hatchet', 'main_hand', held('1181-woodcutters-hatchet', [11, 20], Z.main_hand)),
  1184: art('1184-dwarven-mine-axe', 'main_hand', held('1184-dwarven-mine-axe', [11, 20], Z.main_hand)),
  1187: art('1187-gnoll-splitter-hand-axe', 'main_hand', held('1187-gnoll-splitter-hand-axe', [11, 20], Z.main_hand)),
  1190: art('1190-stonecutters-pick-axe', 'main_hand', held('1190-stonecutters-pick-axe', [11, 20], Z.main_hand)),
  1195: art('1195-shepherdesss-bill-hook', 'main_hand', held('1195-shepherdesss-bill-hook', [11, 20])),
  1196: art('1196-tinkers-spare-hatchet', 'main_hand', held('1196-tinkers-spare-hatchet', [11, 20], Z.main_hand)),
  1199: art('1199-bog-oak-timber-axe', 'main_hand', held('1199-bog-oak-timber-axe', [11, 20], Z.main_hand)),
  1202: art('1202-dwarven-thrower-of-kragmouth', 'main_hand', held('1202-dwarven-thrower-of-kragmouth', [11, 20])),
  1205: art('1205-clear-cutters-axe', 'main_hand', held('1205-clear-cutters-axe', [11, 20], Z.main_hand)),
  1210: art('1210-ghost-metal-hand-axe', 'main_hand', held('1210-ghost-metal-hand-axe', [11, 20], Z.main_hand)),
  1211: art('1211-candied-axe-of-lollyfair', 'main_hand', held('1211-candied-axe-of-lollyfair', [11, 20], Z.main_hand)),
  1216: art('1216-croaking-mire-froggy-axe', 'main_hand', held('1216-croaking-mire-froggy-axe', [11, 20], Z.main_hand)),
  1218: art('1218-heathen-woodsmans-bearded-axe', 'main_hand', held('1218-heathen-woodsmans-bearded-axe', [11, 20], Z.main_hand)),
  1221: art('1221-lumberjacks-twin-bit', 'main_hand', held('1221-lumberjacks-twin-bit', [11, 20])),
  1225: art('1225-skirmishers-tomahawk', 'main_hand', held('1225-skirmishers-tomahawk', [11, 20])),
  1226: art('1226-hollow-tor-prayer-axe', 'main_hand', held('1226-hollow-tor-prayer-axe', [11, 20], Z.main_hand)),
  1228: art('1228-ravager-of-the-saltmarsh', 'main_hand', held('1228-ravager-of-the-saltmarsh', [11, 20])),
  1232: art('1232-candied-axe-of-gingerwick', 'main_hand', held('1232-candied-axe-of-gingerwick', [11, 20], Z.main_hand)),
  1237: art('1237-fenwitchs-bog-axe', 'main_hand', held('1237-fenwitchs-bog-axe', [11, 20], Z.main_hand)),
  1238: art('1238-sunder-axe-of-saint-brannoc', 'main_hand', held('1238-sunder-axe-of-saint-brannoc', [11, 20], Z.main_hand)),
  1241: art('1241-tidebreaker-boarding-axe', 'main_hand', held('1241-tidebreaker-boarding-axe', [11, 20], Z.main_hand)),
  1245: art('1245-old-queen-brisas-bearded-axe', 'main_hand', held('1245-old-queen-brisas-bearded-axe', [11, 20], Z.main_hand)),
  1247: art('1247-emberwyrms-tongue', 'main_hand', held('1247-emberwyrms-tongue', [11, 20])),
  1250: art('1250-mirewood-cudgel', 'main_hand', held('1250-mirewood-cudgel', [11, 20])),
  1253: art('1253-alehouse-bung-mallet', 'main_hand', held('1253-alehouse-bung-mallet', [11, 20])),
  1257: art('1257-blackthorn-shillelagh', 'main_hand', held('1257-blackthorn-shillelagh', [11, 20])),
  1261: art('1261-barrow-door-morningstar', 'main_hand', held('1261-barrow-door-morningstar', [11, 20])),
  1265: art('1265-tollbridge-cosh', 'main_hand', held('1265-tollbridge-cosh', [11, 20])),
  1266: art('1266-brambleknot-club', 'main_hand', held('1266-brambleknot-club', [11, 20])),
  1269: art('1269-rowdys-notched-club', 'main_hand', held('1269-rowdys-notched-club', [11, 20])),
  1272: art('1272-anointing-mace', 'main_hand', held('1272-anointing-mace', [11, 20])),
  1275: art('1275-glacier-maul', 'main_hand', held('1275-glacier-maul', [11, 20])),
  1279: art('1279-conflagration-club', 'main_hand', held('1279-conflagration-club', [11, 20])),
  1280: art('1280-sonic-tuning-mace', 'main_hand', held('1280-sonic-tuning-mace', [11, 20])),
  1285: art('1285-cairn-raiser-hammer', 'main_hand', held('1285-cairn-raiser-hammer', [11, 20])),
  1288: art('1288-gnome-hooked-hammer', 'main_hand', held('1288-gnome-hooked-hammer', [11, 20])),
  1291: art('1291-mace-of-smiting', 'main_hand', held('1291-mace-of-smiting', [11, 20])),
  1293: art('1293-bonebreaker-mace', 'main_hand', held('1293-bonebreaker-mace', [11, 20])),
  1295: art('1295-hammer-of-decrees', 'main_hand', held('1295-hammer-of-decrees', [11, 20])),
  1299: art('1299-clockwork-mace-of-divinity', 'main_hand', held('1299-clockwork-mace-of-divinity', [11, 20])),
  1301: art('1301-belkzen-deadsmasher', 'main_hand', held('1301-belkzen-deadsmasher', [11, 20])),
  1306: art('1306-iron-foe-mace', 'main_hand', held('1306-iron-foe-mace', [11, 20])),
  1308: art('1308-jistkan-colossus-crusher', 'main_hand', held('1308-jistkan-colossus-crusher', [11, 20])),
  1311: art('1311-dune-traders-kanabo', 'main_hand', held('1311-dune-traders-kanabo', [11, 20])),
  1314: art('1314-sextons-knocker', 'main_hand', held('1314-sextons-knocker', [11, 20])),
  1318: art('1318-cobblestone-flail', 'main_hand', held('1318-cobblestone-flail', [11, 20])),
  1320: art('1320-beadles-staff-mace', 'main_hand', held('1320-beadles-staff-mace', [11, 20])),
  1321: art('1321-lumber-camp-mallet', 'main_hand', held('1321-lumber-camp-mallet', [11, 20])),
  1326: art('1326-ox-bone-club', 'main_hand', held('1326-ox-bone-club', [11, 20])),
  1329: art('1329-gloamwood-shillelagh', 'main_hand', held('1329-gloamwood-shillelagh', [11, 20])),
  1333: art('1333-vampire-fang-morningstar', 'main_hand', held('1333-vampire-fang-morningstar', [11, 20])),
  1335: art('1335-clinkers-coal-hammer', 'main_hand', held('1335-clinkers-coal-hammer', [11, 20])),
  1338: art('1338-tipstaff-of-the-assize', 'main_hand', held('1338-tipstaff-of-the-assize', [11, 20])),
  1340: art('1340-molewrights-tunnel-hammer', 'main_hand', held('1340-molewrights-tunnel-hammer', [11, 20])),
  1343: art('1343-smugglers-weighted-oar', 'main_hand', held('1343-smugglers-weighted-oar', [11, 20])),
  1348: art('1348-thorn-brush', 'main_hand', held('1348-thorn-brush', [11, 20])),
  1349: art('1349-black-scorpion-stingmace', 'main_hand', held('1349-black-scorpion-stingmace', [11, 20])),
  1353: art('1353-ankylostar', 'main_hand', held('1353-ankylostar', [11, 20])),
  1355: art('1355-witch-hazel-bludgeon', 'main_hand', held('1355-witch-hazel-bludgeon', [11, 20])),
  1359: art('1359-bell-of-the-drowned-abbey', 'main_hand', held('1359-bell-of-the-drowned-abbey', [11, 20])),
  1362: art('1362-the-gravewards-mace', 'main_hand', held('1362-the-gravewards-mace', [11, 20])),
  1363: art('1363-rat-kings-sceptre', 'main_hand', held('1363-rat-kings-sceptre', [11, 20])),
  1367: art('1367-hammer-of-the-hearth-mother', 'main_hand', held('1367-hammer-of-the-hearth-mother', [11, 20])),
  1370: art('1370-eel-fishers-gig', 'main_hand', held('1370-eel-fishers-gig', [11, 20])),
  1373: art('1373-hayfork-of-the-uprising', 'main_hand', held('1373-hayfork-of-the-uprising', [11, 20])),
  1376: art('1376-toll-gate-partisan', 'main_hand', held('1376-toll-gate-partisan', [11, 20])),
  1380: art('1380-winter-ice-pick-spear', 'main_hand', held('1380-winter-ice-pick-spear', [11, 20])),
  1384: art('1384-barge-hands-boathook', 'main_hand', held('1384-barge-hands-boathook', [11, 20])),
  1385: art('1385-javelin-of-lightning', 'main_hand', held('1385-javelin-of-lightning', [11, 20])),
  1389: art('1389-sanguine-lance', 'main_hand', held('1389-sanguine-lance', [11, 20])),
  1391: art('1391-tiger-fork', 'main_hand', held('1391-tiger-fork', [11, 20])),
  1394: art('1394-doomsweeper-halberd', 'main_hand', held('1394-doomsweeper-halberd', [11, 20])),
  1398: art('1398-fulminating-spear', 'main_hand', held('1398-fulminating-spear', [11, 20])),
  1399: art('1399-wolfsbane-boar-spear', 'main_hand', held('1399-wolfsbane-boar-spear', [11, 20])),
  1405: art('1405-breaker-lance', 'main_hand', held('1405-breaker-lance', [11, 20])),
  1407: art('1407-lionfish-spear', 'main_hand', held('1407-lionfish-spear', [11, 20])),
  1411: art('1411-stoneraiser-javelin', 'main_hand', held('1411-stoneraiser-javelin', [11, 20])),
  1414: art('1414-trident-of-the-yearning-tide', 'main_hand', held('1414-trident-of-the-yearning-tide', [11, 20])),
  1415: art('1415-spear-of-the-stilled-heart', 'main_hand', held('1415-spear-of-the-stilled-heart', [11, 20])),
  1419: art('1419-deepdread-claw', 'main_hand', held('1419-deepdread-claw', [11, 20])),
  1421: art('1421-shrine-keepers-ranseur', 'main_hand', held('1421-shrine-keepers-ranseur', [11, 20])),
  1426: art('1426-gnoll-cracker-spear', 'main_hand', held('1426-gnoll-cracker-spear', [11, 20])),
  1429: art('1429-abbey-vineyard-fork', 'main_hand', held('1429-abbey-vineyard-fork', [11, 20])),
  1431: art('1431-novices-wading-staff-spear', 'main_hand', held('1431-novices-wading-staff-spear', [11, 20])),
  1434: art('1434-hobgoblins-bardiche-pole', 'main_hand', held('1434-hobgoblins-bardiche-pole', [11, 20])),
  1436: art('1436-javelin-of-teleportation', 'main_hand', held('1436-javelin-of-teleportation', [11, 20])),
  1440: art('1440-hydra-spear', 'main_hand', held('1440-hydra-spear', [11, 20])),
  1445: art('1445-pilgrims-crook-spear', 'main_hand', held('1445-pilgrims-crook-spear', [11, 20])),
  1446: art('1446-almoners-pike', 'main_hand', held('1446-almoners-pike', [11, 20])),
  1449: art('1449-rimewater-trident', 'main_hand', held('1449-rimewater-trident', [11, 20])),
  1453: art('1453-trident-of-the-vortex', 'main_hand', held('1453-trident-of-the-vortex', [11, 20])),
  1455: art('1455-dragontooth-javelin', 'main_hand', held('1455-dragontooth-javelin', [11, 20])),
  1459: art('1459-cinder-wasp-pike', 'main_hand', held('1459-cinder-wasp-pike', [11, 20])),
  1460: art('1460-rimeholt-boar-spear', 'main_hand', held('1460-rimeholt-boar-spear', [11, 20])),
  1463: art('1463-knight-captains-lance', 'main_hand', held('1463-knight-captains-lance', [11, 20])),
  1468: art('1468-saint-carrows-pilgrim-lance', 'main_hand', held('1468-saint-carrows-pilgrim-lance', [11, 20])),
  1471: art('1471-hedge-priests-crook', 'main_hand', held('1471-hedge-priests-crook', [11, 20])),
  1474: art('1474-splinter-staff', 'main_hand', held('1474-splinter-staff', [11, 20])),
  1479: art('1479-staff-of-minor-curses', 'main_hand', held('1479-staff-of-minor-curses', [11, 20])),
  1481: art('1481-acrobats-balance-staff', 'main_hand', held('1481-acrobats-balance-staff', [11, 20])),
  1486: art('1486-stygian-crook', 'main_hand', held('1486-stygian-crook', [11, 20])),
  1489: art('1489-woodsmans-pole-axe', 'main_hand', held('1489-woodsmans-pole-axe', [11, 20], Z.main_hand)),
  1491: art('1491-gatehouse-bardiche', 'main_hand', held('1491-gatehouse-bardiche', [11, 20])),
  1494: art('1494-icebreaker-pole-axe', 'main_hand', held('1494-icebreaker-pole-axe', [11, 20], Z.main_hand)),
  1499: art('1499-cragwatch-sparth', 'main_hand', held('1499-cragwatch-sparth', [11, 20])),
  1500: art('1500-mirewood-poachers-bow', 'main_hand', held('1500-mirewood-poachers-bow', [11, 20])),
  1504: art('1504-gallowsfield-longbow', 'main_hand', held('1504-gallowsfield-longbow', [11, 20])),
  1506: art('1506-fenwardens-iron-recurve', 'main_hand', held('1506-fenwardens-iron-recurve', [11, 20])),
  1510: art('1510-reedmarsh-fowling-bow', 'main_hand', held('1510-reedmarsh-fowling-bow', [11, 20])),
  1516: art('1516-adder-coil-bow', 'main_hand', held('1516-adder-coil-bow', [11, 20])),
  1519: art('1519-linnets-song-shortbow', 'main_hand', held('1519-linnets-song-shortbow', [11, 20])),
  1520: art('1520-steppe-riders-longbow', 'main_hand', held('1520-steppe-riders-longbow', [11, 20])),
  1524: art('1524-gatewardens-hornbow', 'main_hand', held('1524-gatewardens-hornbow', [11, 20])),
  1526: art('1526-tithe-collectors-longbow', 'main_hand', held('1526-tithe-collectors-longbow', [11, 20])),
  1530: art('1530-crimson-tithe-bow', 'main_hand', held('1530-crimson-tithe-bow', [11, 20])),
  1533: art('1533-tuskers-greatbow', 'main_hand', held('1533-tuskers-greatbow', [11, 20])),
  1536: art('1536-huntsmans-anthem', 'main_hand', held('1536-huntsmans-anthem', [11, 20])),
  1539: art('1539-oathbow-of-the-weeping-saint', 'main_hand', held('1539-oathbow-of-the-weeping-saint', [11, 20])),
  1543: art('1543-harrowmark-widowmaker', 'main_hand', held('1543-harrowmark-widowmaker', [11, 20])),
  1545: art('1545-goatherds-leather-sling', 'main_hand', held('1545-goatherds-leather-sling', [11, 20])),
  1548: art('1548-caterwaul-sling', 'main_hand', held('1548-caterwaul-sling', [11, 20])),
  1551: art('1551-hailstone-sling', 'main_hand', held('1551-hailstone-sling', [11, 20])),
  1554: art('1554-bolas-of-the-windlass-plain', 'main_hand', held('1554-bolas-of-the-windlass-plain', [11, 20])),
  1557: art('1557-grandfather-hammer-of-kazrak', 'main_hand', held('1557-grandfather-hammer-of-kazrak', [11, 20])),
  1559: art('1559-pedlars-throwing-knives', 'main_hand', held('1559-pedlars-throwing-knives', [11, 20])),
  1564: art('1564-hollowmere-boomerang', 'main_hand', held('1564-hollowmere-boomerang', [11, 20])),
  1567: art('1567-phasemoth-javelin', 'main_hand', held('1567-phasemoth-javelin', [11, 20])),
  1570: art('1570-kell-gatehouse-arbalest', 'main_hand', held('1570-kell-gatehouse-arbalest', [11, 20])),
  1574: art('1574-gravediggers-bolt-thrower', 'main_hand', held('1574-gravediggers-bolt-thrower', [11, 20])),
  1576: art('1576-ratcatchers-hand-crossbow', 'main_hand', held('1576-ratcatchers-hand-crossbow', [11, 20])),
  1580: art('1580-siege-ditch-crossbow', 'main_hand', held('1580-siege-ditch-crossbow', [11, 20])),
  1584: art('1584-customs-house-crossbow', 'main_hand', held('1584-customs-house-crossbow', [11, 20])),
  1588: art('1588-tidecaller-crossbow', 'main_hand', held('1588-tidecaller-crossbow', [11, 20])),
  1590: art('1590-bolt-net-crossbow', 'main_hand', held('1590-bolt-net-crossbow', [11, 20])),
  1592: art('1592-sergeant-holts-arbalest', 'main_hand', held('1592-sergeant-holts-arbalest', [11, 20])),
  1595: art('1595-firethorn-crossbow', 'main_hand', held('1595-firethorn-crossbow', [11, 20])),
  1598: art('1598-kestrel-hand-bow', 'main_hand', held('1598-kestrel-hand-bow', [11, 20])),
  1602: art('1602-witchfinders-wheellock', 'main_hand', held('1602-witchfinders-wheellock', [11, 20])),
  1606: art('1606-king-ostrics-siege-crown-bow', 'main_hand', held('1606-king-ostrics-siege-crown-bow', [11, 20])),
  1609: art('1609-gloamreach-the-last-watch', 'main_hand', held('1609-gloamreach-the-last-watch', [11, 20])),
  1610: art('1610-hazelwood-divining-wand', 'main_hand', held('1610-hazelwood-divining-wand', [11, 20])),
  1613: art('1613-apprentices-cantrip-wand', 'main_hand', held('1613-apprentices-cantrip-wand', [11, 20])),
  1615: art('1615-copyists-reed-wand', 'main_hand', held('1615-copyists-reed-wand', [11, 20])),
  1619: art('1619-lantern-snuffers-wand', 'main_hand', held('1619-lantern-snuffers-wand', [11, 20])),
  1621: art('1621-attic-cobweb-wand', 'main_hand', held('1621-attic-cobweb-wand', [11, 20])),
  1625: art('1625-faerie-fire-wand', 'main_hand', held('1625-faerie-fire-wand', [11, 20])),
  1628: art('1628-saint-mercys-willow-wand', 'main_hand', held('1628-saint-mercys-willow-wand', [11, 20])),
  1631: art('1631-hopfrog-wand', 'main_hand', held('1631-hopfrog-wand', [11, 20])),
  1634: art('1634-belfry-wand', 'main_hand', held('1634-belfry-wand', [11, 20])),
  1636: art('1636-magpie-glint-wand', 'main_hand', held('1636-magpie-glint-wand', [11, 20])),
  1639: art('1639-battle-magisters-baton', 'main_hand', held('1639-battle-magisters-baton', [11, 20])),
  1644: art('1644-stillwater-paralytic-wand', 'main_hand', held('1644-stillwater-paralytic-wand', [11, 20])),
  1646: art('1646-manifold-missile-wand', 'main_hand', held('1646-manifold-missile-wand', [11, 20])),
  1648: art('1648-barrow-ghost-wand', 'main_hand', held('1648-barrow-ghost-wand', [11, 20])),
  1651: art('1651-rod-of-the-drowned-mariner', 'main_hand', held('1651-rod-of-the-drowned-mariner', [11, 20])),
  1654: art('1654-rod-of-icicle-eaves', 'main_hand', held('1654-rod-of-icicle-eaves', [11, 20])),
  1658: art('1658-freerunners-hazel-rod', 'main_hand', held('1658-freerunners-hazel-rod', [11, 20])),
  1660: art('1660-sceptre-of-the-barley-throne', 'main_hand', held('1660-sceptre-of-the-barley-throne', [11, 20])),
  1663: art('1663-wand-of-the-ninefold-fire', 'main_hand', held('1663-wand-of-the-ninefold-fire', [11, 20])),
  1666: art('1666-wand-of-the-unwritten-rune', 'main_hand', held('1666-wand-of-the-unwritten-rune', [11, 20])),
  1670: art('1670-mossbury-drovers-staff', 'main_hand', held('1670-mossbury-drovers-staff', [11, 20])),
  1671: art('1671-crook-of-the-flock', 'main_hand', held('1671-crook-of-the-flock', [11, 20])),
  1675: art('1675-librarians-ladder-staff', 'main_hand', held('1675-librarians-ladder-staff', [11, 20])),
  1677: art('1677-bell-ringers-staff', 'main_hand', held('1677-bell-ringers-staff', [11, 20])),
  1681: art('1681-snowshoe-alpenstock', 'main_hand', held('1681-snowshoe-alpenstock', [11, 20])),
  1683: art('1683-hearthkeepers-firestaff', 'main_hand', held('1683-hearthkeepers-firestaff', [11, 20])),
  1689: art('1689-python-staff-of-the-mire', 'main_hand', held('1689-python-staff-of-the-mire', [11, 20])),
  1690: art('1690-staff-of-the-four-winds', 'main_hand', held('1690-staff-of-the-four-winds', [11, 20])),
  1692: art('1692-withering-thornstaff', 'main_hand', held('1692-withering-thornstaff', [11, 20])),
  1696: art('1696-beekeepers-smoking-staff', 'main_hand', held('1696-beekeepers-smoking-staff', [11, 20])),
  1699: art('1699-striking-staff-of-the-abbot', 'main_hand', held('1699-striking-staff-of-the-abbot', [11, 20])),
  1702: art('1702-staff-of-squalor', 'main_hand', held('1702-staff-of-squalor', [11, 20])),
  1704: art('1704-stygian-crook-of-the-ferry', 'main_hand', held('1704-stygian-crook-of-the-ferry', [11, 20])),
  1706: art('1706-wardstone-staff-of-kell', 'main_hand', held('1706-wardstone-staff-of-kell', [11, 20])),
  1711: art('1711-staff-of-midnight-bells', 'main_hand', held('1711-staff-of-midnight-bells', [11, 20])),
  1715: art('1715-hedge-witchs-cloudy-orb', 'off_hand'),
  1718: art('1718-tallow-vigil-candle', 'off_hand'),
  1721: art('1721-ironwick-reading-lamp', 'off_hand'),
  1724: art('1724-copper-prayer-wheel', 'off_hand'),
  1729: art('1729-orb-of-the-mossbury-seer', 'off_hand'),
  1731: art('1731-ember-eyed-skull-of-cinderhow', 'off_hand'),
  1735: art('1735-gilt-tongue-lexicon-of-varn', 'off_hand'),
  1736: art('1736-pearl-of-the-quiet-harbour', 'off_hand'),
  1739: art('1739-bell-of-the-drowsing-fold', 'off_hand'),
  1742: art('1742-ebbing-night-folio', 'off_hand'),
  1745: art('1745-plague-skull-of-wychmoor', 'off_hand'),
  1750: art('1750-hedge-witchs-smock', 'torso'),
  1755: art('1755-robe-of-useful-pockets', 'torso'),
  1757: art('1757-bellwether-cassock', 'torso'),
  1760: art('1760-archmage-vellorans-vestments', 'torso'),
  1765: art('1765-sunpriests-alb', 'torso'),
  1766: art('1766-cinderveil-robes', 'torso'),
  1770: art('1770-shroud-of-saint-ivel', 'torso'),
  1772: art('1772-scribes-ink-stained-robe', 'torso'),
  1776: art('1776-robe-of-the-bog-abbess', 'torso'),
  1779: art('1779-tanners-boiled-jack', 'torso'),
  1780: art('1780-ratcatchers-studded-vest', 'torso'),
  1787: art('1787-crocodile-back-harness', 'torso'),
  1791: art('1791-drovers-sheepskin', 'torso'),
  1793: art('1793-hodag-spine-jerkin', 'torso'),
  1797: art('1797-dragonauts-wingsuit', 'torso'),
  1798: art('1798-ghoulhide-coat', 'torso'),
  1801: art('1801-rust-eater-shell-coat', 'torso'),
  1804: art('1804-fungal-plates-of-the-mire', 'torso'),
  1807: art('1807-ringmail-of-the-old-levy', 'torso'),
  1812: art('1812-mithral-shirt-of-under-kharn', 'torso'),
  1813: art('1813-adamant-byrnie-of-the-deeps', 'torso'),
  1817: art('1817-stinging-mail-of-harrowmark', 'torso'),
  1819: art('1819-scale-mail-of-ashfang', 'torso'),
  1822: art('1822-celestial-chain-of-saint-aubel', 'torso'),
  1826: art('1826-holy-chain-of-the-saint-errant', 'torso'),
  1827: art('1827-dented-brigand-mail', 'torso'),
  1833: art('1833-gatewardens-breastplate', 'torso'),
  1836: art('1836-bell-forged-cuirass', 'torso'),
  1840: art('1840-plate-of-the-ploughshare-knight', 'torso'),
  1843: art('1843-hellgate-cuirass', 'torso'),
  1844: art('1844-rampart-cuirass-of-fordwater', 'torso'),
  1848: art('1848-frost-giants-cast-off', 'torso'),
  1850: art('1850-lionmane-plate-of-ravensgate', 'torso'),
  1855: art('1855-knight-errants-battered-plate', 'torso'),
  1859: art('1859-osseous-plate-of-the-barrow', 'torso'),
  1860: art('1860-tinkers-pot-lid-buckler', 'off_hand'),
  1863: art('1863-bandits-spiked-buckler', 'off_hand'),
  1866: art('1866-fan-buckler-of-the-lantern-court', 'off_hand'),
  1870: art('1870-adamant-buckler-of-under-kharn', 'off_hand'),
  1874: art('1874-alehouse-table-top-shield', 'off_hand'),
  1875: art('1875-fishermans-cork-round', 'off_hand'),
  1879: art('1879-goatherds-hide-round', 'off_hand'),
  1881: art('1881-oak-and-iron-shield-of-holloway', 'off_hand'),
  1884: art('1884-mushroom-felt-roundshield', 'off_hand'),
  1886: art('1886-sentinel-round-of-the-watch', 'off_hand'),
  1889: art('1889-dawnsilver-roundshield', 'off_hand'),
  1894: art('1894-ploughmans-painted-kite', 'off_hand'),
  1897: art('1897-old-knights-chipped-kite', 'off_hand'),
  1901: art('1901-rivet-laced-kite', 'off_hand'),
  1902: art('1902-warding-escutcheon-of-lynde', 'off_hand'),
  1905: art('1905-burr-studded-kite', 'off_hand'),
  1908: art('1908-bloodroot-kite-of-the-old-wood', 'off_hand'),
  1912: art('1912-ember-scale-kite-of-vhastrax', 'off_hand'),
  1916: art('1916-gatehouse-door-shield', 'off_hand'),
  1917: art('1917-stonewall-tower-of-the-old-march', 'off_hand'),
  1921: art('1921-greenmere-archers-bulwark', 'off_hand'),
  1923: art('1923-hippo-hide-tower-of-the-delta', 'off_hand'),
  1926: art('1926-hearthwall-of-the-kettle-inn', 'off_hand'),
  1928: art('1928-starfall-bulwark-of-brannoch', 'off_hand'),
  1931: art('1931-jester-pims-belled-cap', 'helmet'),
  1934: art('1934-feathered-cap-of-greenmere', 'helmet'),
  1937: art('1937-holly-circlet-of-midwinter', 'helmet'),
  1942: art('1942-circlet-of-scorching', 'helmet'),
  1946: art('1946-mask-of-the-travelling-players', 'helmet'),
  1949: art('1949-hood-of-the-fen-owl', 'helmet'),
  1951: art('1951-ferry-captains-feathered-cap', 'helmet'),
  1955: art('1955-ivy-crown-of-the-hollow-seer', 'helmet'),
  1957: art('1957-faceless-hood-of-night-market', 'helmet'),
  1962: art('1962-sand-king-crown-of-ashur', 'helmet'),
  1966: art('1966-lamp-kettle-of-under-kharn', 'helmet'),
  1969: art('1969-sergeant-mabbs-shouting-helm', 'helmet'),
  1971: art('1971-tactician-kettle-of-the-old-levy', 'helmet'),
  1973: art('1973-cherub-winged-kettle', 'helmet'),
  1977: art('1977-bellhelm-of-saint-clangor', 'helmet'),
  1979: art('1979-wolf-lords-sallet', 'helmet'),
  1984: art('1984-barrow-kings-rusted-helm', 'helmet'),
  1988: art('1988-oni-faced-helm-of-the-red-pass', 'helmet'),
  1993: art('1993-helm-of-the-hundred-gems', 'helmet'),
  1994: art('1994-wayfarers-blinking-helm', 'helmet'),
  1998: art('1998-dragon-crested-helm-of-vhastrax', 'helmet'),
  2000: art('2000-hedgerow-oilcloth', 'cape'),
  2003: art('2003-molecatchers-hood', 'cape'),
  2006: art('2006-mangy-coyote-wrap', 'cape'),
  2010: art('2010-muddy-jacks-tattered-cape', 'cape'),
  2015: art('2015-ashwood-tube-quiver', 'cape'),
  2016: art('2016-mosswood-elvenhood', 'cape'),
  2020: art('2020-wren-feather-aerial-cloak', 'cape'),
  2024: art('2024-underbrush-ghillie', 'cape'),
  2026: art('2026-plain-grey-rakes-cloak', 'cape'),
  2030: art('2030-rat-whisker-cloak', 'cape'),
  2032: art('2032-mountebanks-spangled-cape', 'cape'),
  2037: art('2037-hexbreakers-ash-mantle', 'cape'),
  2040: art('2040-cloak-of-the-fallen-kingdom', 'cape'),
  2042: art('2042-whisperpelt-cloak', 'cape'),
  2046: art('2046-spinner-queens-silk-cloak', 'cape'),
  2048: art('2048-stormcrane-featherwing', 'cape'),
  2052: art('2052-fogbound-wraith-cape', 'cape'),
  2055: art('2055-hedge-layers-hide-mitts', 'hands', [part('2055-hedge-layers-hide-mitts-back', 'hand_back'), part('2055-hedge-layers-hide-mitts-front', 'hand_front')]),
  2058: art('2058-fishwifes-gutting-gloves', 'hands', [part('2058-fishwifes-gutting-gloves-back', 'hand_back'), part('2058-fishwifes-gutting-gloves-front', 'hand_front')]),
  2061: art('2061-conjurers-fair-day-gloves', 'hands', [part('2061-conjurers-fair-day-gloves-back', 'hand_back'), part('2061-conjurers-fair-day-gloves-front', 'hand_front')]),
  2064: art('2064-healers-linen-gloves', 'hands', [part('2064-healers-linen-gloves-back', 'hand_back'), part('2064-healers-linen-gloves-front', 'hand_front')]),
  2070: art('2070-ogre-grip-gauntlets', 'hands', [part('2070-ogre-grip-gauntlets-back', 'hand_back'), part('2070-ogre-grip-gauntlets-front', 'hand_front')]),
  2071: art('2071-lacquered-archers-bracers', 'hands', [part('2071-lacquered-archers-bracers-back', 'hand_back'), part('2071-lacquered-archers-bracers-front', 'hand_front')]),
  2076: art('2076-ruby-eyed-adder-bracers', 'hands', [part('2076-ruby-eyed-adder-bracers-back', 'hand_back'), part('2076-ruby-eyed-adder-bracers-front', 'hand_front')]),
  2078: art('2078-bonesetters-sure-gloves', 'hands', [part('2078-bonesetters-sure-gloves-back', 'hand_back'), part('2078-bonesetters-sure-gloves-front', 'hand_front')]),
  2081: art('2081-briarthorn-vambraces', 'hands', [part('2081-briarthorn-vambraces-back', 'hand_back'), part('2081-briarthorn-vambraces-front', 'hand_front')]),
  2085: art('2085-quarry-breaker-gloves', 'hands', [part('2085-quarry-breaker-gloves-back', 'hand_back'), part('2085-quarry-breaker-gloves-front', 'hand_front')]),
  2086: art('2086-goblin-tanners-gauntlets', 'hands', [part('2086-goblin-tanners-gauntlets-back', 'hand_back'), part('2086-goblin-tanners-gauntlets-front', 'hand_front')]),
  2088: art('2088-adamant-stonechewers', 'hands', [part('2088-adamant-stonechewers-back', 'hand_back'), part('2088-adamant-stonechewers-front', 'hand_front')]),
  2093: art('2093-red-assassins-armbands', 'hands', [part('2093-red-assassins-armbands-back', 'hand_back'), part('2093-red-assassins-armbands-front', 'hand_front')]),
  2097: art('2097-onyx-iron-sphere-gauntlet', 'hands', [part('2097-onyx-iron-sphere-gauntlet-back', 'hand_back'), part('2097-onyx-iron-sphere-gauntlet-front', 'hand_front')]),
  2098: art('2098-heartwood-bough-bracers', 'hands', [part('2098-heartwood-bough-bracers-back', 'hand_back'), part('2098-heartwood-bough-bracers-front', 'hand_front')]),
  2101: art('2101-red-wyrm-handwraps', 'hands', [part('2101-red-wyrm-handwraps-back', 'hand_back'), part('2101-red-wyrm-handwraps-front', 'hand_front')]),
  2106: art('2106-bogtrotters-pattens', 'feet'),
  2110: art('2110-quick-march-brogans', 'feet'),
  2112: art('2112-granny-ottleys-comfy-slippers', 'feet'),
  2116: art('2116-tinkers-road-boots', 'feet'),
  2123: art('2123-thistle-light-windwalkers', 'feet'),
  2124: art('2124-treetop-arboreal-boots', 'feet'),
  2127: art('2127-bloodstride-ox-boots', 'feet'),
  2131: art('2131-chiming-crystal-boots', 'feet'),
  2133: art('2133-pikemans-iron-shod-sabatons', 'feet'),
  2135: art('2135-ooze-waders-galoshes', 'feet'),
  2138: art('2138-featherfall-levitating-boots', 'feet'),
  2142: art('2142-swift-strikers-boots', 'feet'),
  2147: art('2147-gull-winged-sandals', 'feet'),
  2149: art('2149-scullery-maids-glass-slippers', 'feet'),
  2152: art('2152-witch-wedding-red-shoes', 'feet'),
  2154: art('2154-hollow-kings-grave-boots', 'feet'),
  2156: art('2156-fen-waders-oilskin-leggings', 'legs'),
  2160: art('2160-sandwalkers-linen-trousers', 'legs'),
  2162: art('2162-cellar-rat-black-leggings', 'legs'),
  2167: art('2167-gamekeepers-moleskin-trousers', 'legs'),
  2171: art('2171-bandit-queens-red-leggings', 'legs'),
  2175: art('2175-shadowstep-black-hose', 'legs'),
  2176: art('2176-pilgrims-sackcloth-skirt', 'legs'),
  2180: art('2180-hedge-priests-kneeling-breeches', 'legs'),
  2182: art('2182-grave-robbers-kneepads', 'legs'),
  2186: art('2186-wolfrunner-leggings', 'legs'),
  2188: art('2188-brine-knights-armoured-kilt', 'legs'),
  2189: art('2189-mirewalkers-bog-leggings', 'legs'),
  2195: art('2195-kilt-of-the-hundred-clans', 'legs'),
  2201: art('2201-alewifes-keg-strap', 'waist'),
  2205: art('2205-gooseherds-red-sash', 'waist'),
  2207: art('2207-throwers-leather-bandolier', 'waist'),
  2210: art('2210-bluecap-miners-belt', 'waist'),
  2214: art('2214-wrestlers-leather-girdle', 'waist'),
  2216: art('2216-cowards-cure-yellow-sash', 'waist'),
  2220: art('2220-stag-hunters-hip-quiver', 'waist'),
  2222: art('2222-cutthroats-ruby-belt', 'waist'),
  2226: art('2226-jugglers-purple-belt', 'waist'),
  2227: art('2227-knife-jugglers-bandolier', 'waist'),
  2230: art('2230-goblin-ear-trophy-belt', 'waist'),
  2235: art('2235-village-champions-sash', 'waist'),
  2238: art('2238-last-arrow-quiver', 'waist'),
  2240: art('2240-sewer-wardens-waxed-belt', 'waist'),
  2243: art('2243-gnome-tinkers-toolbelt', 'waist'),
  2245: art('2245-green-troll-regeneration-belt', 'waist'),
  2249: art('2249-girdle-of-the-drowned-queen', 'waist'),
  2250: art('2250-tinker-wennas-copper-loop'),
  2254: art('2254-pedlars-thumb-ring'),
  2258: art('2258-rowan-twig-promise-ring'),
  2262: art('2262-saltmarsh-scholars-ring'),
  2266: art('2266-leaping-hare-ring'),
  2267: art('2267-fetterbreakers-ring'),
  2270: art('2270-goldsnatch-ring'),
  2274: art('2274-kettlewick-spark-ring'),
  2278: art('2278-smugglers-turning-ring'),
  2281: art('2281-halfpenny-duelists-ring'),
  2284: art('2284-gallowmere-regrowth-ring'),
  2286: art('2286-ring-of-winters-knight'),
  2290: art('2290-graverobbers-accounting-ring'),
  2292: art('2292-vengeance-seekers-ring'),
  2296: art('2296-djinn-wifes-brass-ring'),
  2297: art('2297-ring-of-four-weathers'),
  2299: art('2299-three-wishes-never-spent'),
  2304: art('2304-snowbound-trappers-ring'),
  2307: art('2307-glassblowers-bubble-ring'),
  2310: art('2310-cutpurses-slip-ring'),
  2313: art('2313-ring-of-the-steady-ox'),
  2315: art('2315-ring-of-the-frog-prince'),
  2318: art('2318-hagstone-loop'),
  2322: art('2322-ring-of-arcane-adjustment'),
  2325: art('2325-ring-of-the-weary-traveller'),
  2328: art('2328-watchmans-night-ring'),
  2330: art('2330-ring-of-the-green-tongue'),
  2332: art('2332-shimmerglass-ring'),
  2336: art('2336-slatelight-ring'),
  2338: art('2338-ring-of-the-gentle-sleeper'),
  2343: art('2343-ring-of-the-undying-root'),
  2344: art('2344-the-telling-ring-of-old-corvane'),
  2348: art('2348-starfall-ring-of-lady-vesque'),
  2350: art('2350-lucky-hapenny-of-bellwether'),
  2355: art('2355-sea-glass-worry-stone'),
  2357: art('2357-hearthstone-chip'),
  2362: art('2362-tarred-sailors-knot'),
  2366: art('2366-scriveners-inkdrop'),
  2367: art('2367-bramble-toll-token'),
  2369: art('2369-ember-seed-ioun'),
  2374: art('2374-ioun-stone-of-insight'),
  2377: art('2377-air-charm-of-the-kestrel-road'),
  2379: art('2379-fire-charm-of-brandoc-smithy'),
  2383: art('2383-corpseward-pendant-pin'),
  2386: art('2386-seven-sided-coin'),
  2389: art('2389-wolf-pack-medal'),
  2392: art('2392-spinning-phidjet'),
  2393: art('2393-toothless-witchs-charm'),
  2396: art('2396-ioun-stone-of-protection'),
  2399: art('2399-aeon-stone-of-the-clear-hour'),
  2404: art('2404-coin-of-the-drowned-exchequer'),
  2407: art('2407-brooch-of-inspiration'),
  2408: art('2408-plague-doctors-skull-pin'),
  2411: art('2411-scarab-of-eternal-protection'),
  2414: art('2414-dread-scarab-of-ankhal'),
  2417: art('2417-the-sphere-of-seven-orbits'),
  2419: art('2419-sun-diamond-of-saint-halloran'),
  2422: art('2422-library-scarf-of-the-grey-abbey'),
  2426: art('2426-tinkers-torc-of-copper-wire'),
  2430: art('2430-silver-band-of-the-night-watch'),
  2433: art('2433-feverfew-garland'),
  2437: art('2437-spiked-dog-collar-of-bruiser'),
  2438: art('2438-vintners-garnet-drop'),
  2442: art('2442-torc-of-the-plough'),
  2445: art('2445-periapt-of-proof-against-venom'),
  2449: art('2449-amulet-of-sustaining-health'),
  2452: art('2452-amulet-of-the-hellcat'),
  2456: art('2456-orm-hide-choker'),
  2457: art('2457-sailors-blue-collar'),
  2461: art('2461-deaths-essence-pendant'),
  2463: art('2463-phylactery-of-the-faithful'),
  2466: art('2466-swarmform-collar'),
  2469: art('2469-weavers-spindle-pendant'),
  2474: art('2474-necklace-of-prayer-beads'),
  2475: art('2475-amulet-of-whirlwinds'),
  2478: art('2478-ziphian-eye-amulet'),
  2481: art('2481-sihedron-star-of-old-thassil'),
  2484: art('2484-choker-of-the-strangler-fig'),
  2488: art('2488-frost-torc-of-the-greywold-thane'),
  2489: art('2489-pendant-of-the-penitent-saint'),
  2492: art('2492-periapt-of-the-undying-saint'),
  2496: art('2496-jaathooms-sky-silk-scarf'),
  2498: art('2498-black-silk-of-the-nightjar'),
  2501: art('2501-mother-comfreys-knitting-tonic'),
  2505: art('2505-lastwall-broth-flask'),
  2508: art('2508-lesser-lifewater'),
  2513: art('2513-shepherds-lambing-cordial'),
  2516: art('2516-sister-marrows-bedside-flask'),
  2517: art('2517-elixir-of-the-undying-orchard'),
  2520: art('2520-wakewater-of-saint-hallow'),
  2524: art('2524-wizards-inkwater'),
  2528: art('2528-clearwell-elixir'),
  2530: art('2530-mana-rattler-liniment'),
  2531: art('2531-starsong-nectar-of-the-eyrie'),
  2536: art('2536-moonwell-sip'),
  2537: art('2537-spring-of-saint-ysolde'),
  2541: art('2541-juggernaut-mutagen'),
  2543: art('2543-shellback-elixir'),
  2547: art('2547-coldiron-resistance-philter'),
  2549: art('2549-oak-and-iron-draught'),
  2550: art('2550-immovable-slate-draught'),
  2555: art('2555-storm-giants-tempest-brandy'),
  2558: art('2558-hewers-bitter-draught'),
  2561: art('2561-bloodhammer-reserve'),
  2564: art('2564-ascendant-dragon-spirit'),
  2566: art('2566-mistform-elixir'),
  2568: art('2568-thief-mothers-hush'),
  2571: art('2571-transparency-tonic'),
  2576: art('2576-focus-cathartic'),
  2579: art('2579-antivenom-milk'),
  2584: art('2584-nectar-of-purification'),
  2585: art('2585-ladys-blessing-tincture'),
  2588: art('2588-lightning-jar-haste'),
  2591: art('2591-emergency-escape-phial'),
  2594: art('2594-bog-mist-phial'),
  2598: art('2598-spider-climb-draught'),
  2601: art('2601-featherfall-philter'),
  2603: art('2603-skaters-gliding-draught'),
  2605: art('2605-dragon-pepper-fire-breath'),
  2609: art('2609-bottled-thunderclap-draught'),
  2611: art('2611-black-bile-acid-draught'),
  2614: art('2614-retaliation-draught'),
  2616: art('2616-octopus-ink-draught'),
  2621: art('2621-owl-eye-clarity-philter'),
  2623: art('2623-effulgent-drop'),
  2627: art('2627-grounding-amber-syrup'),
  2629: art('2629-rovagugs-sour-mud'),
  2631: art('2631-sharpness-unguent-of-tor-vael'),
  2634: art('2634-disrupting-grave-oil'),
  2638: art('2638-numbing-nightshade-oil'),
  2641: art('2641-repulsion-oil'),
  2643: art('2643-kindling-pitch'),
  2645: art('2645-thunderhead-grease'),
  2649: art('2649-shielding-salve'),
  2651: art('2651-flame-layer-balm'),
  2655: art('2655-ghost-oil'),
  2658: art('2658-hunger-oil'),
  2661: art('2661-pilgrims-blade-balm'),
  2663: art('2663-oil-of-keen-edges'),
  2665: art('2665-ironskin-oil'),
  2669: art('2669-saddlers-dubbin'),
  2671: art('2671-salamander-grease'),
  2674: art('2674-adder-gland-oil'),
  2676: art('2676-mire-witchs-black-venom'),
  2681: art('2681-odourless-oil'),
  2683: art('2683-lantern-monk-ward-wax'),
  2686: art('2686-lockpickers-golden-oil'),
  2689: art('2689-hearthfellows-gourd-broth'),
  2692: art('2692-lifeblood-mead-of-hrothgarde'),
  2695: art('2695-moonlit-blossom-tincture'),
  2699: art('2699-elixir-of-the-deep'),
  2702: art('2702-applereed-mutagen'),
  2704: art('2704-elixir-of-the-peaks'),
  2707: art('2707-cats-eye-elixir'),
  2710: art('2710-cooling-elixir'),
  2713: art('2713-stable-form-draught'),
  2715: art('2715-olfactory-obfuscator'),
  2719: art('2719-dragonfly-draught'),
  2721: art('2721-megaphone-mutagen'),
  2725: art('2725-liquid-luck'),
  2727: art('2727-spike-skin-elixir'),
  2731: art('2731-mnemonic-acid'),
  2733: art('2733-enigma-sight-potion'),
  2734: art('2734-oracular-delirium-elixir'),
  2739: art('2739-diminution-drops'),
  2743: art('2743-numbing-tonic'),
  2745: art('2745-malleable-mixture'),
  2749: art('2749-rainbow-extract'),
  2750: art('2750-saint-wends-prayer-strip'),
  2755: art('2755-scroll-of-quickened-heels'),
  2757: art('2757-hedge-witchs-seeing-leaf'),
  2762: art('2762-rimefinger-scroll'),
  2766: art('2766-ward-stitched-blessing'),
  2767: art('2767-poachers-field-sketch'),
  2769: art('2769-scroll-of-the-feather-drop'),
  2774: art('2774-scroll-of-mistwalk'),
  2779: art('2779-lantern-saints-cleansing-leaf'),
  2781: art('2781-webspinners-copied-notes'),
  2786: art('2786-scroll-of-the-hags-bargain'),
  2787: art('2787-bead-seal-of-holding'),
  2790: art('2790-lay-brothers-courage-strip'),
  2793: art('2793-scroll-of-the-second-wind'),
  2796: art('2796-gale-script-scroll'),
  2801: art('2801-grandfather-oaks-rootwrit'),
  2802: art('2802-penitents-iron-litany'),
  2804: art('2804-fetchwick-lesson-letter'),
  2808: art('2808-mourners-dawnlight-strip'),
  2810: art('2810-stumbling-strip'),
  2814: art('2814-feather-step-stone'),
  2816: art('2816-night-shepherds-psalm'),
  2820: art('2820-hexers-pinprick-note'),
  2822: art('2822-chapel-leaf-of-sanctuary'),
  2826: art('2826-scroll-of-mirror-selves'),
  2828: art('2828-tinderfinger-scroll'),
  2830: art('2830-scroll-of-whirling-wards'),
  2833: art('2833-ensnaring-disk'),
  2837: art('2837-wrens-messenger-letter'),
  2840: art('2840-saint-ysoldes-second-dawn'),
  2843: art('2843-hailmongers-scroll'),
  2846: art('2846-hymn-of-the-harvest-mend'),
  2848: art('2848-cloister-ghosts-scroll'),
  2850: art('2850-archmage-corwens-errata'),
  2855: art('2855-mother-ilses-great-mending'),
  2858: art('2858-thunder-kings-word'),
  2861: art('2861-duke-aldrics-warrant-of-valour'),
  2863: art('2863-cinderhold-firepot'),
  2868: art('2868-chandlers-tallow-bomb'),
  2870: art('2870-inflammation-flask'),
  2875: art('2875-bottled-gelatinous-cube'),
  2878: art('2878-rimewold-frost-pellet'),
  2879: art('2879-bottled-blizzard'),
  2882: art('2882-stormglass-jar'),
  2887: art('2887-dwarven-daisy'),
  2890: art('2890-radiance-bomb'),
  2895: art('2895-fisherwifes-weighted-net'),
  2896: art('2896-glue-bomb'),
  2898: art('2898-spider-satchel'),
  2902: art('2902-dust-of-disappearance'),
  2904: art('2904-mirewood-fog-bottle'),
  2908: art('2908-saltpetre-squib'),
  2909: art('2909-hedge-witchs-ember-gourd'),
  2911: art('2911-kobold-firecracker-string'),
  2916: art('2916-emberwyrm-egg'),
  2920: art('2920-tanners-lye-flask'),
  2922: art('2922-mudpot-corrosive'),
  2924: art('2924-grave-mould-spores'),
  2927: art('2927-gravel-snowball'),
  2930: art('2930-yeti-pelt-snowbomb'),
  2934: art('2934-gnomish-bang-pop'),
  2937: art('2937-wind-in-a-bottle'),
  2939: art('2939-saint-orlas-salt-satchel'),
  2942: art('2942-censer-of-the-wending-saint'),
  2944: art('2944-ghost-lantern-shard'),
  2948: art('2948-hunters-bear-trap-satchel'),
  2950: art('2950-sticky-pitch-pot'),
  2953: art('2953-sinew-bolas'),
  2956: art('2956-thornwall-seed'),
  2960: art('2960-gypsum-flash-powder'),
  2962: art('2962-dust-of-dryness'),
  2966: art('2966-moonmilk-glimmer-dust'),
  2967: art('2967-smokeless-night-powder'),
  2969: art('2969-mire-gas-bladder'),
  2974: art('2974-bead-of-repulsion'),
  2977: art('2977-mistletoe-dew-bomb'),
  2980: art('2980-bottled-will-o-wisp'),
  2982: art('2982-bottled-earthquake'),
  2985: art('2985-leech-jar'),
  2988: art('2988-bottled-tempest-ember'),
  2991: art('2991-gelid-crystal-grenade'),
  2995: art('2995-spongebloom-sop'),
  2996: art('2996-grandmother-nettles-pepperpot'),
  3000: art('3000-hobnail-lockpick-roll'),
  3004: art('3004-shepherds-tin-whistle'),
  3007: art('3007-old-hobbs-ten-foot-pole'),
  3011: art('3011-field-dressing-satchel'),
  3014: art('3014-puddlesby-cowbell'),
  3016: art('3016-gammer-peels-knucklebones'),
  3019: art('3019-customs-house-crowbar'),
  3023: art('3023-snapping-chalkline-reel'),
  3028: art('3028-tinkers-grease-pot'),
  3030: art('3030-lantern-of-telling-shadows'),
  3032: art('3032-signalling-compass'),
  3035: art('3035-baleful-wardrum'),
  3037: art('3037-birdsong-whistle'),
  3041: art('3041-gutterwick-cartographers-case'),
  3046: art('3046-pot-of-mapping-ink'),
  3047: art('3047-lockbreakers-bar'),
  3049: art('3049-couriers-message-whistle'),
  3053: art('3053-owl-feather-fletching-kit'),
  3055: art('3055-marked-tavern-deck'),
  3059: art('3059-sir-ansels-polishing-kit'),
  3060: art('3060-horn-of-the-drowned-ferry'),
  3065: art('3065-clock-of-opening'),
  3067: art('3067-dunwick-saddlebag-of-holding'),
  3071: art('3071-drum-of-upheaval'),
  3073: art('3073-arachnolute'),
  3074: art('3074-everburning-torch'),
  3078: art('3078-burglars-lock-and-key'),
  3082: art('3082-grandfather-pells-handkerchief'),
  3085: art('3085-cartographer-royals-atlas'),
  3088: art('3088-clockwork-hare-of-gearwick'),
  3090: art('3090-barrow-bread'),
  3095: art('3095-stem-ginger-chew'),
  3097: art('3097-hagbane-biscuit'),
  3102: art('3102-aurochs-jerky'),
  3106: art('3106-fen-mushrooms-on-toast'),
  3107: art('3107-red-cap-toadstool'),
  3110: art('3110-dandelion-small-beer'),
  3113: art('3113-kettle-hold-oat-stout'),
  3117: art('3117-square-of-wild-honeycomb'),
  3118: art('3118-nettle-tea'),
  3123: art('3123-goats-milk-posset'),
  3125: art('3125-blackbird-pie'),
  3129: art('3129-bowl-of-warding-punch'),
  3131: art('3131-cooperative-waffles'),
  3134: art('3134-warped-wildflower-honey'),
  3136: art('3136-delve-scale-mushroom'),
  3139: art('3139-egg-cream-fizz'),
  3143: art('3143-fen-eel-pie-with-mash'),
  3145: art('3145-crystallised-violets'),
  3148: art('3148-peace-in-dreams-tea'),
  3151: art('3151-longnight-tea'),
  3154: art('3154-phantom-roll'),
  3156: art('3156-ogres-pot-stew'),
  3158: art('3158-mug-of-merry-drinking'),
  3163: art('3163-sun-orchid-honey-cake'),
  3166: art('3166-saint-wends-unending-loaf'),
  3169: art('3169-preserved-moonflower'),
  3170: art('3170-almanac-of-common-wisdom'),
  3175: art('3175-wanted-the-crowe-brothers'),
  3177: art('3177-pilgrims-hymnal-of-saint-hesk'),
  3182: art('3182-footpads-cant-glossary'),
  3185: art('3185-drill-manual-of-the-fenwatch'),
  3186: art('3186-broadsheet-of-fireside-ballads'),
  3190: art('3190-cookery-of-brannock-hall'),
  3192: art('3192-catechism-of-the-lantern-saints'),
  3196: art('3196-family-scrapbook-of-the-thornes'),
  3197: art('3197-triumphant-tavern-keeping'),
  3202: art('3202-book-of-warding-prayers'),
  3204: art('3204-grudge-journal'),
  3209: art('3209-battle-plan-ford-at-harrowmark'),
  3211: art('3211-bestiary-page-mire-goblins'),
  3212: art('3212-couriers-waybook'),
  3216: art('3216-architects-pattern-book'),
  3219: art('3219-tactica-of-general-orsk'),
  3223: art('3223-cataloguing-book'),
  3225: art('3225-manual-of-the-lesser-golem'),
  3228: art('3228-tome-of-scintillating-sleet'),
  3231: art('3231-tyrants-writ'),
  3233: art('3233-battle-plan-kettle-hold-siege'),
  3236: art('3236-tome-of-restorative-cleansing'),
  3238: art('3238-brother-oskars-hearty-living'),
  3243: art('3243-tome-of-understanding'),
  3245: art('3245-radiant-libram'),
  3249: art('3249-last-campaign-of-queen-ysolde'),
  3250: art('3250-mirewater-garnet'),
  3255: art('3255-hedge-witchs-jade-disc'),
  3257: art('3257-ossuary-dice-of-old-ketch'),
  3262: art('3262-green-abbesss-emerald'),
  3266: art('3266-sunken-mints-yellow-sapphire'),
  3268: art('3268-rose-quartz-lump'),
  3270: art('3270-witchlight-tourmaline'),
  3273: art('3273-sea-glass-sapphire-rough'),
  3276: art('3276-gilded-reliquary-crown'),
  3279: art('3279-abbey-handbell-of-st-meer'),
  3282: art('3282-rimewood-sapphire-necklace'),
  3285: art('3285-signet-of-a-dead-baron'),
  3288: art('3288-garnet-square-silver-ring'),
  3291: art('3291-ember-dukes-ruby-ring'),
  3294: art('3294-raiders-silver-arm-ring'),
  3296: art('3296-first-sea-ammonite'),
  3298: art('3298-firebird-plume'),
  3303: art('3303-deed-to-a-drowned-mill'),
  3305: art('3305-moonstone-skull-of-the-abbot'),
  3309: art('3309-widows-jet-heart'),
  3310: art('3310-tinkers-bottomless-satchel'),
  3315: art('3315-tobins-jar-of-elsewhere'),
  3317: art('3317-folded-portable-hole'),
  3322: art('3322-ivory-goat-of-travail'),
  3324: art('3324-marble-elephant-of-queen-isk'),
  3327: art('3327-silver-raven-of-the-lost-post'),
  3329: art('3329-jade-frog-of-the-mire'),
  3334: art('3334-bone-wolf-of-the-barrow'),
  3338: art('3338-sea-gate-key-with-blue-stone'),
  3341: art('3341-bone-key-of-the-ossuary'),
  3342: art('3342-cockatrice-egg'),
  3346: art('3346-saints-finger-bone'),
  3348: art('3348-troll-knuckle-rattle'),
  3351: art('3351-pin-witchs-poppet'),
  3356: art('3356-nurse-amas-lullaby-box'),
  3357: art('3357-jigging-music-box'),
  3360: art('3360-fortune-tellers-deck'),
  3363: art('3363-mother-gloams-seeing-orb'),
  3366: art('3366-sunstone-orb-of-the-dawn-priory'),
  3369: art('3369-lighthouse-keepers-glass-eye'),
  3370: art('3370-trolls-beating-heart'),
  3375: art('3375-feather-token-whirlwind'),
  3378: art('3378-whispering-whelk'),
  3381: art('3381-bell-of-the-last-rites'),
  3384: art('3384-lodestone-of-the-iron-hills'),
  3386: art('3386-grandsire-vells-talking-skull'),
  3388: art('3388-plague-skull-of-grey-fen'),
  3393: art('3393-pressed-fey-rose'),
  3395: art('3395-desert-kings-pocket-sundial'),
  3399: art('3399-blood-oath-knife-of-the-clans'),
  3400: art('3400-ancestral-blade-of-embers', 'main_hand', held('3400-ancestral-blade-of-embers', [11, 20])),
  3406: art('3406-berserkers-ruin-axe', 'main_hand', held('3406-berserkers-ruin-axe', [11, 20], Z.main_hand)),
  3409: art('3409-bog-oak-cudgel-of-the-fen', 'main_hand', held('3409-bog-oak-cudgel-of-the-fen', [11, 20])),
  3410: art('3410-assassin-saints-thorn', 'main_hand', held('3410-assassin-saints-thorn', [11, 20])),
  3412: art('3412-wave-lords-trident', 'main_hand', held('3412-wave-lords-trident', [11, 20])),
  3414: art('3414-hunter-gods-ash-bow', 'main_hand', held('3414-hunter-gods-ash-bow', [11, 20])),
  3417: art('3417-archmages-staff-of-storms', 'main_hand', held('3417-archmages-staff-of-storms', [11, 20])),
  3419: art('3419-witch-queens-bone-wand', 'main_hand', held('3419-witch-queens-bone-wand', [11, 20])),
  3422: art('3422-saints-gilded-plate', 'torso'),
  3425: art('3425-dragon-scale-breastplate', 'torso'),
  3430: art('3430-dwarf-lords-stone-bulwark', 'off_hand'),
  3435: art('3435-witchs-seeing-orb', 'off_hand'),
  3437: art('3437-necromancers-grimoire-of-bone', 'off_hand'),
  3439: art('3439-drowned-kings-circlet', 'helmet'),
  3444: art('3444-wardens-feathered-cap', 'helmet'),
  3446: art('3446-elven-cloak-of-the-hidden-glade', 'cape'),
  3449: art('3449-ermine-of-the-winter-throne', 'cape'),
  3452: art('3452-displacer-hide-cloak', 'cape'),
  3453: art('3453-ogre-strength-gauntlets', 'hands', [part('3453-ogre-strength-gauntlets-back', 'hand_back'), part('3453-ogre-strength-gauntlets-front', 'hand_front')]),
  3455: art('3455-frostbite-gauntlets-of-the-north', 'hands', [part('3455-frostbite-gauntlets-of-the-north-back', 'hand_back'), part('3455-frostbite-gauntlets-of-the-north-front', 'hand_front')]),
  3459: art('3459-archer-queens-bracers', 'hands', [part('3459-archer-queens-bracers-back', 'hand_back'), part('3459-archer-queens-bracers-front', 'hand_front')]),
  3463: art('3463-elven-soft-step-boots', 'feet'),
  3466: art('3466-ember-dancers-slippers', 'feet'),
  3467: art('3467-chain-greaves-of-the-shield-wall', 'legs'),
  3470: art('3470-smugglers-many-pocket-trousers', 'legs'),
  3474: art('3474-dwarvenfolk-belt-of-the-hold', 'waist'),
  3476: art('3476-monk-abbots-sash-of-stillness', 'waist'),
  3479: art('3479-signet-of-the-fallen-house'),
  3483: art('3483-deaths-head-ring-of-the-lich'),
  3485: art('3485-shepherds-copper-luck-ring'),
  3487: art('3487-saints-pilgrim-badge'),
  3492: art('3492-cursed-idol-of-the-drowned-god'),
  3495: art('3495-grave-torc-of-the-barrow-chief'),
  3496: art('3496-locket-of-the-lost-beloved'),
  3499: art('3499-pendant-of-the-moonlit-path'),
  4000: art('4000-obsidian-warplate', 'torso'),
  4002: art('4002-obsidian-gauntlets', 'hands', [part('4002-obsidian-gauntlets-back', 'hand_back'), part('4002-obsidian-gauntlets-front', 'hand_front')]),
  4003: art('4003-obsidian-greaves', 'legs'),
  4004: art('4004-obsidian-sabatons', 'feet'),
  4005: art('4005-obsidian-bulwark', 'off_hand'),
  4020: art('4020-silver-rose-rapier', 'main_hand', held('4020-silver-rose-rapier', [11, 20])),
  4030: art('4030-stormcallers-staff', 'main_hand', held('4030-stormcallers-staff', [11, 20])),
  4031: art('4031-stormcallers-cap', 'helmet'),
  4033: art('4033-stormcallers-signet'),
  4062: art('4062-velvet-cloak', 'cape'),
  4070: art('4070-elder-fang-of-kesh', 'main_hand', held('4070-elder-fang-of-kesh', [11, 20])),
  4081: art('4081-lantern-saints-vestment', 'torso'),
  4083: art('4083-lantern-saints-wick-chain'),
  4091: art('4091-tidecallers-shawl', 'cape'),
  4100: art('4100-emberheart-warhammer', 'main_hand', held('4100-emberheart-warhammer', [11, 20])),
  4106: art('4106-frostbound-greathelm', 'helmet'),
  4111: art('4111-whispering-grove-moss-treads', 'feet'),
  4118: art('4118-gilded-princes-coin-sash', 'waist'),
  4120: art('4120-hollow-kings-warplate', 'torso'),
  4121: art('4121-hollow-kings-crowned-helm', 'helmet'),
  4124: art('4124-hollow-kings-seal'),
  4125: art('4125-moonlit-stalkers-longbow', 'main_hand', held('4125-moonlit-stalkers-longbow', [11, 20])),
  4127: art('4127-moonlit-stalkers-shroud', 'cape'),
  4128: art('4128-sunforged-crusaders-blade', 'main_hand', held('4128-sunforged-crusaders-blade', [11, 20])),
  4129: art('4129-sunforged-kite-shield', 'off_hand'),
  4130: art('4130-sunforged-crusaders-plate', 'torso'),
  4136: art('4136-wyrmscale-grips', 'hands', [part('4136-wyrmscale-grips-back', 'hand_back'), part('4136-wyrmscale-grips-front', 'hand_front')]),
  4138: art('4138-wyrmscale-horned-helm', 'helmet'),
  4220: art('4220-bonecallers-ossuary-wand', 'main_hand', held('4220-bonecallers-ossuary-wand', [11, 20])),
  4221: art('4221-bonecallers-knucklebone-torc'),
  4241: art('4241-thornwarden-barkhide', 'torso'),
  4263: art('4263-starweaver-lodestar-charm'),
  4273: art('4273-saltcrown-doubloon-ring'),
  4282: art('4282-ashen-pilgrims-sandals', 'feet'),
  4290: art('4290-beastmasters-bite-gauntlets', 'hands', [part('4290-beastmasters-bite-gauntlets-back', 'hand_back'), part('4290-beastmasters-bite-gauntlets-front', 'hand_front')]),
  4302: art('4302-deepdelvers-lamp-helm', 'helmet'),
  4305: art('4305-glasswing-mantle', 'cape'),
  4308: art('4308-ashlands-warlords-axe', 'main_hand', held('4308-ashlands-warlords-axe', [11, 20], Z.main_hand)),
  4315: art('4315-plaguedoctors-waxed-coat', 'torso'),
  4316: art('4316-plaguedoctors-gloves', 'hands', [part('4316-plaguedoctors-gloves-back', 'hand_back'), part('4316-plaguedoctors-gloves-front', 'hand_front')]),
  4317: art('4317-plaguedoctors-vial-belt', 'waist'),
  4319: art('4319-wolfpelt-cloak', 'cape'),
  4326: art('4326-nomads-dune-boots', 'feet'),
  4327: art('4327-nomads-headwrap', 'helmet'),
  4330: art('4330-crimson-court-locket'),
  4400: art('4400-phoenix-quill-staff', 'main_hand', held('4400-phoenix-quill-staff', [11, 20])),
  4404: art('4404-phoenix-rekindling-ring'),
  4405: art('4405-leviathan-tide-trident', 'main_hand', held('4405-leviathan-tide-trident', [11, 20])),
  4406: art('4406-leviathan-scale-hauberk', 'torso'),
  4409: art('4409-harlequins-grinning-knife', 'main_hand', held('4409-harlequins-grinning-knife', [11, 20])),
  4411: art('4411-harlequins-motley-cape', 'cape'),
  4414: art('4414-runeguard-helm', 'helmet'),
  4416: art('4416-celestial-choir-hymnal', 'off_hand'),
  4426: art('4426-goblin-kings-lucky-tooth'),
  4427: art('4427-hollowpoint-arbalest', 'main_hand', held('4427-hollowpoint-arbalest', [11, 20])),
  4428: art('4428-hollowpoint-trigger-gloves', 'hands', [part('4428-hollowpoint-trigger-gloves-back', 'hand_back'), part('4428-hollowpoint-trigger-gloves-front', 'hand_front')]),
  4429: art('4429-hollowpoint-spotters-cap', 'helmet'),
  4430: art('4430-eclipse-duskblade', 'main_hand', held('4430-eclipse-duskblade', [11, 20])),
  4431: art('4431-eclipse-sentinel-shield', 'off_hand'),
  4433: art('4433-eclipse-sentinel-locket'),
  4434: art('4434-verdant-titan-maul', 'main_hand', held('4434-verdant-titan-maul', [11, 20])),
  4435: art('4435-verdant-titan-barkhide', 'torso'),
  4436: art('4436-verdant-titan-rootgreaves', 'legs'),
  4437: art('4437-verdant-titan-mossboots', 'feet'),
  4438: art('4438-verdant-titan-vine-girdle', 'waist'),
  4439: art('4439-silvermoon-pact-band'),
  4441: art('4441-flask-of-lamp-oil'),
  4443: art('4443-kindling-flask'),
  4447: art('4447-tarfoot-bag'),
  4449: art('4449-smokestick'),
  4452: art('4452-bucket-of-river-water'),
  4454: art('4454-black-powder-horn'),
  4459: art('4459-jar-of-winter'),
  4461: art('4461-sanctified-censer-bomb'),
  4466: art('4466-bottled-blight'),
  // the seventh batch: the last 723 pieces (docs/129 §6.5)
  1001: art('1001-millbrook-militia-blade', 'main_hand', held('1001-millbrook-militia-blade', [11, 20])),
  1005: art('1005-silvered-sidesword', 'main_hand', held('1005-silvered-sidesword', [11, 20])),
  1008: art('1008-rushcutters-falchion', 'main_hand', held('1008-rushcutters-falchion', [11, 20])),
  1013: art('1013-watchmans-sidesword', 'main_hand', held('1013-watchmans-sidesword', [11, 20])),
  1018: art('1018-legionarys-gladius', 'main_hand', held('1018-legionarys-gladius', [11, 20])),
  1020: art('1020-old-kingdom-spatha', 'main_hand', held('1020-old-kingdom-spatha', [11, 20])),
  1023: art('1023-pilgrims-walking-sword', 'main_hand', held('1023-pilgrims-walking-sword', [11, 20])),
  1026: art('1026-ferrymans-hookblade', 'main_hand', held('1026-ferrymans-hookblade', [11, 20])),
  1033: art('1033-cinderbrand', 'main_hand', held('1033-cinderbrand', [11, 20])),
  1036: art('1036-ghost-touch-arming-sword', 'main_hand', held('1036-ghost-touch-arming-sword', [11, 20])),
  1040: art('1040-dunewind-scimitar', 'main_hand', held('1040-dunewind-scimitar', [11, 20])),
  1042: art('1042-riverwifes-blade', 'main_hand', held('1042-riverwifes-blade', [11, 20])),
  1047: art('1047-fate-thread-sidesword', 'main_hand', held('1047-fate-thread-sidesword', [11, 20])),
  1050: art('1050-mirrorpond-flashblade', 'main_hand', held('1050-mirrorpond-flashblade', [11, 20])),
  1055: art('1055-trollkin-splitter', 'main_hand', held('1055-trollkin-splitter', [11, 20])),
  1057: art('1057-scalebreaker-falchion', 'main_hand', held('1057-scalebreaker-falchion', [11, 20])),
  1061: art('1061-brambleheart-sword', 'main_hand', held('1061-brambleheart-sword', [11, 20])),
  1066: art('1066-heralds-golden-sidesword', 'main_hand', held('1066-heralds-golden-sidesword', [11, 20])),
  1068: art('1068-dancing-masters-smallsword', 'main_hand', held('1068-dancing-masters-smallsword', [11, 20])),
  1073: art('1073-leechwater-sabre', 'main_hand', held('1073-leechwater-sabre', [11, 20])),
  1076: art('1076-wyrmhollow-scalebane', 'main_hand', held('1076-wyrmhollow-scalebane', [11, 20])),
  1081: art('1081-crookback-hexen-blade', 'main_hand', held('1081-crookback-hexen-blade', [11, 20])),
  1083: art('1083-sea-hags-kelpblade', 'main_hand', held('1083-sea-hags-kelpblade', [11, 20])),
  1087: art('1087-ditchwater-blink-blade', 'main_hand', held('1087-ditchwater-blink-blade', [11, 20])),
  1090: art('1090-thornqueens-rapier', 'main_hand', held('1090-thornqueens-rapier', [11, 20])),
  1096: art('1096-dawnhallow-sunblade', 'main_hand', held('1096-dawnhallow-sunblade', [11, 20])),
  1098: art('1098-widow-merrins-luck-blade', 'main_hand', held('1098-widow-merrins-luck-blade', [11, 20])),
  1102: art('1102-tyrfing-the-thrice-cursed', 'main_hand', held('1102-tyrfing-the-thrice-cursed', [11, 20])),
  1104: art('1104-glasswater-mirrorblade', 'main_hand', held('1104-glasswater-mirrorblade', [11, 20])),
  1109: art('1109-kingsfall-the-hollow-throne', 'main_hand', held('1109-kingsfall-the-hollow-throne', [11, 20])),
  1114: art('1114-tanners-hide-knife', 'main_hand', held('1114-tanners-hide-knife', [11, 20])),
  1116: art('1116-brine-row-oyster-knife', 'main_hand', held('1116-brine-row-oyster-knife', [11, 20])),
  1119: art('1119-kitchen-cleaver-of-the-brass-pot', 'main_hand', held('1119-kitchen-cleaver-of-the-brass-pot', [11, 20], Z.main_hand)),
  1124: art('1124-glaziers-glass-knife', 'main_hand', held('1124-glaziers-glass-knife', [11, 20])),
  1129: art('1129-scullery-shiv', 'main_hand', held('1129-scullery-shiv', [11, 20])),
  1131: art('1131-goblin-ear-knife', 'main_hand', held('1131-goblin-ear-knife', [11, 20])),
  1135: art('1135-flicker-knife', 'main_hand', held('1135-flicker-knife', [11, 20])),
  1137: art('1137-phantom-fang', 'main_hand', held('1137-phantom-fang', [11, 20])),
  1143: art('1143-coldwake-icicle-dirk', 'main_hand', held('1143-coldwake-icicle-dirk', [11, 20])),
  1146: art('1146-pickpocket-princes-dirk', 'main_hand', held('1146-pickpocket-princes-dirk', [11, 20])),
  1150: art('1150-saltcellar-dagger', 'main_hand', held('1150-saltcellar-dagger', [11, 20])),
  1152: art('1152-fortune-tellers-knife', 'main_hand', held('1152-fortune-tellers-knife', [11, 20])),
  1156: art('1156-mistwalkers-kris', 'main_hand', held('1156-mistwalkers-kris', [11, 20])),
  1162: art('1162-ghast-stiletto', 'main_hand', held('1162-ghast-stiletto', [11, 20])),
  1165: art('1165-obsidian-butterfly-knife', 'main_hand', held('1165-obsidian-butterfly-knife', [11, 20])),
  1167: art('1167-witch-fire-athame', 'main_hand', held('1167-witch-fire-athame', [11, 20])),
  1171: art('1171-bishops-mercy-misericord', 'main_hand', held('1171-bishops-mercy-misericord', [11, 20])),
  1174: art('1174-heartbreaks-kiss', 'main_hand', held('1174-heartbreaks-kiss', [11, 20])),
  1179: art('1179-old-mabs-pinking-knife', 'main_hand', held('1179-old-mabs-pinking-knife', [11, 20])),
  1182: art('1182-crookfell-felling-axe', 'main_hand', held('1182-crookfell-felling-axe', [11, 20], Z.main_hand)),
  1185: art('1185-raiders-bearded-axe', 'main_hand', held('1185-raiders-bearded-axe', [11, 20], Z.main_hand)),
  1189: art('1189-hearth-ward-kindling-axe', 'main_hand', held('1189-hearth-ward-kindling-axe', [11, 20], Z.main_hand)),
  1194: art('1194-militia-sparth', 'main_hand', held('1194-militia-sparth', [11, 20])),
  1197: art('1197-highland-lochaber-axe', 'main_hand', held('1197-highland-lochaber-axe', [11, 20], Z.main_hand)),
  1201: art('1201-wagoners-wheel-hatchet', 'main_hand', held('1201-wagoners-wheel-hatchet', [11, 20], Z.main_hand)),
  1204: art('1204-retribution-axe', 'main_hand', held('1204-retribution-axe', [11, 20], Z.main_hand)),
  1207: art('1207-kindlecleaver', 'main_hand', held('1207-kindlecleaver', [11, 20], Z.main_hand)),
  1212: art('1212-hollow-tor-chieftains-axe', 'main_hand', held('1212-hollow-tor-chieftains-axe', [11, 20], Z.main_hand)),
  1217: art('1217-ooze-scraper-of-gullhaven', 'main_hand', held('1217-ooze-scraper-of-gullhaven', [11, 20])),
  1220: art('1220-quartermasters-requisition-axe', 'main_hand', held('1220-quartermasters-requisition-axe', [11, 20], Z.main_hand)),
  1223: art('1223-stump-splitters-maul-axe', 'main_hand', held('1223-stump-splitters-maul-axe', [11, 20], Z.main_hand)),
  1227: art('1227-berserkers-red-axe', 'main_hand', held('1227-berserkers-red-axe', [11, 20], Z.main_hand)),
  1231: art('1231-giant-feller-of-ettinmoor', 'main_hand', held('1231-giant-feller-of-ettinmoor', [11, 20])),
  1233: art('1233-clockwrights-moment-axe', 'main_hand', held('1233-clockwrights-moment-axe', [11, 20], Z.main_hand)),
  1240: art('1240-widowmaker-of-crowsgate', 'main_hand', held('1240-widowmaker-of-crowsgate', [11, 20])),
  1242: art('1242-reavers-hooked-glaive', 'main_hand', held('1242-reavers-hooked-glaive', [11, 20])),
  1246: art('1246-ymmers-frostgrave-greataxe', 'main_hand', held('1246-ymmers-frostgrave-greataxe', [11, 20], Z.main_hand)),
  1248: art('1248-the-headsman-of-graymoot', 'main_hand', held('1248-the-headsman-of-graymoot', [11, 20])),
  1251: art('1251-tinkers-peen-hammer', 'main_hand', held('1251-tinkers-peen-hammer', [11, 20])),
  1254: art('1254-fordwater-watch-truncheon', 'main_hand', held('1254-fordwater-watch-truncheon', [11, 20])),
  1259: art('1259-harrowmark-cross-peen', 'main_hand', held('1259-harrowmark-cross-peen', [11, 20])),
  1264: art('1264-bellringers-mallet', 'main_hand', held('1264-bellringers-mallet', [11, 20])),
  1267: art('1267-coopers-hoop-mallet', 'main_hand', held('1267-coopers-hoop-mallet', [11, 20])),
  1271: art('1271-slipshod-hammer', 'main_hand', held('1271-slipshod-hammer', [11, 20])),
  1274: art('1274-storm-hammer', 'main_hand', held('1274-storm-hammer', [11, 20])),
  1277: art('1277-auspicious-sceptre', 'main_hand', held('1277-auspicious-sceptre', [11, 20])),
  1284: art('1284-pewterers-planishing-hammer', 'main_hand', held('1284-pewterers-planishing-hammer', [11, 20])),
  1286: art('1286-penny-flail-of-the-fair', 'main_hand', held('1286-penny-flail-of-the-fair', [11, 20])),
  1289: art('1289-mudlarks-paddle-club', 'main_hand', held('1289-mudlarks-paddle-club', [11, 20])),
  1294: art('1294-forgefire-hammer', 'main_hand', held('1294-forgefire-hammer', [11, 20])),
  1298: art('1298-tenebrous-flail-of-screams', 'main_hand', held('1298-tenebrous-flail-of-screams', [11, 20])),
  1300: art('1300-kinetic-greatclub', 'main_hand', held('1300-kinetic-greatclub', [11, 20])),
  1305: art('1305-dwarven-thrower', 'main_hand', held('1305-dwarven-thrower', [11, 20])),
  1309: art('1309-osseous-warhammer', 'main_hand', held('1309-osseous-warhammer', [11, 20])),
  1313: art('1313-kettle-bottom-mace', 'main_hand', held('1313-kettle-bottom-mace', [11, 20])),
  1315: art('1315-footpads-sap', 'main_hand', held('1315-footpads-sap', [11, 20])),
  1319: art('1319-peat-cutters-mallet', 'main_hand', held('1319-peat-cutters-mallet', [11, 20])),
  1324: art('1324-candlemakers-dipping-rod', 'main_hand', held('1324-candlemakers-dipping-rod', [11, 20])),
  1328: art('1328-hammer-of-throwing', 'main_hand', held('1328-hammer-of-throwing', [11, 20])),
  1331: art('1331-habus-fearsome-club', 'main_hand', held('1331-habus-fearsome-club', [11, 20])),
  1334: art('1334-metronomic-hammer', 'main_hand', held('1334-metronomic-hammer', [11, 20])),
  1339: art('1339-wolf-rattle-club', 'main_hand', held('1339-wolf-rattle-club', [11, 20])),
  1341: art('1341-fen-lantern-flail', 'main_hand', held('1341-fen-lantern-flail', [11, 20])),
  1345: art('1345-catoblepas-maul', 'main_hand', held('1345-catoblepas-maul', [11, 20])),
  1352: art('1352-belfry-hammer-of-saint-orne', 'main_hand', held('1352-belfry-hammer-of-saint-orne', [11, 20])),
  1354: art('1354-hydra-head-club', 'main_hand', held('1354-hydra-head-club', [11, 20])),
  1358: art('1358-draddeths-shifting-hammer', 'main_hand', held('1358-draddeths-shifting-hammer', [11, 20])),
  1360: art('1360-kingsfall-maul', 'main_hand', held('1360-kingsfall-maul', [11, 20])),
  1365: art('1365-pedlars-counterweight', 'main_hand', held('1365-pedlars-counterweight', [11, 20])),
  1369: art('1369-mace-of-the-sundered-crown', 'main_hand', held('1369-mace-of-the-sundered-crown', [11, 20])),
  1371: art('1371-boar-hunters-spear', 'main_hand', held('1371-boar-hunters-spear', [11, 20])),
  1375: art('1375-reedcutters-glaive', 'main_hand', held('1375-reedcutters-glaive', [11, 20])),
  1378: art('1378-mooring-pole', 'main_hand', held('1378-mooring-pole', [11, 20])),
  1383: art('1383-fowlers-bird-spear', 'main_hand', held('1383-fowlers-bird-spear', [11, 20])),
  1386: art('1386-trident-of-fish-command', 'main_hand', held('1386-trident-of-fish-command', [11, 20])),
  1390: art('1390-fighters-fork', 'main_hand', held('1390-fighters-fork', [11, 20])),
  1393: art('1393-cavalry-commanders-lance', 'main_hand', held('1393-cavalry-commanders-lance', [11, 20])),
  1396: art('1396-phase-spike', 'main_hand', held('1396-phase-spike', [11, 20])),
  1404: art('1404-blooddrinker-spear', 'main_hand', held('1404-blooddrinker-spear', [11, 20])),
  1406: art('1406-gnawing-spear', 'main_hand', held('1406-gnawing-spear', [11, 20])),
  1409: art('1409-dragons-tongue-longspear', 'main_hand', held('1409-dragons-tongue-longspear', [11, 20])),
  1412: art('1412-anchor-spear', 'main_hand', held('1412-anchor-spear', [11, 20])),
  1417: art('1417-golden-spear-of-mzali', 'main_hand', held('1417-golden-spear-of-mzali', [11, 20])),
  1420: art('1420-bog-probe-spear', 'main_hand', held('1420-bog-probe-spear', [11, 20])),
  1425: art('1425-kelp-wrapped-harpoon', 'main_hand', held('1425-kelp-wrapped-harpoon', [11, 20])),
  1428: art('1428-fog-watch-spear', 'main_hand', held('1428-fog-watch-spear', [11, 20])),
  1433: art('1433-poachers-short-spear', 'main_hand', held('1433-poachers-short-spear', [11, 20])),
  1435: art('1435-rat-catcher-trident', 'main_hand', held('1435-rat-catcher-trident', [11, 20])),
  1439: art('1439-gravediggers-call', 'main_hand', held('1439-gravediggers-call', [11, 20])),
  1441: art('1441-acrobats-long-pole', 'main_hand', held('1441-acrobats-long-pole', [11, 20])),
  1448: art('1448-owl-feather-spear', 'main_hand', held('1448-owl-feather-spear', [11, 20])),
  1450: art('1450-peddlers-folding-spear', 'main_hand', held('1450-peddlers-folding-spear', [11, 20])),
  1454: art('1454-spear-of-the-western-whale', 'main_hand', held('1454-spear-of-the-western-whale', [11, 20])),
  1456: art('1456-alicorn-lance', 'main_hand', held('1456-alicorn-lance', [11, 20])),
  1461: art('1461-hangmans-hooked-bill', 'main_hand', held('1461-hangmans-hooked-bill', [11, 20])),
  1466: art('1466-fey-road-wardens-glaive', 'main_hand', held('1466-fey-road-wardens-glaive', [11, 20])),
  1469: art('1469-wyrmbone-spear-of-harrowmark', 'main_hand', held('1469-wyrmbone-spear-of-harrowmark', [11, 20])),
  1473: art('1473-hill-walkers-knobstick', 'main_hand', held('1473-hill-walkers-knobstick', [11, 20])),
  1476: art('1476-bo-of-the-mountain-abbey', 'main_hand', held('1476-bo-of-the-mountain-abbey', [11, 20])),
  1480: art('1480-scrollstaff-of-the-magaambya', 'main_hand', held('1480-scrollstaff-of-the-magaambya', [11, 20])),
  1484: art('1484-staff-of-the-woodlands', 'main_hand', held('1484-staff-of-the-woodlands', [11, 20])),
  1487: art('1487-staff-of-thunder-and-lightning', 'main_hand', held('1487-staff-of-thunder-and-lightning', [11, 20])),
  1492: art('1492-peat-bog-slane', 'main_hand', held('1492-peat-bog-slane', [11, 20])),
  1497: art('1497-flamecrest-halberd', 'main_hand', held('1497-flamecrest-halberd', [11, 20])),
  1503: art('1503-bramblewick-shortbow', 'main_hand', held('1503-bramblewick-shortbow', [11, 20])),
  1505: art('1505-moth-lamp-bow', 'main_hand', held('1505-moth-lamp-bow', [11, 20])),
  1509: art('1509-ramshorn-composite', 'main_hand', held('1509-ramshorn-composite', [11, 20])),
  1513: art('1513-toll-bridge-wardens-bow', 'main_hand', held('1513-toll-bridge-wardens-bow', [11, 20])),
  1517: art('1517-plumbline-bow', 'main_hand', held('1517-plumbline-bow', [11, 20])),
  1521: art('1521-forkhead-bow', 'main_hand', held('1521-forkhead-bow', [11, 20])),
  1525: art('1525-wisp-strung-bow', 'main_hand', held('1525-wisp-strung-bow', [11, 20])),
  1528: art('1528-pilgrims-ashwood-bow', 'main_hand', held('1528-pilgrims-ashwood-bow', [11, 20])),
  1531: art('1531-lark-ascending-bow', 'main_hand', held('1531-lark-ascending-bow', [11, 20])),
  1534: art('1534-rimefall-longbow', 'main_hand', held('1534-rimefall-longbow', [11, 20])),
  1540: art('1540-saint-aldrics-breath', 'main_hand', held('1540-saint-aldrics-breath', [11, 20])),
  1544: art('1544-fordwater-ferrywomans-bow', 'main_hand', held('1544-fordwater-ferrywomans-bow', [11, 20])),
  1546: art('1546-brook-pebble-sling', 'main_hand', held('1546-brook-pebble-sling', [11, 20])),
  1550: art('1550-hobs-dell-giant-sling', 'main_hand', held('1550-hobs-dell-giant-sling', [11, 20])),
  1553: art('1553-millers-lead-shot-sling', 'main_hand', held('1553-millers-lead-shot-sling', [11, 20])),
  1558: art('1558-wayfarers-starknife', 'main_hand', held('1558-wayfarers-starknife', [11, 20])),
  1560: art('1560-lamplighters-sleeve-darts', 'main_hand', held('1560-lamplighters-sleeve-darts', [11, 20])),
  1565: art('1565-woodcutters-hurlaxe', 'main_hand', held('1565-woodcutters-hurlaxe', [11, 20], Z.main_hand)),
  1569: art('1569-sodheaver-javelin', 'main_hand', held('1569-sodheaver-javelin', [11, 20])),
  1573: art('1573-coopers-hoop-crossbow', 'main_hand', held('1573-coopers-hoop-crossbow', [11, 20])),
  1575: art('1575-tollhouse-stirrup-bow', 'main_hand', held('1575-tollhouse-stirrup-bow', [11, 20])),
  1578: art('1578-mineshaft-crossbow', 'main_hand', held('1578-mineshaft-crossbow', [11, 20])),
  1583: art('1583-hogsbristle-crossbow', 'main_hand', held('1583-hogsbristle-crossbow', [11, 20])),
  1585: art('1585-mountaineers-crossbow', 'main_hand', held('1585-mountaineers-crossbow', [11, 20])),
  1589: art('1589-angry-hornet', 'main_hand', held('1589-angry-hornet', [11, 20])),
  1594: art('1594-lockbreaker-crossbow', 'main_hand', held('1594-lockbreaker-crossbow', [11, 20])),
  1597: art('1597-shieldwall-crossbow', 'main_hand', held('1597-shieldwall-crossbow', [11, 20])),
  1599: art('1599-jistkan-war-crossbow', 'main_hand', held('1599-jistkan-war-crossbow', [11, 20])),
  1604: art('1604-nightjar-hand-crossbow', 'main_hand', held('1604-nightjar-hand-crossbow', [11, 20])),
  1607: art('1607-dragonbone-arbalest-of-drak-hold', 'main_hand', held('1607-dragonbone-arbalest-of-drak-hold', [11, 20])),
  1611: art('1611-oak-heart-wand', 'main_hand', held('1611-oak-heart-wand', [11, 20])),
  1614: art('1614-hedge-scryers-twig', 'main_hand', held('1614-hedge-scryers-twig', [11, 20])),
  1618: art('1618-wayside-shrine-wand', 'main_hand', held('1618-wayside-shrine-wand', [11, 20])),
  1624: art('1624-soapsud-wand', 'main_hand', held('1624-soapsud-wand', [11, 20])),
  1626: art('1626-rimeclinger-wand', 'main_hand', held('1626-rimeclinger-wand', [11, 20])),
  1630: art('1630-shrouded-step-wand', 'main_hand', held('1630-shrouded-step-wand', [11, 20])),
  1633: art('1633-bogwater-acid-wand', 'main_hand', held('1633-bogwater-acid-wand', [11, 20])),
  1638: art('1638-adders-tongue-wand', 'main_hand', held('1638-adders-tongue-wand', [11, 20])),
  1640: art('1640-weary-waltz-wand', 'main_hand', held('1640-weary-waltz-wand', [11, 20])),
  1645: art('1645-toadwitchs-wand', 'main_hand', held('1645-toadwitchs-wand', [11, 20])),
  1650: art('1650-ghoulbane-rod', 'main_hand', held('1650-ghoulbane-rod', [11, 20])),
  1653: art('1653-rod-of-sanguine-tithe', 'main_hand', held('1653-rod-of-sanguine-tithe', [11, 20])),
  1656: art('1656-rod-of-warning-bells', 'main_hand', held('1656-rod-of-warning-bells', [11, 20])),
  1659: art('1659-rod-of-the-drinking-star', 'main_hand', held('1659-rod-of-the-drinking-star', [11, 20])),
  1665: art('1665-saint-wends-lantern-wand', 'main_hand', held('1665-saint-wends-lantern-wand', [11, 20])),
  1669: art('1669-rod-of-the-cradle-moon', 'main_hand', held('1669-rod-of-the-cradle-moon', [11, 20])),
  1673: art('1673-rowan-waystaff', 'main_hand', held('1673-rowan-waystaff', [11, 20])),
  1676: art('1676-hillfort-cudgel-staff', 'main_hand', held('1676-hillfort-cudgel-staff', [11, 20])),
  1678: art('1678-fen-pole-of-the-ferryman', 'main_hand', held('1678-fen-pole-of-the-ferryman', [11, 20])),
  1682: art('1682-staff-of-rimewood', 'main_hand', held('1682-staff-of-rimewood', [11, 20])),
  1685: art('1685-swarming-insect-staff', 'main_hand', held('1685-swarming-insect-staff', [11, 20])),
  1691: art('1691-featherfall-staff', 'main_hand', held('1691-featherfall-staff', [11, 20])),
  1695: art('1695-earthmothers-rootstaff', 'main_hand', held('1695-earthmothers-rootstaff', [11, 20])),
  1697: art('1697-stormherds-thunderstaff', 'main_hand', held('1697-stormherds-thunderstaff', [11, 20])),
  1701: art('1701-unseelie-winter-staff', 'main_hand', held('1701-unseelie-winter-staff', [11, 20])),
  1705: art('1705-staff-of-the-verdant-heart', 'main_hand', held('1705-staff-of-the-verdant-heart', [11, 20])),
  1709: art('1709-treant-fathers-bough', 'main_hand', held('1709-treant-fathers-bough', [11, 20])),
  1712: art('1712-dragonprism-staff-of-drak-hold', 'main_hand', held('1712-dragonprism-staff-of-drak-hold', [11, 20])),
  1716: art('1716-pilgrims-tin-sunburst', 'off_hand'),
  1720: art('1720-grandmother-ashs-bone-dice', 'off_hand'),
  1723: art('1723-rowan-twig-fetish', 'off_hand'),
  1727: art('1727-beekeepers-smoke-censer', 'off_hand'),
  1730: art('1730-psalter-of-saint-merrow', 'off_hand'),
  1732: art('1732-steadyheart-lamp-of-brindle', 'off_hand'),
  1737: art('1737-giggling-orb-of-fiddlers-green', 'off_hand'),
  1741: art('1741-harvest-day-sunflower-censer', 'off_hand'),
  1744: art('1744-reliquary-of-saint-wends-finger', 'off_hand'),
  1748: art('1748-orb-of-the-drowned-kings', 'off_hand'),
  1751: art('1751-novices-grey-habit', 'torso'),
  1756: art('1756-mantle-of-the-reed-choir', 'torso'),
  1759: art('1759-robe-of-a-hundred-eyes', 'torso'),
  1764: art('1764-fenwitchs-mossgown', 'torso'),
  1769: art('1769-shepherds-fleece-smock', 'torso'),
  1771: art('1771-smugglers-lined-greatcoat', 'torso'),
  1775: art('1775-penitents-hairshirt', 'torso'),
  1777: art('1777-vestments-of-the-drowned-bishop', 'torso'),
  1783: art('1783-glamoured-brigands-leathers', 'torso'),
  1788: art('1788-ankhrav-carapace-jack', 'torso'),
  1792: art('1792-cutpurses-soft-leathers', 'torso'),
  1794: art('1794-webwalker-harness', 'torso'),
  1799: art('1799-huntsmans-buckskin', 'torso'),
  1802: art('1802-coat-of-padarn-the-unbent', 'torso'),
  1806: art('1806-watchmans-rusty-hauberk', 'torso'),
  1811: art('1811-elven-chain-of-thistlewood', 'torso'),
  1814: art('1814-larksong-hauberk', 'torso'),
  1818: art('1818-bandagers-hauberk', 'torso'),
  1821: art('1821-brother-rooks-living-mail', 'torso'),
  1825: art('1825-fountmail-of-the-silver-spring', 'torso'),
  1828: art('1828-cowards-chain-shirt', 'torso'),
  1834: art('1834-tourney-plate-of-sir-dunstan', 'torso'),
  1837: art('1837-ploughmens-muster-splint', 'torso'),
  1842: art('1842-steadfast-splint-of-ostrava', 'torso'),
  1847: art('1847-plate-of-the-thinning-veil', 'torso'),
  1849: art('1849-starfall-plate-of-brannoch', 'torso'),
  1854: art('1854-dragonplate-of-the-ember-throne', 'torso'),
  1858: art('1858-glorious-plate-of-queen-imra', 'torso'),
  1861: art('1861-cold-iron-buckler-of-kettlecross', 'off_hand'),
  1865: art('1865-peacock-buckler-of-lady-wren', 'off_hand'),
  1868: art('1868-hawthorn-grown-buckler', 'off_hand'),
  1873: art('1873-orichalcum-buckler-of-tock', 'off_hand'),
  1876: art('1876-pilgrim-knights-roundel', 'off_hand'),
  1880: art('1880-arrowsnare-round-of-fennick', 'off_hand'),
  1882: art('1882-lion-mouth-roundshield', 'off_hand'),
  1887: art('1887-spellguard-of-the-reed-abbey', 'off_hand'),
  1892: art('1892-sunwheel-of-king-aldmar', 'off_hand'),
  1896: art('1896-lizard-scale-kite-of-the-fens', 'off_hand'),
  1898: art('1898-pilgrim-road-kite', 'off_hand'),
  1903: art('1903-tiger-stripe-kite', 'off_hand'),
  1907: art('1907-mirror-kite-of-the-hexfinder', 'off_hand'),
  1910: art('1910-forge-warden-of-kharn-dabur', 'off_hand'),
  1915: art('1915-pavise-of-the-bowmens-guild', 'off_hand'),
  1918: art('1918-barrow-lid-tower-shield', 'off_hand'),
  1922: art('1922-bolthole-tower-of-the-tinkers', 'off_hand'),
  1924: art('1924-cairnstone-tower-shield', 'off_hand'),
  1929: art('1929-zephyr-tower-of-the-high-pass', 'off_hand'),
  1933: art('1933-poachers-felt-hood', 'helmet'),
  1935: art('1935-apprentices-ink-blot-hat', 'helmet'),
  1940: art('1940-hat-of-a-thousand-strangers', 'helmet'),
  1944: art('1944-abbot-hesks-skullcap', 'helmet'),
  1947: art('1947-doctor-ravels-beak-mask', 'helmet'),
  1953: art('1953-pellows-thinking-cap', 'helmet'),
  1956: art('1956-lamplit-circlet-of-saint-quill', 'helmet'),
  1958: art('1958-lupine-crown-of-the-greyhowl', 'helmet'),
  1963: art('1963-greenwood-crown-of-the-fey-road', 'helmet'),
  1968: art('1968-bandit-kettle-of-the-kingsroad', 'helmet'),
  1972: art('1972-finned-helm-of-the-saltmarsh', 'helmet'),
  1976: art('1976-rhino-snout-kettle', 'helmet'),
  1978: art('1978-tidewater-kettle-of-fordwater', 'helmet'),
  1983: art('1983-tourney-helm-of-sir-dunstan', 'helmet'),
  1987: art('1987-zealot-helm-of-the-pyre', 'helmet'),
  1991: art('1991-great-helm-of-the-gatehouse', 'helmet'),
  1995: art('1995-hrolfs-iron-fury-mask', 'helmet'),
  1999: art('1999-helm-of-saint-wend-the-watcher', 'helmet'),
  2001: art('2001-drovers-greycoat', 'cape'),
  2005: art('2005-lamplighters-short-cape', 'cape'),
  2008: art('2008-gravediggers-rain-cloak', 'cape'),
  2014: art('2014-scarecrows-patchwork-cape', 'cape'),
  2017: art('2017-briar-stitched-mantle', 'cape'),
  2022: art('2022-alchemists-scorched-mantle', 'cape'),
  2025: art('2025-sextons-lychgate-cloak', 'cape'),
  2029: art('2029-watch-fire-sentry-cloak', 'cape'),
  2034: art('2034-stewards-livery-mantle', 'cape'),
  2038: art('2038-cinderfall-mantle', 'cape'),
  2041: art('2041-blood-oath-mantle', 'cape'),
  2045: art('2045-purple-steelsilk-mantle', 'cape'),
  2047: art('2047-mantle-of-the-moonless-hour', 'cape'),
  2053: art('2053-wyrmhide-ash-cloak', 'cape'),
  2056: art('2056-blacksmiths-scorch-gloves', 'hands', [part('2056-blacksmiths-scorch-gloves-back', 'hand_back'), part('2056-blacksmiths-scorch-gloves-front', 'hand_front')]),
  2060: art('2060-fletchers-bracer-of-ash', 'hands', [part('2060-fletchers-bracer-of-ash-back', 'hand_back'), part('2060-fletchers-bracer-of-ash-front', 'hand_front')]),
  2063: art('2063-iron-knuckle-mittens', 'hands', [part('2063-iron-knuckle-mittens-back', 'hand_back'), part('2063-iron-knuckle-mittens-front', 'hand_front')]),
  2069: art('2069-ploughmans-callused-wraps', 'hands', [part('2069-ploughmans-callused-wraps-back', 'hand_back'), part('2069-ploughmans-callused-wraps-front', 'hand_front')]),
  2072: art('2072-dawnsilver-deflecting-bracers', 'hands', [part('2072-dawnsilver-deflecting-bracers-back', 'hand_back'), part('2072-dawnsilver-deflecting-bracers-front', 'hand_front')]),
  2077: art('2077-inkwell-octopus-bracers', 'hands', [part('2077-inkwell-octopus-bracers-back', 'hand_back'), part('2077-inkwell-octopus-bracers-front', 'hand_front')]),
  2080: art('2080-card-sharps-silk-gloves', 'hands', [part('2080-card-sharps-silk-gloves-back', 'hand_back'), part('2080-card-sharps-silk-gloves-front', 'hand_front')]),
  2083: art('2083-bloodletters-gauntlets', 'hands', [part('2083-bloodletters-gauntlets-back', 'hand_back'), part('2083-bloodletters-gauntlets-front', 'hand_front')]),
  2087: art('2087-crimson-throwers-glove', 'hands', [part('2087-crimson-throwers-glove-back', 'hand_back'), part('2087-crimson-throwers-glove-front', 'hand_front')]),
  2091: art('2091-hedge-witchs-subtle-gloves', 'hands', [part('2091-hedge-witchs-subtle-gloves-back', 'hand_back'), part('2091-hedge-witchs-subtle-gloves-front', 'hand_front')]),
  2095: art('2095-fulminar-stormbracers', 'hands', [part('2095-fulminar-stormbracers-back', 'hand_back'), part('2095-fulminar-stormbracers-front', 'hand_front')]),
  2100: art('2100-dwarf-kings-hammer-bracers', 'hands', [part('2100-dwarf-kings-hammer-bracers-back', 'hand_back'), part('2100-dwarf-kings-hammer-bracers-front', 'hand_front')]),
  2102: art('2102-gauntlet-of-the-witch-queen', 'hands', [part('2102-gauntlet-of-the-witch-queen-back', 'hand_back'), part('2102-gauntlet-of-the-witch-queen-front', 'hand_front')]),
  2109: art('2109-long-trek-snowshoes', 'feet'),
  2111: art('2111-dead-mans-boots', 'feet'),
  2115: art('2115-poachers-soft-toe-boots', 'feet'),
  2119: art('2119-tumblers-toe-shoes', 'feet'),
  2126: art('2126-rootgrip-boots', 'feet'),
  2128: art('2128-rooftop-free-runners', 'feet'),
  2132: art('2132-grandmothers-creak-free-boots', 'feet'),
  2137: art('2137-hare-heel-boots-of-haste', 'feet'),
  2141: art('2141-quakefoot-stompers', 'feet'),
  2146: art('2146-mire-rot-boots', 'feet'),
  2148: art('2148-ogres-seven-league-boots', 'feet'),
  2153: art('2153-iron-boots-of-the-greyfell-hold', 'feet'),
  2159: art('2159-kettle-iron-shin-guards', 'legs'),
  2161: art('2161-woodcutters-hobble-kilt', 'legs'),
  2166: art('2166-milkmaids-pleated-skirt', 'legs'),
  2168: art('2168-eel-skin-mudlark-breeches', 'legs'),
  2173: art('2173-troll-hunters-studded-leggings', 'legs'),
  2177: art('2177-gatewardens-mail-chausses', 'legs'),
  2181: art('2181-glimmerknit-leggings', 'legs'),
  2183: art('2183-tumbleweed-desert-trousers', 'legs'),
  2187: art('2187-scorchmarsh-lava-leggings', 'legs'),
  2193: art('2193-thieves-guild-velvet-breeches', 'legs'),
  2198: art('2198-greaves-of-saint-aldric', 'legs'),
  2202: art('2202-sunday-best-silver-buckle', 'waist'),
  2206: art('2206-coopers-hoop-iron-belt', 'waist'),
  2209: art('2209-mole-catchers-pouch-belt', 'waist'),
  2215: art('2215-sergeant-hobbs-brave-baldric', 'waist'),
  2219: art('2219-wayfletchers-hip-quiver', 'waist'),
  2221: art('2221-antler-buckle-wildwood-belt', 'waist'),
  2224: art('2224-porters-lifting-belt', 'waist'),
  2229: art('2229-ironhold-smiths-belt', 'waist'),
  2231: art('2231-toad-skin-witch-girdle', 'waist'),
  2236: art('2236-opera-house-cincture', 'waist'),
  2241: art('2241-saltwind-sailors-sash', 'waist'),
  2244: art('2244-girdle-of-the-hill-giant-jarl', 'waist'),
  2248: art('2248-smuggler-kings-bandolier', 'waist'),
  2251: art('2251-mirewalkers-reed-band'),
  2256: art('2256-gravediggers-knuckle'),
  2261: art('2261-tavern-brawlers-band'),
  2264: art('2264-cobblers-iron-ring'),
  2268: art('2268-ring-of-the-quiet-mind'),
  2272: art('2272-ring-of-the-patient-heron'),
  2275: art('2275-ring-of-the-rams-brow'),
  2279: art('2279-mossback-healers-ring'),
  2285: art('2285-spellkeepers-ring-of-five'),
  2287: art('2287-ring-of-sundown-stars'),
  2291: art('2291-ring-of-the-grove-warden'),
  2293: art('2293-ring-of-the-water-dancer'),
  2298: art('2298-the-sealing-ring-of-cael-morrow'),
  2303: art('2303-ring-of-ratsbane-silver'),
  2305: art('2305-moneylenders-weighted-ring'),
  2309: art('2309-chandlers-hot-ring'),
  2311: art('2311-ring-of-the-hedge-herbalist'),
  2316: art('2316-ring-of-lights-comfort'),
  2319: art('2319-ring-of-featherweight-steel'),
  2324: art('2324-stalwarts-plain-band'),
  2326: art('2326-ring-of-climbing-claws'),
  2331: art('2331-ring-of-the-sawbones'),
  2334: art('2334-ring-of-battered-courage'),
  2337: art('2337-assassins-hollow-ring'),
  2339: art('2339-ring-of-torags-anvil'),
  2345: art('2345-wolfkings-bloodstone'),
  2349: art('2349-ring-of-the-krakens-grasp'),
  2353: art('2353-broken-fang-talisman'),
  2356: art('2356-hunters-rabbit-bone-charm'),
  2360: art('2360-constables-pewter-star'),
  2363: art('2363-brindle-hound-badge'),
  2368: art('2368-little-brass-bell-badge'),
  2372: art('2372-ioun-stone-of-awareness'),
  2375: art('2375-diplomats-brass-badge'),
  2380: art('2380-water-charm-of-the-wellwife'),
  2385: art('2385-mourning-medallion-of-ivy-lane'),
  2387: art('2387-gorilla-might-medal'),
  2391: art('2391-gossips-earring'),
  2395: art('2395-passage-charm-of-mirkwood-lane'),
  2398: art('2398-ioun-stone-of-mastery'),
  2401: art('2401-unicorn-purity-medal'),
  2405: art('2405-doubling-coin-of-twelvetrees'),
  2410: art('2410-sapphire-tear-of-the-glass-queen'),
  2413: art('2413-rebirth-scarab-of-khemet'),
  2415: art('2415-talisman-of-the-pure-hearth'),
  2424: art('2424-woad-blue-amulet'),
  2428: art('2428-bone-tooth-necklace'),
  2432: art('2432-millers-flour-sack-scarf'),
  2436: art('2436-wayfarers-sun-disc'),
  2439: art('2439-hedge-wizards-hollow-pendant'),
  2443: art('2443-saint-wends-pewter-medal'),
  2446: art('2446-amulet-of-hearty-health'),
  2450: art('2450-amulet-of-memory'),
  2455: art('2455-pendant-of-the-occult-eye'),
  2459: art('2459-locket-of-love-left-behind'),
  2462: art('2462-corpse-hunters-medallion'),
  2465: art('2465-collar-of-inconspicuousness'),
  2468: art('2468-goblin-ear-necklace'),
  2472: art('2472-amulet-of-the-iron-constitution'),
  2477: art('2477-locket-of-dragon-vitality'),
  2479: art('2479-torc-of-the-comet'),
  2483: art('2483-raven-feather-mantle-band'),
  2485: art('2485-mantle-scarf-of-the-ember-monk'),
  2491: art('2491-amulet-of-the-giants-health'),
  2495: art('2495-storm-oyster-pearl-torc'),
  2497: art('2497-mourning-torc-of-queen-ysolde'),
  2502: art('2502-bogwater-restorative'),
  2507: art('2507-haverbrook-soothing-tonic'),
  2510: art('2510-saint-wends-rosewater'),
  2514: art('2514-bramblewifes-hip-flask'),
  2518: art('2518-draught-of-the-hollow-hills'),
  2522: art('2522-twin-vial-of-kinsblood'),
  2526: art('2526-lowmere-quiet-mind-tonic'),
  2529: art('2529-archmage-pells-brain-juice'),
  2535: art('2535-bottled-second-breath'),
  2538: art('2538-whitewash-ward-draught'),
  2542: art('2542-tower-watch-tonic'),
  2544: art('2544-time-shield-draught'),
  2548: art('2548-elysian-dew'),
  2552: art('2552-frost-giants-firkin'),
  2556: art('2556-heroes-rally-draught'),
  2560: art('2560-war-blood-mutagen'),
  2562: art('2562-caydens-golden-brew'),
  2567: art('2567-liquid-shadow'),
  2570: art('2570-moth-wing-cordial'),
  2573: art('2573-antiplague-posset'),
  2577: art('2577-bottled-catharsis'),
  2580: art('2580-contagion-eater-draught'),
  2582: art('2582-salve-of-antiparalysis'),
  2587: art('2587-quicksilver-mutagen'),
  2590: art('2590-expeditious-retreat-flask'),
  2592: art('2592-sure-step-potion'),
  2596: art('2596-ghostwalk-elixir'),
  2602: art('2602-skyborn-flying-elixir'),
  2604: art('2604-bouncing-brew'),
  2608: art('2608-winter-wolf-breath-draught'),
  2610: art('2610-stormcallers-spark-spit'),
  2615: art('2615-frogskin-tincture'),
  2620: art('2620-truesight-draught'),
  2622: art('2622-potion-of-living-light'),
  2625: art('2625-phoenix-wing-draught'),
  2628: art('2628-witch-light-rouse'),
  2633: art('2633-ghoulbane-oil'),
  2635: art('2635-black-dragon-oil'),
  2640: art('2640-spider-silk-snare-oil'),
  2644: art('2644-bone-chill-frost-oil'),
  2647: art('2647-slipperiness-oil'),
  2650: art('2650-statue-skin-salve'),
  2653: art('2653-anointing-oil-of-lastwall'),
  2656: art('2656-revelation-oil'),
  2662: art('2662-silver-keen-edge-salve'),
  2664: art('2664-whisper-steel-oil'),
  2668: art('2668-hardening-polish'),
  2670: art('2670-anticorrosion-oil'),
  2675: art('2675-spider-queens-venom'),
  2679: art('2679-efficacious-eyewash'),
  2682: art('2682-obfuscation-oil'),
  2685: art('2685-locksmiths-oil'),
  2691: art('2691-royal-jelly-cordial'),
  2693: art('2693-comprehension-elixir'),
  2698: art('2698-bloodspeed-elixir'),
  2701: art('2701-philter-of-empty-dreams'),
  2706: art('2706-darkvision-elixir'),
  2708: art('2708-delvers-lamp-black-draught'),
  2712: art('2712-elixir-of-saint-brannoc'),
  2714: art('2714-abbey-ward-milk'),
  2720: art('2720-clairvoyants-crystal-draught'),
  2724: art('2724-philter-of-luck'),
  2726: art('2726-bitterblood-elixir'),
  2730: art('2730-shared-memory-draught'),
  2732: art('2732-bottled-omen'),
  2737: art('2737-silvertongue-mutagen'),
  2740: art('2740-fizzy-lifter'),
  2744: art('2744-blood-booster'),
  2746: art('2746-sun-barley-wake-up'),
  2753: art('2753-scroll-of-the-kindled-spark'),
  2756: art('2756-ward-ink-sigil'),
  2761: art('2761-kitchen-witchs-grease-scroll'),
  2763: art('2763-bell-ringers-last-toll'),
  2768: art('2768-bramblesnare-scroll'),
  2771: art('2771-ashcairn-sleeping-verse'),
  2775: art('2775-gravediggers-rousing-writ'),
  2780: art('2780-scroll-of-hold-fast'),
  2783: art('2783-scroll-of-the-greater-mend'),
  2789: art('2789-wayfarers-folded-door'),
  2791: art('2791-scroll-of-iron-skin'),
  2795: art('2795-wolfstep-scroll'),
  2799: art('2799-scroll-of-the-sunlit-lance'),
  2803: art('2803-hearthkeepers-summons'),
  2807: art('2807-scroll-of-sparks-between-fingers'),
  2809: art('2809-firecracker-prayer-strip'),
  2813: art('2813-wolf-fang-talisman'),
  2819: art('2819-impelling-medallion'),
  2821: art('2821-dancing-ember-sheet'),
  2823: art('2823-spare-stitching-scroll'),
  2827: art('2827-blink-fox-scroll'),
  2832: art('2832-smiths-cursing-scroll'),
  2836: art('2836-shrieking-skull-talisman'),
  2839: art('2839-captain-harrows-muster-roll'),
  2842: art('2842-rimewold-gale-scroll'),
  2845: art('2845-veil-of-the-grey-lady'),
  2849: art('2849-midge-swarm-vellum'),
  2854: art('2854-seven-forks-storm-scroll'),
  2856: art('2856-vigil-of-saint-wend'),
  2860: art('2860-dwarven-undermining-writ'),
  2864: art('2864-redpitch-tar-bomb'),
  2869: art('2869-harrowmark-pumpkin-bomb'),
  2873: art('2873-bog-witchs-acid-flask'),
  2876: art('2876-blightburn-bomb'),
  2881: art('2881-bottled-lightning'),
  2884: art('2884-jar-of-the-seventh-storm'),
  2888: art('2888-boom-snare-kit'),
  2891: art('2891-bottled-sunlight'),
  2897: art('2897-goo-grenade'),
  2901: art('2901-poachers-smoke-ball'),
  2903: art('2903-fogbank-pot'),
  2905: art('2905-ink-squid-bomb'),
  2910: art('2910-greek-fire-amphora'),
  2915: art('2915-chimney-sweeps-soot-bomb'),
  2918: art('2918-hearth-spark-tinderbag'),
  2921: art('2921-black-dragon-bile'),
  2925: art('2925-fen-hags-melting-jar'),
  2928: art('2928-rimeback-caltrops'),
  2931: art('2931-heart-of-the-long-winter'),
  2936: art('2936-skyforge-lightning-jar'),
  2940: art('2940-grave-dirt-bomb'),
  2943: art('2943-pilgrims-ash-vial'),
  2947: art('2947-caltrop-pouch'),
  2949: art('2949-gnome-spring-snare'),
  2955: art('2955-kraken-ink-net'),
  2959: art('2959-ash-fen-smoke-pot'),
  2961: art('2961-pixie-dust-twist'),
  2963: art('2963-mothwing-dust'),
  2968: art('2968-bottled-midnight'),
  2972: art('2972-clockwork-bee-bomb'),
  2976: art('2976-bottled-sanctuary-mist'),
  2979: art('2979-goblin-popper'),
  2983: art('2983-molehill-charge'),
  2986: art('2986-firefly-lantern-jar'),
  2989: art('2989-wasp-paper-smoke-ball'),
  2992: art('2992-ruby-ember-shot'),
  2999: art('2999-bottled-moonfire'),
  3001: art('3001-mirewood-tinderbox'),
  3005: art('3005-fenmarket-chalk-lump'),
  3010: art('3010-weatherworn-waymap'),
  3013: art('3013-brass-signal-mirror'),
  3017: art('3017-ox-horn-hunting-call'),
  3020: art('3020-sooty-tinkers-kit'),
  3024: art('3024-saint-hesks-pocket-sundial'),
  3029: art('3029-waxed-map-case'),
  3034: art('3034-bagpipes-of-the-bannock-glen'),
  3036: art('3036-temple-clamour-bell'),
  3040: art('3040-sister-amsels-physic-roll'),
  3042: art('3042-alchemical-fen-lantern'),
  3048: art('3048-ever-shifting-map'),
  3052: art('3052-bonesetters-splint-kit'),
  3054: art('3054-string-of-alarm-bells'),
  3056: art('3056-salt-marsh-astrolabe'),
  3062: art('3062-spirit-lantern-of-hollowmere'),
  3066: art('3066-chronomancers-pocket-clock'),
  3068: art('3068-brannock-horn-of-plenty'),
  3072: art('3072-tinkerwifes-cajon-drum'),
  3076: art('3076-saint-ilsas-star-chart'),
  3080: art('3080-horn-of-the-barrow-kings'),
  3084: art('3084-chime-of-the-thousand-doors'),
  3086: art('3086-sky-whale-spyglass'),
  3091: art('3091-journeybread-of-the-fen-road'),
  3096: art('3096-drowned-duck-pucker-pickle'),
  3101: art('3101-firefoot-popcorn'),
  3103: art('3103-ferrymans-hot-toddy'),
  3108: art('3108-turnip-and-barley-pottage'),
  3111: art('3111-hedgerow-sloe-gin'),
  3114: art('3114-saint-wends-waybread'),
  3121: art('3121-pepper-string-chilli'),
  3124: art('3124-sprite-orchard-apple'),
  3128: art('3128-kettle-hold-iron-wine'),
  3130: art('3130-diplomats-charcuterie'),
  3135: art('3135-owl-screech-egg'),
  3137: art('3137-cinnamon-seers'),
  3142: art('3142-rosehip-cordial'),
  3147: art('3147-woodcutters-breakfast-box'),
  3149: art('3149-covenant-tea'),
  3153: art('3153-dragon-pearl'),
  3157: art('3157-bountiful-cauldron-feast'),
  3162: art('3162-grindlegrub-steak'),
  3164: art('3164-witch-queens-candied-apple'),
  3168: art('3168-dwarf-kings-coronation-ale'),
  3171: art('3171-tome-of-the-endless-tale'),
  3176: art('3176-goblin-raid-tally-sheet'),
  3180: art('3180-ledger-of-the-toll-keeper'),
  3183: art('3183-sappers-notes-on-mud'),
  3188: art('3188-penny-dreadful-hollow-knight'),
  3191: art('3191-bestiary-page-fen-trolls'),
  3194: art('3194-treatise-on-clockwork-locks'),
  3199: art('3199-tome-of-the-spellblade'),
  3203: art('3203-hunters-hagbook'),
  3206: art('3206-linguists-dictionary'),
  3210: art('3210-scouting-report-crowsfoot'),
  3214: art('3214-wayfinders-chronicle'),
  3217: art('3217-codebreakers-parchment'),
  3222: art('3222-poisoners-almanac'),
  3226: art('3226-radiant-tome'),
  3229: art('3229-whispering-spirit-tome'),
  3232: art('3232-storytellers-opus'),
  3237: art('3237-atlas-arcane'),
  3242: art('3242-manual-of-guardians'),
  3244: art('3244-the-shieldwall-codex'),
  3246: art('3246-book-of-eibon'),
  3251: art('3251-pocketful-of-bog-pearls'),
  3256: art('3256-sister-alders-silver-ewer'),
  3261: art('3261-hold-silver-bars-of-karak-dun'),
  3263: art('3263-lake-orm-sapphire-tear'),
  3269: art('3269-fen-chapel-peridot'),
  3272: art('3272-cinder-ruby-rough'),
  3275: art('3275-tidewifes-pink-pearl'),
  3278: art('3278-ambassadors-pocket-watch'),
  3284: art('3284-moon-ring-pendant'),
  3286: art('3286-twice-pawned-wedding-band'),
  3290: art('3290-archmages-pawned-amethyst'),
  3292: art('3292-low-tide-sapphire-ring'),
  3297: art('3297-jade-toad-of-wet-hollow'),
  3302: art('3302-treasury-ingots-of-the-old-mint'),
  3304: art('3304-map-of-a-sold-kingdom'),
  3308: art('3308-merchant-princes-tally-coins'),
  3311: art('3311-hollow-hill-haversack'),
  3316: art('3316-gourd-of-the-patient-camel'),
  3321: art('3321-golden-lions-of-pride-gate'),
  3323: art('3323-ebony-fly-in-a-thimble'),
  3328: art('3328-bronze-griffon-figurine'),
  3330: art('3330-brass-bee-swarm-figurine'),
  3335: art('3335-pewter-hen-of-plenty'),
  3340: art('3340-furnace-door-key'),
  3344: art('3344-rimewyrm-egg'),
  3347: art('3347-harvest-goose-wishbone'),
  3350: art('3350-barrow-wights-jawbone'),
  3353: art('3353-straw-effigy'),
  3359: art('3359-deck-of-many-things'),
  3362: art('3362-deck-of-harrowed-tales'),
  3365: art('3365-green-scrying-ball'),
  3368: art('3368-eye-of-the-wise'),
  3374: art('3374-corpse-candle-stub'),
  3376: art('3376-feather-token-oak'),
  3380: art('3380-sandglass-of-last-breath'),
  3382: art('3382-marbles-of-clumsy-kings'),
  3387: art('3387-cinderskull-of-the-bone-hag'),
  3392: art('3392-bottled-bog-wisp'),
  3394: art('3394-hangmans-rope'),
  3398: art('3398-warpaint-of-the-hill-clans'),
  3403: art('3403-bloodthirsty-blade-of-the-pit', 'main_hand', held('3403-bloodthirsty-blade-of-the-pit', [11, 20])),
  3405: art('3405-dwarf-kings-rune-axe', 'main_hand', held('3405-dwarf-kings-rune-axe', [11, 20], Z.main_hand)),
  3413: art('3413-dragonbane-lance-of-the-vow', 'main_hand', held('3413-dragonbane-lance-of-the-vow', [11, 20])),
  3418: art('3418-elder-druids-living-staff', 'main_hand', held('3418-elder-druids-living-staff', [11, 20])),
  3420: art('3420-phoenix-feather-wand', 'main_hand', held('3420-phoenix-feather-wand', [11, 20])),
  3424: art('3424-archmages-starwoven-robe', 'torso'),
  3426: art('3426-frost-giants-rime-hauberk', 'torso'),
  3433: art('3433-horned-shield-of-the-beast-king', 'off_hand'),
  3436: art('3436-holy-symbol-of-the-radiant-dawn', 'off_hand'),
  3438: art('3438-fey-courts-moon-totem', 'off_hand'),
  3443: art('3443-knight-errants-plumed-helm', 'helmet'),
  3445: art('3445-dwarf-thanes-ancestral-helm', 'helmet'),
  3447: art('3447-bat-wing-mantle-of-the-night', 'cape'),
  3451: art('3451-pilgrims-dust-cloak', 'cape'),
  3454: art('3454-thief-lords-velvet-gloves', 'hands', [part('3454-thief-lords-velvet-gloves-back', 'hand_back'), part('3454-thief-lords-velvet-gloves-front', 'hand_front')]),
  3456: art('3456-gauntlets-of-the-storm-giant', 'hands', [part('3456-gauntlets-of-the-storm-giant-back', 'hand_back'), part('3456-gauntlets-of-the-storm-giant-front', 'hand_front')]),
  3461: art('3461-winged-boots-of-the-sky-temple', 'feet'),
  3464: art('3464-iron-shod-boots-of-the-mountain', 'feet'),
  3468: art('3468-golden-greaves-of-the-sun-king', 'legs'),
  3471: art('3471-ice-wolf-leggings', 'legs'),
  3473: art('3473-giant-kings-girdle', 'waist'),
  3480: art('3480-starfall-ring-of-the-last-wish'),
  3484: art('3484-sea-queens-sapphire-band'),
  3486: art('3486-ioun-stone-of-the-starlit-mind'),
  3491: art('3491-fey-lantern-moth-charm'),
  3494: art('3494-periapt-of-the-plague-warden'),
  3497: art('3497-eye-pendant-of-the-oracle'),
  4010: art('4010-red-fjord-bloodaxe', 'main_hand', held('4010-red-fjord-bloodaxe', [11, 20], Z.main_hand)),
  4011: art('4011-bloodoath-belt', 'waist'),
  4021: art('4021-silver-rose-gloves', 'hands', [part('4021-silver-rose-gloves-back', 'hand_back'), part('4021-silver-rose-gloves-front', 'hand_front')]),
  4022: art('4022-silver-rose-cape', 'cape'),
  4040: art('4040-huntmasters-longbow', 'main_hand', held('4040-huntmasters-longbow', [11, 20])),
  4042: art('4042-huntmasters-boots', 'feet'),
  4050: art('4050-gravewardens-mace', 'main_hand', held('4050-gravewardens-mace', [11, 20])),
  4051: art('4051-gravewardens-hauberk', 'torso'),
  4052: art('4052-gravewardens-kettle-helm', 'helmet'),
  4053: art('4053-gravewardens-candle-pendant'),
  4060: art('4060-velvet-knife', 'main_hand', held('4060-velvet-knife', [11, 20])),
  4092: art('4092-tidecallers-pearl-ring'),
  4101: art('4101-emberheart-hauberk', 'torso'),
  4107: art('4107-frostbound-jarls-bearskin', 'cape'),
  4112: art('4112-whispering-grove-acorn'),
  4113: art('4113-iron-tortoise-shell', 'off_hand'),
  4119: art('4119-hollow-kings-blade', 'main_hand', held('4119-hollow-kings-blade', [11, 20])),
  4126: art('4126-moonlit-stalkers-hood', 'helmet'),
  4131: art('4131-sunforged-pilgrims-sunburst'),
  4134: art('4134-wyrmscale-hauberk', 'torso'),
  4135: art('4135-wyrmscale-kite', 'off_hand'),
  4200: art('4200-shadowmantle-fang', 'main_hand', held('4200-shadowmantle-fang', [11, 20])),
  4210: art('4210-orrery-staff-of-turning-spheres', 'main_hand', held('4210-orrery-staff-of-turning-spheres', [11, 20])),
  4213: art('4213-orrery-cogwheel-ring'),
  4230: art('4230-windrunner-boots', 'feet'),
  4242: art('4242-thornwarden-bramble-gloves', 'hands', [part('4242-thornwarden-bramble-gloves-back', 'hand_back'), part('4242-thornwarden-bramble-gloves-front', 'hand_front')]),
  4243: art('4243-thornwarden-rootbound-greaves', 'legs'),
  4250: art('4250-siegebreaker-ram-maul', 'main_hand', held('4250-siegebreaker-ram-maul', [11, 20])),
  4261: art('4261-starweaver-circlet', 'helmet'),
  4262: art('4262-starweaver-night-cloak', 'cape'),
  4291: art('4291-beastmasters-bonewhistle'),
  4301: art('4301-deepdelvers-ringmail', 'torso'),
  4304: art('4304-deepdelvers-tool-belt', 'waist'),
  4311: art('4311-ashlands-warcloak', 'cape'),
  4312: art('4312-ashlands-gauntlets', 'hands', [part('4312-ashlands-gauntlets-back', 'hand_back'), part('4312-ashlands-gauntlets-front', 'hand_front')]),
  4314: art('4314-plaguedoctors-beaked-mask', 'helmet'),
  4320: art('4320-wolfpelt-boots', 'feet'),
  4321: art('4321-clockwork-repeater', 'main_hand', held('4321-clockwork-repeater', [11, 20])),
  4322: art('4322-clockwork-gauntlets', 'hands', [part('4322-clockwork-gauntlets-back', 'hand_back'), part('4322-clockwork-gauntlets-front', 'hand_front')]),
  4325: art('4325-nomads-sandcloak', 'cape'),
  4331: art('4331-crimson-court-signet'),
  4332: art('4332-oathkeepers-blade', 'main_hand', held('4332-oathkeepers-blade', [11, 20])),
  4336: art('4336-thunderclap-belt', 'waist'),
  4401: art('4401-phoenix-plume-robe', 'torso'),
  4408: art('4408-leviathan-pearl-ring'),
  4410: art('4410-harlequins-belled-cap', 'helmet'),
  4419: art('4419-celestial-choir-pendant'),
  4420: art('4420-celestial-choir-bell'),
  4423: art('4423-goblin-kings-stewpot-helm', 'helmet'),
  4424: art('4424-goblin-kings-left-boots', 'feet'),
  4432: art('4432-eclipse-sentinel-plate', 'torso'),
  4442: art('4442-vial-of-acid'),
  4446: art('4446-stormglass-flask'),
  4448: art('4448-thunderstone'),
  4453: art('4453-pouch-of-rock-salt'),
  4456: art('4456-puffball-bomb'),
  4460: art('4460-bottled-fog'),
  4462: art('4462-dragons-pitch'),
  // starter garb (docs/144): plain class clothes, art only (ids 900–979 are no item; content/items/garb.json)
  900: art('900-shieldhands-quilted-gambeson', 'torso'),
  901: art('901-shieldhands-wool-breeches', 'legs'),
  902: art('902-shieldhands-marching-boots', 'feet'),
  903: art('903-shieldhands-red-headband', 'helmet'),
  910: art('910-cutpurses-shadow-jerkin', 'torso'),
  911: art('911-cutpurses-charcoal-leggings', 'legs'),
  912: art('912-cutpurses-soft-soled-boots', 'feet'),
  913: art('913-cutpurses-deep-hood', 'helmet'),
  915: art('915-cutpurses-crimson-sash', 'waist'),
  920: art('920-apprentices-starry-robe', 'torso'),
  921: art('921-apprentices-robe-skirts', 'legs'),
  922: art('922-apprentices-curled-slippers', 'feet'),
  923: art('923-apprentices-pointed-hat', 'helmet'),
  925: art('925-apprentices-gold-cord', 'waist'),
  930: art('930-acolytes-sun-tabard', 'torso'),
  931: art('931-acolytes-grey-trousers', 'legs'),
  932: art('932-acolytes-pilgrim-wraps', 'feet'),
  933: art('933-acolytes-linen-coif', 'helmet'),
  934: art('934-acolytes-gold-hemmed-mantle', 'cape'),
  940: art('940-woodwards-green-tunic', 'torso'),
  941: art('941-woodwards-moss-trousers', 'legs'),
  942: art('942-woodwards-tall-boots', 'feet'),
  943: art('943-woodwards-green-hood', 'helmet'),
  944: art('944-woodwards-leaf-cloak', 'cape'),
  950: art('950-oathsworn-surcoat', 'torso'),
  951: art('951-oathsworn-grey-hose', 'legs'),
  952: art('952-oathsworn-steel-toed-boots', 'feet'),
  954: art('954-oathsworn-blue-cape', 'cape'),
  955: art('955-oathsworn-white-belt', 'waist'),
  960: art('960-grovekeepers-leaf-robe', 'torso'),
  961: art('961-grovekeepers-wrapped-leggings', 'legs'),
  962: art('962-grovekeepers-fur-footwraps', 'feet'),
  963: art('963-grovekeepers-leaf-wreath', 'helmet'),
  964: art('964-grovekeepers-autumn-mantle', 'cape'),
  970: art('970-minstrels-slashed-doublet', 'torso'),
  971: art('971-minstrels-yellow-hose', 'legs'),
  972: art('972-minstrels-pointed-shoes', 'feet'),
  973: art('973-minstrels-feathered-cap', 'helmet'),
  974: art('974-minstrels-red-cape', 'cape'),
  906: art('906-shieldhands-drill-vest', 'torso'),
  907: art('907-shieldhands-padded-coif', 'helmet'),
  916: art('916-cutpurses-patched-vest', 'torso'),
  917: art('917-cutpurses-knotted-bandana', 'helmet'),
  926: art('926-apprentices-moon-robe', 'torso'),
  927: art('927-apprentices-floppy-hat', 'helmet'),
  936: art('936-acolytes-pilgrim-cassock', 'torso'),
  937: art('937-acolytes-sun-circlet', 'helmet'),
  946: art('946-woodwards-hunting-jerkin', 'torso'),
  947: art('947-woodwards-feathered-hat', 'helmet'),
  953: art('953-oathsworn-silver-circlet', 'helmet'),
  956: art('956-oathsworn-blue-tabard', 'torso'),
  957: art('957-oathsworn-blue-coif', 'helmet'),
  966: art('966-grovekeepers-bark-vest', 'torso'),
  967: art('967-grovekeepers-antler-circlet', 'helmet'),
  976: art('976-minstrels-motley-tunic', 'torso'),
  977: art('977-minstrels-plumed-hat', 'helmet'),
  // docs/159: the basics, the staples every counter keeps
  4500: art('4500-healing-potion'),
  4501: art('4501-greater-healing-potion'),
  4502: art('4502-superior-healing-potion'),
  4503: art('4503-spirit-tonic'),
  4504: art('4504-antidote'),
  4505: art('4505-roll-of-bandages'),
  4506: art('4506-smelling-salts'),
  4507: art('4507-fire-flask'),
  4508: art('4508-smoke-bomb'),
  4509: art('4509-sack-of-caltrops'),
  4510: art('4510-vial-of-holy-water'),
  4511: art('4511-draught-of-might'),
  4512: art('4512-stoneskin-draught'),
  4513: art('4513-potion-of-swiftness'),
  4514: art('4514-trail-rations'),
  4515: art('4515-whetstone'),
  4516: art('4516-bundle-of-torches'),
  4517: art('4517-mug-of-strong-ale'),
  4518: art('4518-chalk-and-string'),
  4519: art('4519-scroll-of-mending'),
};

/** The three heights, all in the human's pose; anchors measured from where each one's head, hands, torso and waist sit. */
// The front hand and fist are the back ones mirrored across the body's centre column (x 15.5, so x → 31 − x); the
// anchors agree (a hand_back and a hand_front glove move alike on the short and medium bodies, and apart on the tall).
export const HEIGHTS: Readonly<Record<Height['key'], Height>> = {
  short: {
    key: 'short', anchors: { helmet: [0, 1], torso: [0, 1], cape: [0, 1], hand_back: [-1, 0], hand_front: [-1, 0], off_hand: [-1, 0] },
    hand: [10, 20], fist: [[17, 9, 10], [18, 8, 12], [19, 8, 11], [20, 8, 13], [21, 8, 12], [22, 9, 11]],
    offHand: [21, 20], offFist: [[17, 21, 22], [18, 19, 23], [19, 20, 23], [20, 18, 23], [21, 19, 23], [22, 20, 22]],
    arm: [[16, 9, 11], [17, 8, 11], [17, 9, 10], [18, 8, 12], [19, 8, 11], [20, 8, 13], [21, 8, 12], [22, 9, 11]], chin: 15,
  },
  medium: {
    key: 'medium', anchors: {},
    hand: [11, 20], fist: [[17, 10, 10], [18, 9, 13], [19, 9, 12], [20, 9, 13], [21, 9, 13], [22, 10, 12]],
    offHand: [20, 20], offFist: [[17, 21, 21], [18, 18, 22], [19, 19, 22], [20, 18, 22], [21, 18, 22], [22, 19, 21]],
    arm: [[15, 11, 12], [16, 10, 12], [17, 9, 12], [18, 9, 13], [19, 9, 12], [20, 9, 13], [21, 9, 13], [22, 10, 12]], chin: 14,
  },
  tall: {
    key: 'tall', anchors: { helmet: [0, -1], waist: [0, -1], hand_back: [1, 1], hand_front: [-1, 1], off_hand: [-1, 1] },
    hand: [12, 20], fist: [[18, 11, 11], [18, 13, 14], [19, 10, 13], [20, 10, 14], [21, 10, 14], [22, 11, 13]],
    offHand: [19, 20], offFist: [[18, 20, 20], [18, 17, 18], [19, 18, 21], [20, 17, 21], [21, 17, 21], [22, 18, 20]],
    arm: [[15, 10, 13], [16, 10, 12], [17, 10, 11], [18, 11, 11], [18, 13, 14], [19, 10, 13], [20, 10, 14], [21, 10, 14], [22, 11, 13]], chin: 14,
  },
};

/** Feature z numbers, among the worn layers': tails and wings under the cape (a tail comes out from under it), feathers
 *  on the bare body under legs, boots, armour and gloves, a woman's loose hair under armour (it frames a breastplate
 *  instead of burying it), a beard over it, a head (ears, tusks) under any helmet, a beak or snout and horns over it (a
 *  brim sits on the skull, never over the beak). */
const FZ = { tail: 8, wings: 9, feathers: 22, hair: 38, bust: 46, beard: 60, head: 65, ears: 65, face: 65, tusks: 65, snout: 72, horns: 75 } as const;

type FeatureKey = keyof typeof FZ;

// whose women have loose hair past the neck (species/female/<key>-hair.png); the birdfolk and dragonborn heads cover it,
// and their women have a head of their own instead (a long crest; a slimmer snout and longer horns)
const LOOSE_HAIR = new Set(['human', 'half-elf', 'tiefling', 'dwarf', 'halfling', 'gnome', 'elf', 'orc']);
const OWN_HEAD = new Set(['birdfolk', 'dragonborn']);

/** `helmet`: where a hat sits on this species' head when its head is not its height's (a bird's eye sits two rows
 *  higher than a man's, a dragon's one higher and one back); hats are placed by the eye. */
const species = (key: string, name: string, height: Height['key'], features: FeatureKey[] = [], helmet?: readonly [number, number]) => (sex: Sex): BodyArt => {
  const dir = sex === 'female' ? '/assets/hero-kit/species/female' : '/assets/hero-kit/species';
  // a woman also has a hint of a bust: a few translucent dark pixels over whatever she wears, inside the shared outline
  const own: FeatureKey[] = sex === 'female' ? [...features.filter((f) => f !== 'beard'), ...(LOOSE_HAIR.has(key) ? ['hair' as const] : []), 'bust'] : features;
  const { hand, fist, offHand, offFist, arm, chin } = HEIGHTS[height];
  const anchors = helmet ? { ...HEIGHTS[height].anchors, helmet } : HEIGHTS[height].anchors;
  const url = (f: FeatureKey) =>
    f === 'bust' ? `${dir}/bust-${height}.png`
      : f === 'hair' || ((f === 'head' || f === 'snout') && sex === 'female' && OWN_HEAD.has(key)) ? `${dir}/${key}-${f}.png`
        : `/assets/hero-kit/species/${key}-${f}.png`;
  return {
    key, name, sex, height, anchors, hand, fist, offHand, offFist, arm, chin,
    url: `${dir}/body-${key}.png`,
    features: own.map((f) => ({ key: f, z: FZ[f], url: url(f) })),
  };
};

const SPECIES = [
  species('human', 'Human', 'medium'),
  species('half-elf', 'Half-Elf', 'medium', ['ears']),
  species('tiefling', 'Tiefling', 'medium', ['horns', 'tail']),
  species('birdfolk', 'Birdfolk', 'medium', ['head', 'snout', 'wings', 'feathers'], [0, -2]),
  species('dwarf', 'Dwarf', 'short', ['beard']),
  species('halfling', 'Halfling', 'short'),
  species('gnome', 'Gnome', 'short', ['face']),
  species('elf', 'Elf', 'tall'),
  species('orc', 'Orc', 'tall', ['tusks']),
  species('dragonborn', 'Dragonborn', 'tall', ['head', 'snout', 'tail'], [-1, -1]),
];

/** Every species in the game (fk-grammar SpeciesId), each on one of the three heights, as men and as women. */
export const BODIES_BY_SEX: Readonly<Record<Sex, readonly BodyArt[]>> = {
  male: SPECIES.map((s) => s('male')),
  female: SPECIES.map((s) => s('female')),
};

export const BODIES = BODIES_BY_SEX.male;

/** The QA art for a catalogue piece, if it has any. */
export function itemArt(def: number): ItemArt | undefined {
  // docs/145 KH4: a base piece (ids 100–192) wears a catalogue piece's art
  return ITEM_ART[artDefOf(def)];
}
